import React, { useState, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createPortal } from "react-dom";
import Swal from "sweetalert2";
import "../BitacoraProduccion.css";
import "../BitacoraEnvasado.css";
import { obtenerCatalogoMaguey } from "../../../../redux/DucksBeneficiario/BitacoraProduccionDucks";

// Material UI Icons
import LayersIcon from '@mui/icons-material/Layers';
import ScienceIcon from '@mui/icons-material/Science';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import UndoIcon from '@mui/icons-material/Undo';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import OpacityIcon from '@mui/icons-material/Opacity';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import BiotechIcon from '@mui/icons-material/Biotech';
import ApartmentIcon from '@mui/icons-material/Apartment';
import HistoryIcon from '@mui/icons-material/History';
import SendIcon from '@mui/icons-material/Send';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import RefreshIcon from '@mui/icons-material/Refresh';
import { obtenerGranelesEnvasadoPorLogin } from "../../../../redux/DucksBeneficiario/BitacoraEnvasadoDucks";

const InventarioGranelSection = ({
  granelRecords = [],
  allGranelRecords = [],
  produccionLots = [],
  destilacionRecords = [],
  catalogoMaguey = [],
  availableInstalaciones = [],
  isFormOpen,
  setIsFormOpen,
  formData = {},
  setFormData,
  handleSaveGranel,
  handleCancelGranel,
  handleSaveFusion,
  handleUndoGranel,
  handleSaveTraslado,
  handleSaveDilucion,
  handleSaveBaja,
  handleSaveMuestreo,
  onRegistrarEnvasado,
  handleRefreshGranel,
  isRefreshingGranel = false,
  pagination = { total: 0, page: 1, limit: 10, totalPages: 1 },
  currentPage = 1,
  setCurrentPage,
  limit = 10,
  setLimit,
  onPageChange,
  onLimitChange
}) => {
  const dispatch = useDispatch();
  const [localRefreshing, setLocalRefreshing] = useState(false);
  const isRefreshing = isRefreshingGranel || localRefreshing;
  const reduxUser = useSelector(state => state.Usuario?.data?.user?.[0] || state.Usuario?.user?.[0] || state.Usuario || null);

  const handleRefresh = async () => {
    if (handleRefreshGranel) {
      await handleRefreshGranel();
    } else if (dispatch) {
      setLocalRefreshing(true);
      try {
        const idLogin = reduxUser?.id || reduxUser?.idlogin;
        if (idLogin) {
          await dispatch(obtenerGranelesEnvasadoPorLogin(idLogin));
        }
      } catch (err) {
        console.error("Error refreshing granel:", err);
      } finally {
        setTimeout(() => setLocalRefreshing(false), 500);
      }
    }
  };

  useEffect(() => {
    if (dispatch) {
      dispatch(obtenerCatalogoMaguey());
    }
  }, [dispatch]);

  const [selectedRecord, setSelectedRecord] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [claseFilter, setClaseFilter] = useState("todos");

  // Filtrar exclusivamente instalaciones de Envasado del asociado
  const envasadoInstalaciones = useMemo(() => {
    const list = (availableInstalaciones || []).filter(inst => {
      const idTipo = Number(inst.idtipoanexo);
      const tipo = String(inst.tipo || inst.informaciongeneralinstalacioncliente?.tipo || '').toLowerCase();
      const alias = String(inst.alias || inst.informaciongeneralinstalacioncliente?.alias || '').toLowerCase();
      return idTipo === 2 || tipo.includes('envasad') || alias.includes('envasad');
    });
    return list.length > 0 ? list : (availableInstalaciones || []);
  }, [availableInstalaciones]);

  const reduxCatalogo = useSelector(state => state.bitacoraProduccion?.catalogoMaguey || []);
  const activeCatalog = useMemo(() => {
    let list = [];
    if (Array.isArray(catalogoMaguey) && catalogoMaguey.length > 0) list = catalogoMaguey;
    else if (Array.isArray(reduxCatalogo) && reduxCatalogo.length > 0) list = reduxCatalogo;

    if (list.length > 0) {
      return [...list].sort((a, b) => {
        const nameA = (a.nombre_comun || a.nombreComun || '').toLowerCase();
        const nameB = (b.nombre_comun || b.nombreComun || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
    }

    return [
      { nc: 1, nombre_comun: "Espadín", nombre_cientifico: "Agave angustifolia Haw" },
      { nc: 2, nombre_comun: "Tobalá", nombre_cientifico: "Agave potatorum Zucc" },
      { nc: 3, nombre_comun: "Arroqueño", nombre_cientifico: "Agave americana L." },
      { nc: 4, nombre_comun: "Tepeztate", nombre_cientifico: "Agave marmorata" },
      { nc: 5, nombre_comun: "Mexicano", nombre_cientifico: "Agave rhodacantha" },
      { nc: 6, nombre_comun: "Madrecuixe / Cuishe", nombre_cientifico: "Agave karwinskii" },
      { nc: 7, nombre_comun: "Jabalí", nombre_cientifico: "Agave convallis" },
      { nc: 8, nombre_comun: "Chiquilao", nombre_cientifico: "Agave seemanniana" }
    ];
  }, [catalogoMaguey, reduxCatalogo]);

  // Catálogo completo de lotes de granel disponibles (usando allGranelRecords sin paginar si existe, o granelRecords)
  const allAvailableGraneles = useMemo(() => {
    const list = (Array.isArray(allGranelRecords) && allGranelRecords.length > 0)
      ? allGranelRecords
      : (Array.isArray(granelRecords) ? granelRecords : []);

    const seen = new Set();
    const result = [];

    list.forEach((g, gIdx) => {
      const gId = g.id_bitacora_granel || g.id || g.dbId;
      const gKey = gId ? String(gId) : `idx-${gIdx}-${g.no_lote || g.noLote || ''}`;
      if (seen.has(gKey)) return;
      seen.add(gKey);

      const vDisp = parseFloat(String(g.invFinalVol ?? g.saldoFinal ?? g.volFinal ?? g.volumen ?? g.litros ?? g.litrosTotal ?? g.entradaVolumen ?? 0).replace(/[^\d.]/g, '')) || 0;
      const aDisp = parseFloat(String(g.porcentaje_alc_vol ?? g.invFinalAlc ?? g.alcFinal ?? g.entradaAlc ?? 0).replace(/[^\d.]/g, '')) || 0;
      const optVal = String(g.id_bitacora_granel || g.id || g.dbId || g.noLote || g.no_lote || gIdx);
      const loteName = g.noLote || g.no_lote || 'Sin Lote';
      const tanqueName = g.idContenedor || g.no_tanque || 'S/N';
      const catName = g.categoria || 'Mezcal';
      const claseName = g.clase || 'Sin Clase';

      result.push({
        ...g,
        _vDisp: vDisp,
        _aDisp: aDisp,
        _optVal: optVal,
        _label: `${loteName} (Tanque: ${tanqueName}) - ${catName} | ${claseName} (Disp: ${vDisp.toLocaleString()} L @ ${aDisp}%)`
      });
    });

    return result;
  }, [allGranelRecords, granelRecords]);

  // Estado para Modal de Fusión / Mezcla / Extracción Parcial de Graneles
  const [isFusionModalOpen, setIsFusionModalOpen] = useState(false);
  const [isSubmittingFusion, setIsSubmittingFusion] = useState(false);
  const [fusionSources, setFusionSources] = useState([
    { granelId: "", volExtraer: "" },
    { granelId: "", volExtraer: "" }
  ]);
  const [fusionForm, setFusionForm] = useState({
    fechaMovimiento: new Date().toISOString().split('T')[0],
    noLote: "",
    idContenedor: "",
    categoria: "Mezcal Artesanal",
    clase: "Joven / Blanco (Mezcla)",
    ingredientes: "",
    tipoMaguey: "",
    noAnalisis: "",
    observaciones: ""
  });

  // Modales adicionales solicitados para Granel
  const [isTrasladoModalOpen, setIsTrasladoModalOpen] = useState(false);
  const [trasladoForm, setTrasladoForm] = useState({
    id_granel_origen: "",
    no_lote_origen: "",
    instalacion_origen: "",
    volumen_disponible: 0,
    id_unidad_produccion_destino: "",
    instalacion_destino: "",
    no_tanque_destino: "",
    litros_traslado: "",
    fecha: new Date().toISOString().split('T')[0],
    folio_documento: "",
    observaciones: ""
  });

  const [isDilucionModalOpen, setIsDilucionModalOpen] = useState(false);
  const [dilucionForm, setDilucionForm] = useState({
    id_granel_origen: "",
    no_lote_origen: "",
    volumen_actual: 0,
    grado_actual: 0,
    grado_objetivo: 45.0,
    agua_agregada: 0,
    volumen_final: 0,
    no_tanque: "",
    lote_agua: "",
    fecha: new Date().toISOString().split('T')[0],
    observaciones: ""
  });

  const [isBajaModalOpen, setIsBajaModalOpen] = useState(false);
  const [bajaForm, setBajaForm] = useState({
    id_granel_origen: "",
    no_lote_origen: "",
    volumen_actual: 0,
    litros_baja: "",
    motivo: "Merma natural por evaporación en tanque",
    folio_documento: "",
    fecha: new Date().toISOString().split('T')[0],
    observaciones: ""
  });

  const [isMuestreoModalOpen, setIsMuestreoModalOpen] = useState(false);
  const [muestreoForm, setMuestreoForm] = useState({
    id_granel_origen: "",
    no_lote_origen: "",
    volumen_actual: 0,
    litros_muestra: 1.5,
    tipo_muestreo: "Muestreo Oficial NOM-070 (Tercio de muestra: ensayo, testigo socio, testigo UI)",
    laboratorio_inspector: "",
    folio_documento: "",
    fecha: new Date().toISOString().split('T')[0],
    observaciones: ""
  });

  // Estado para Modal de Movimientos y Trazabilidad (UI y Asociado)
  const [isMovimientosModalOpen, setIsMovimientosModalOpen] = useState(false);
  const [selectedLoteForMovimientos, setSelectedLoteForMovimientos] = useState(null);
  const [movimientosFilterTab, setMovimientosFilterTab] = useState("todos");
  const [movimientosStatusFilter, setMovimientosStatusFilter] = useState("todos");

  const handleOpenMovimientosModal = (rec = null) => {
    setSelectedLoteForMovimientos(rec);
    setMovimientosFilterTab("todos");
    setMovimientosStatusFilter("todos");
    setIsMovimientosModalOpen(true);
  };

  const handleOpenTraslado = (rec = null) => {
    const selectedG = rec || (granelRecords && granelRecords[0]) || null;
    const vDisp = selectedG ? (parseFloat(String(selectedG.invFinalVol ?? selectedG.saldoFinal ?? selectedG.volFinal ?? selectedG.volumen ?? selectedG.litros ?? selectedG.litrosTotal ?? selectedG.entradaVolumen ?? 0).replace(/[^\d.]/g, '')) || 0) : 0;
    const origInstId = selectedG?.id_unidad_produccion || selectedG?.id_anexo_cliente || "";
    let instOrig = selectedG?.instalacion || selectedG?.instalacionOrigen || "";
    if (!instOrig || instOrig === "Instalación Principal") {
      const foundOrig = availableInstalaciones.find(i => String(i.idanexocliente || i.id) === String(origInstId));
      if (foundOrig) {
        instOrig = foundOrig.informaciongeneralinstalacioncliente?.alias || foundOrig.alias;
      }
    }
    instOrig = instOrig || availableInstalaciones[0]?.alias || "Instalación Principal";
    const optVal = selectedG ? String(selectedG.id ?? selectedG.dbId ?? selectedG.id_bitacora_granel ?? selectedG.noLote ?? selectedG.no_lote ?? "") : "";

    setTrasladoForm({
      id_granel_origen: optVal,
      no_lote_origen: selectedG?.noLote || selectedG?.no_lote || "",
      id_unidad_produccion_origen: origInstId,
      instalacion_origen: instOrig,
      volumen_disponible: vDisp,
      id_unidad_produccion_destino: "",
      instalacion_destino: "",
      tipo_instalacion_destino: "",
      no_tanque_destino: "",
      litros_traslado: "",
      fecha: new Date().toISOString().split('T')[0],
      folio_documento: "",
      observaciones: ""
    });
    setIsTrasladoModalOpen(true);
  };

  const handleOpenDilucion = (rec = null) => {
    const selectedG = rec || (granelRecords && granelRecords[0]) || null;
    const vDisp = selectedG ? (parseFloat(String(selectedG.invFinalVol ?? selectedG.saldoFinal ?? selectedG.volFinal ?? selectedG.volumen ?? selectedG.litros ?? selectedG.litrosTotal ?? selectedG.entradaVolumen ?? 0).replace(/[^\d.]/g, '')) || 0) : 0;
    const aDisp = selectedG ? (parseFloat(String(selectedG.porcentaje_alc_vol ?? selectedG.invFinalAlc ?? selectedG.alcFinal ?? selectedG.entradaAlc ?? 0).replace(/[^\d.]/g, '')) || 0) : 48.0;
    const optVal = selectedG ? String(selectedG.id ?? selectedG.dbId ?? selectedG.id_bitacora_granel ?? selectedG.noLote ?? selectedG.no_lote ?? "") : "";

    setDilucionForm({
      id_granel_origen: optVal,
      no_lote_origen: selectedG?.noLote || selectedG?.no_lote || "",
      volumen_actual: vDisp,
      grado_actual: aDisp,
      agua_agregada: "",
      grado_objetivo: aDisp,
      volumen_final: vDisp,
      no_tanque: selectedG?.idContenedor || selectedG?.no_tanque || "TANQUE-01",
      lote_agua: "",
      fecha: new Date().toISOString().split('T')[0],
      observaciones: ""
    });
    setIsDilucionModalOpen(true);
  };

  const handleOpenBaja = (rec = null) => {
    const selectedG = rec || (granelRecords && granelRecords[0]) || null;
    const vDisp = selectedG ? (parseFloat(String(selectedG.invFinalVol ?? selectedG.saldoFinal ?? selectedG.volFinal ?? selectedG.volumen ?? selectedG.litros ?? selectedG.litrosTotal ?? selectedG.entradaVolumen ?? 0).replace(/[^\d.]/g, '')) || 0) : 0;
    const optVal = selectedG ? String(selectedG.id ?? selectedG.dbId ?? selectedG.id_bitacora_granel ?? selectedG.noLote ?? selectedG.no_lote ?? "") : "";

    setBajaForm({
      id_granel_origen: optVal,
      no_lote_origen: selectedG?.noLote || selectedG?.no_lote || "",
      volumen_actual: vDisp,
      litros_baja: "",
      motivo: "Merma natural por evaporación en tanque",
      folio_documento: "",
      fecha: new Date().toISOString().split('T')[0],
      observaciones: ""
    });
    setIsBajaModalOpen(true);
  };

  const handleOpenMuestreo = (rec = null) => {
    const selectedG = rec || (granelRecords && granelRecords[0]) || null;
    const vDisp = selectedG ? (parseFloat(String(selectedG.invFinalVol ?? selectedG.saldoFinal ?? selectedG.volFinal ?? selectedG.volumen ?? selectedG.litros ?? selectedG.litrosTotal ?? selectedG.entradaVolumen ?? 0).replace(/[^\d.]/g, '')) || 0) : 0;
    const optVal = selectedG ? String(selectedG.id ?? selectedG.dbId ?? selectedG.id_bitacora_granel ?? selectedG.noLote ?? selectedG.no_lote ?? "") : "";

    setMuestreoForm({
      id_granel_origen: optVal,
      no_lote_origen: selectedG?.noLote || selectedG?.no_lote || "",
      volumen_actual: vDisp,
      litros_muestra: 1.5,
      tipo_muestreo: "Muestreo Oficial NOM-070 (Tercio de muestra: ensayo, testigo socio, testigo UI)",
      laboratorio_inspector: "",
      folio_documento: "",
      fecha: new Date().toISOString().split('T')[0],
      observaciones: ""
    });
    setIsMuestreoModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Captura manual de No. de Lote nuevo para envasado
  const handleLoteSelect = (e) => {
    const selectedNoLote = e.target.value;
    setFormData(prev => ({
      ...prev,
      noLote: selectedNoLote,
      no_lote: selectedNoLote
    }));
  };

  // Solo se puede editar la Identificación del Mezcal y Contenedor (Imagen 1)
  const handleEditRecord = (rec) => {
    const vol = String(rec.litros ?? rec.invFinalVol ?? rec.entradaVolumen ?? "0").replace(/[^\d.]/g, '');
    const alc = String(rec.porcentaje_alc_vol ?? rec.invFinalAlc ?? rec.entradaAlc ?? "0").replace(/[^\d.]/g, '');
    setFormData({
      id: rec.id,
      fechaMovimiento: rec.fechaMovimiento || rec.fecha || new Date().toISOString().split('T')[0],
      fecha: rec.fecha || rec.fechaMovimiento || new Date().toISOString().split('T')[0],
      idContenedor: rec.idContenedor || rec.no_tanque || "",
      no_tanque: rec.no_tanque || rec.idContenedor || "",
      noLote: rec.noLote || rec.no_lote || rec.noProduccion || "",
      no_lote: rec.no_lote || rec.noLote || rec.noProduccion || "",
      categoria: rec.categoria || "Mezcal Artesanal",
      clase: rec.clase || "Blanco Joven",
      ingredientes: rec.ingredientes || rec.ingrediente || "",
      ingrediente: rec.ingrediente || rec.ingredientes || "",
      tipoMaguey: rec.tipoMaguey || rec.especie || "",
      especie: rec.especie || rec.tipoMaguey || "",
      noAnalisis: rec.noAnalisisFisicoquimico || rec.noAnalisis || rec.clave_fq || "",
      clave_fq: rec.clave_fq || rec.noAnalisisFisicoquimico || rec.noAnalisis || "",
      litros: vol,
      porcentaje_alc_vol: alc,
      invInicialVol: String(rec.invInicialVol || vol).replace(/[^\d.]/g, ''),
      invInicialAlc: String(rec.invInicialAlc || alc).replace(/[^\d.]/g, ''),
      entradaProcedencia: rec.entradaProcedencia || "",
      entradaVolumen: vol,
      entradaAlc: alc,
      salidaDestino: rec.salidaDestino || "",
      salidaVolumen: String(rec.salidaVolumen || "0").replace(/[^\d.]/g, ''),
      salidaAlc: String(rec.salidaAlc || "0").replace(/[^\d.]/g, ''),
      invFinalVol: vol,
      invFinalAlc: alc,
      observaciones: rec.observaciones || "",
      status: rec.status || "borrador"
    });
    setIsFormOpen(true);
  };

  // Función auxiliar: Formato Nombre Común (Nombre Científico)
  const formatMagueyNombreComunPrimero = (magueyStr, catalog = []) => {
    if (!magueyStr || typeof magueyStr !== 'string') return "";
    const trimmed = magueyStr.trim();
    if (!trimmed) return "";

    const stripAccents = (str) =>
      (str || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

    const cleanTerm = stripAccents(trimmed);
    const catList = Array.isArray(catalog) ? catalog : [];

    // Helper para verificar si un término incluye la palabra de otro (ignorando "maguey" y "agave")
    const containsWord = (source, target) => {
      if (!source || !target) return false;
      const words = target.split(/\s+/).filter(w => w.length > 2 && w !== "maguey" && w !== "agave");
      if (words.length === 0) return source.includes(target);
      return words.every(w => source.includes(w));
    };

    // 1. Si ya viene con formato "Nombre Común (Nombre Científico)" o viceversa
    const match = trimmed.match(/^([^(]+)\s*\(([^)]+)\)$/);
    if (match) {
      let p1 = match[1].trim();
      let p2 = match[2].trim();
      let commonPart = p1;
      let scientificPart = p2;

      if (p1.toLowerCase().includes("agave")) {
        commonPart = p2;
        scientificPart = p1;
      }

      const cleanCommon = stripAccents(commonPart);

      // Buscar si el nombre común coincide con alguna especie en el catálogo para normalizar
      const matchedCat = catList.find(c => {
        const cComun = stripAccents(c.nombre_comun || c.nombreComun);
        return cComun && (cComun === cleanCommon || containsWord(cleanCommon, cComun) || containsWord(cComun, cleanCommon));
      });

      const cleanStr = (s) => (s || "").replace(/\s+/g, " ").trim();
      if (matchedCat && matchedCat.nombre_comun && matchedCat.nombre_cientifico) {
        return `${cleanStr(matchedCat.nombre_comun)} (${cleanStr(matchedCat.nombre_cientifico)})`;
      }

      return `${cleanStr(commonPart)} (${cleanStr(scientificPart)})`;
    }

    const cleanStr = (s) => (s || "").replace(/\s+/g, " ").trim();

    // 2. Coincidencia EXACTA por nombre común en el catálogo
    const exactCommonMatch = catList.find(c => {
      const cComun = stripAccents(c.nombre_comun || c.nombreComun);
      return cComun && cComun === cleanTerm;
    });
    if (exactCommonMatch && exactCommonMatch.nombre_comun && exactCommonMatch.nombre_cientifico) {
      return `${cleanStr(exactCommonMatch.nombre_comun)} (${cleanStr(exactCommonMatch.nombre_cientifico)})`;
    }

    // 3. Coincidencia PARCIAL por nombre común en el catálogo (ej: "espadin" -> "MAGUEY ESPADÍN")
    const partialCommonMatch = catList.find(c => {
      const cComun = stripAccents(c.nombre_comun || c.nombreComun);
      return cComun && (containsWord(cleanTerm, cComun) || containsWord(cComun, cleanTerm));
    });
    if (partialCommonMatch && partialCommonMatch.nombre_comun && partialCommonMatch.nombre_cientifico) {
      return `${cleanStr(partialCommonMatch.nombre_comun)} (${cleanStr(partialCommonMatch.nombre_cientifico)})`;
    }

    // 4. Si el término ingresado es estrictamente un nombre científico de Agave (ej. "Agave angustifolia")
    if (cleanTerm.startsWith("agave")) {
      const scientMatch = catList.find(c => {
        const cCient = stripAccents(c.nombre_cientifico || c.nombreCientifico);
        return cCient && (cCient === cleanTerm || cleanTerm.includes(cCient));
      });
      if (scientMatch && scientMatch.nombre_comun && scientMatch.nombre_cientifico) {
        return `${cleanStr(scientMatch.nombre_comun)} (${cleanStr(scientMatch.nombre_cientifico)})`;
      }
    }

    return trimmed;
  };

  // Manejo de Fusión / Mezcla / Extracción Parcial de Lotes de Granel
  const handleAddFusionSource = () => {
    setFusionSources(prev => [...prev, { granelId: "", volExtraer: "" }]);
  };

  const handleRemoveFusionSource = (idx) => {
    if (fusionSources.length <= 1) {
      Swal.fire("Atención", "Debe mantener al menos 1 lote origen para realizar la extracción o mezcla.", "warning");
      return;
    }
    setFusionSources(prev => prev.filter((_, i) => i !== idx));
  };

  const handleFusionSourceChange = (idx, field, value) => {
    setFusionSources(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  // Cálculo en vivo de la mezcla consolidada
  const fusionCalculations = useMemo(() => {
    let totalVolMezcla = 0;
    let totalAlcVolPonderado = 0;
    let ingredientesList = [];
    const selectedLots = [];

    fusionSources.forEach(src => {
      if (!src.granelId) return;
      const gRecord = (allAvailableGraneles || []).find(g => (
        String(g.id_bitacora_granel) === String(src.granelId) ||
        String(g.id) === String(src.granelId) ||
        String(g.dbId) === String(src.granelId) ||
        String(g.noLote || g.no_lote) === String(src.granelId) ||
        String(g._optVal) === String(src.granelId)
      ));
      if (gRecord) {
        const vol = parseFloat(src.volExtraer) || 0;
        const alc = parseFloat(String(gRecord.porcentaje_alc_vol ?? gRecord.invFinalAlc ?? gRecord.alcFinal ?? gRecord.entradaAlc ?? 0).replace(/[^\d.]/g, '')) || 0;
        totalVolMezcla += vol;
        totalAlcVolPonderado += (vol * alc);

        selectedLots.push({ ...gRecord, volExtraer: vol, alcExtraer: alc });
        if (gRecord.ingredientes || gRecord.ingrediente) {
          ingredientesList.push(gRecord.ingredientes || gRecord.ingrediente);
        }
      }
    });

    let defaultAlc = "0.0";
    if (selectedLots.length > 0) {
      const sumAlc = selectedLots.reduce((acc, l) => acc + (parseFloat(l.alcExtraer || l.porcentaje_alc_vol || 0) || 0), 0);
      defaultAlc = (sumAlc / selectedLots.length).toFixed(1);
    }

    const alcPonderadoMezcla = totalVolMezcla > 0 ? (totalAlcVolPonderado / totalVolMezcla).toFixed(1) : defaultAlc;
    const ingredientesUnicos = [...new Set(ingredientesList.filter(Boolean))].join(', ');

    // 1. Validaciones NOM-070: Misma Clase y Misma Categoría
    const categorias = [...new Set(selectedLots.map(g => (g.categoria || "").trim()).filter(Boolean))];
    const clases = [...new Set(selectedLots.map(g => (g.clase || "").trim()).filter(Boolean))];

    const tieneConflictoCategoria = categorias.length > 1;
    const tieneConflictoClase = clases.length > 1;

    const categoriaBase = categorias[0] || (selectedLots[0]?.categoria || "Mezcal Artesanal");
    const claseBase = clases[0] || (selectedLots[0]?.clase || "Blanco Joven");

    // 2. Ordenar especies de agave de mayor a menor proporción
    const speciesVolumeMap = {};
    selectedLots.forEach(lot => {
      // Si el volumen a extraer aún es 0 pero hay 1 lote seleccionado, asignamos peso proporcional para mostrar la especie
      const vol = (lot.volExtraer > 0) ? lot.volExtraer : (totalVolMezcla === 0 ? 1 : 0);
      const rawEsp = lot.tipoMaguey || lot.especie || "";
      if (rawEsp) {
        const parts = rawEsp.split(/\s*(?:\+|\/|,)\s*/).filter(Boolean);
        const subVol = parts.length > 0 ? (vol / parts.length) : vol;
        parts.forEach(p => {
          const formatted = formatMagueyNombreComunPrimero(p, activeCatalog);
          speciesVolumeMap[formatted] = (speciesVolumeMap[formatted] || 0) + subVol;
        });
      }
    });

    const sortedSpeciesEntries = Object.entries(speciesVolumeMap).sort((a, b) => b[1] - a[1]);
    let magueyesOrdenados = "";
    if (sortedSpeciesEntries.length > 1 && totalVolMezcla > 0) {
      magueyesOrdenados = sortedSpeciesEntries.map(([name, v]) => {
        const pct = Math.round((v / totalVolMezcla) * 100);
        return `${name} (${pct}%)`;
      }).join(" + ");
    } else if (sortedSpeciesEntries.length > 0) {
      magueyesOrdenados = sortedSpeciesEntries[0][0];
    }

    return {
      totalVolMezcla,
      alcPonderadoMezcla,
      ingredientesUnicos,
      magueyesOrdenados,
      categoriaBase,
      claseBase,
      categorias,
      clases,
      tieneConflictoCategoria,
      tieneConflictoClase,
      selectedLots
    };
  }, [fusionSources, allAvailableGraneles, activeCatalog]);

  const handleExecuteFusion = (e, statusToApply = "revision") => {
    if (e && e.preventDefault) e.preventDefault();
    if (isSubmittingFusion) return;

    const validSources = fusionSources.filter(s => s.granelId && parseFloat(s.volExtraer) > 0);
    if (validSources.length < 1) {
      Swal.fire("Atención", "Por favor seleccione al menos 1 lote de granel con volumen a extraer positivo.", "warning");
      return;
    }

    if (fusionCalculations.tieneConflictoCategoria) {
      Swal.fire("Incompatibilidad NOM-070", `No se pueden mezclar lotes de diferentes categorías (${fusionCalculations.categorias.join(", ")}). Todos los lotes deben ser de la misma categoría.`, "error");
      return;
    }

    if (fusionCalculations.tieneConflictoClase) {
      Swal.fire("Incompatibilidad NOM-070", `No se pueden mezclar lotes de diferentes clases (${fusionCalculations.clases.join(", ")}). Todos los lotes deben ser de la misma clase.`, "error");
      return;
    }

    if (!fusionForm.noLote || !fusionForm.idContenedor) {
      Swal.fire("Atención", "Por favor complete el Nº de Lote y el ID Contenedor para el nuevo registro.", "warning");
      return;
    }

    // Verificar que no se extraiga más del saldo disponible de cada lote origen
    for (const src of validSources) {
      const gRecord = (allAvailableGraneles || []).find(g => (
        String(g.id_bitacora_granel) === String(src.granelId) ||
        String(g.id) === String(src.granelId) ||
        String(g.dbId) === String(src.granelId) ||
        String(g.noLote || g.no_lote) === String(src.granelId) ||
        String(g._optVal) === String(src.granelId)
      ));
      if (gRecord) {
        const saldoDisp = gRecord._vDisp ?? (parseFloat(String(gRecord.invFinalVol ?? gRecord.saldoFinal ?? gRecord.volFinal ?? gRecord.volumen ?? gRecord.litros ?? gRecord.litrosTotal ?? gRecord.entradaVolumen ?? 0).replace(/[^\d.]/g, '')) || 0);
        const extraido = parseFloat(src.volExtraer) || 0;
        if (extraido > saldoDisp) {
          Swal.fire("Volumen Insuficiente", `El lote ${gRecord.noLote || gRecord.idContenedor} solo tiene ${saldoDisp} L disponibles y se intentó extraer ${extraido} L.`, "error");
          return;
        }
      }
    }

    const isBorrador = statusToApply === "borrador";
    const statusFinal = isBorrador ? "borrador" : "revision";
    const statusUIFinal = isBorrador ? "borrador" : "revision";

    const { totalVolMezcla, alcPonderadoMezcla, ingredientesUnicos, magueyesOrdenados, categoriaBase, claseBase } = fusionCalculations;

    const procedenciaDesc = validSources.length === 1
      ? `Extracción parcial de lote origen ${validSources[0].granelId}`
      : `Mezcla/Fusión de ${validSources.length} lotes de granel`;

    // Enriquecer validSources con id_granel_origen y no_lote_origen precisos
    const enrichedSources = validSources.map(s => {
      const g = (allAvailableGraneles || []).find(item => (
        String(item.id_bitacora_granel) === String(s.granelId) ||
        String(item.id) === String(s.granelId) ||
        String(item.dbId) === String(s.granelId) ||
        String(item.noLote || item.no_lote) === String(s.granelId) ||
        String(item._optVal) === String(s.granelId)
      ));
      return {
        ...s,
        id_granel_origen: g?.id_bitacora_granel || g?.id || g?.dbId || null,
        no_lote_origen: g?.noLote || g?.no_lote || s.granelId
      };
    });

    // Crear el nuevo registro de Granel
    // Nota: El FQ queda invalidado y por tanto en blanco
    const newGranelData = {
      id: `GRANEL-${validSources.length === 1 ? 'EXTRACCION' : 'MEZCLA'}-${Date.now().toString().slice(-4)}`,
      fechaMovimiento: fusionForm.fechaMovimiento || new Date().toISOString().split('T')[0],
      fecha: fusionForm.fechaMovimiento || new Date().toISOString().split('T')[0],
      noLote: fusionForm.noLote,
      no_lote: fusionForm.noLote,
      idContenedor: fusionForm.idContenedor,
      no_tanque: fusionForm.idContenedor,
      categoria: categoriaBase,
      clase: claseBase,
      ingredientes: fusionForm.ingredientes || ingredientesUnicos,
      ingrediente: fusionForm.ingredientes || ingredientesUnicos,
      tipoMaguey: magueyesOrdenados || fusionForm.tipoMaguey || "",
      especie: magueyesOrdenados || fusionForm.tipoMaguey || "",
      noAnalisis: "", // FQ invalidado por mezcla/extracción
      noAnalisisFisicoquimico: "",
      clave_fq: "",
      invInicialVol: totalVolMezcla.toString(),
      invInicialAlc: alcPonderadoMezcla,
      entradaProcedencia: procedenciaDesc,
      entradaVolumen: totalVolMezcla.toString(),
      entradaAlc: alcPonderadoMezcla,
      salidaDestino: "",
      salidaVolumen: "0",
      salidaAlc: "0",
      invFinalVol: totalVolMezcla.toString(),
      invFinalAlc: alcPonderadoMezcla,
      litros: totalVolMezcla,
      porcentaje_alc_vol: parseFloat(alcPonderadoMezcla) || 0,
      observaciones: fusionForm.observaciones || `${procedenciaDesc}. ${validSources.length > 1 ? `Especies en orden de proporción: ${magueyesOrdenados}.` : ''} Análisis FQ invalidado pendiente nuevo análisis.`,
      origenFuentes: enrichedSources.map(s => ({
        granelId: s.granelId,
        id_granel_origen: s.id_granel_origen,
        no_lote_origen: s.no_lote_origen,
        volExtraer: parseFloat(s.volExtraer) || 0
      })),
      esFusionOExtraccion: true,
      status: statusFinal,
      statusUI: statusUIFinal
    };

    setIsSubmittingFusion(true);
    try {
      if (handleSaveFusion) {
        handleSaveFusion(newGranelData, enrichedSources, statusFinal);
      } else {
        handleSaveGranel(null, statusFinal, newGranelData);
      }
      setIsFusionModalOpen(false);
    } finally {
      setIsSubmittingFusion(false);
    }
    Swal.fire({
      title: isBorrador ? "¡Borrador Guardado!" : (validSources.length === 1 ? "¡Extracción Enviada a Revisión UI!" : "¡Mezcla Enviada a Revisión UI!"),
      html: `Se ha registrado el nuevo lote a granel <b>${fusionForm.noLote}</b> con <b>${totalVolMezcla} L</b> al <b>${alcPonderadoMezcla}% Alc. Vol.</b> ${isBorrador ? '(en modo Borrador)' : 'y enviado a validación de la Unidad de Inspección'}. Se aplicó el descuento correspondiente en el lote de origen.`,
      icon: "success",
      confirmButtonColor: "#7C3AED"
    });
  };

  // Filtrado de la tabla histórica
  const filteredRecords = useMemo(() => {
    return (granelRecords || []).filter(rec => {
      const term = search.toLowerCase();
      const matchSearch =
        (rec.noLote && String(rec.noLote).toLowerCase().includes(term)) ||
        (rec.idContenedor && String(rec.idContenedor).toLowerCase().includes(term)) ||
        (rec.categoria && String(rec.categoria).toLowerCase().includes(term)) ||
        (rec.clase && String(rec.clase).toLowerCase().includes(term)) ||
        (rec.tipoMaguey && String(rec.tipoMaguey).toLowerCase().includes(term)) ||
        (rec.inspector && String(rec.inspector).toLowerCase().includes(term));

      const rawSt = String(rec.status || rec.statusUI || '').toLowerCase();
      let matchStatus = true;
      if (statusFilter !== "todos") {
        if (statusFilter === "aprobado") matchStatus = rawSt.includes("aprobad") || rawSt.includes("aceptad");
        else if (statusFilter === "enviada") matchStatus = rawSt.includes("enviad") || rawSt.includes("revision") || rawSt.includes("pendiente");
        else if (statusFilter === "borrador") matchStatus = rawSt.includes("borrador");
        else if (statusFilter === "rechazado") matchStatus = rawSt.includes("rechazad");
        else if (statusFilter === "cancelado") matchStatus = rawSt.includes("cancelad");
      }

      let matchClase = true;
      if (claseFilter !== "todos") {
        const cl = String(rec.clase || '').toLowerCase();
        if (claseFilter === "joven") matchClase = cl.includes("joven") || cl.includes("blanco");
        else if (claseFilter === "reposado") matchClase = cl.includes("reposado");
        else if (claseFilter === "anejo") matchClase = cl.includes("añejo") || cl.includes("anejo");
        else if (claseFilter === "madurado_vidrio") matchClase = cl.includes("vidrio") || cl.includes("madurado");
        else if (claseFilter === "abocado") matchClase = cl.includes("abocado");
        else if (claseFilter === "destilado_con") matchClase = cl.includes("destilado con");
      }

      return matchSearch && matchStatus && matchClase;
    });
  }, [granelRecords, search, statusFilter, claseFilter]);

  return (
    <div className="animate-fadeIn">
      {/* =========================================================================
          1. HEADER DE HISTORIAL Y CONTROLES DE FILTRADO
      ========================================================================= */}
      <div className="destilacion-toolbar-container" style={{ borderColor: "#A7F3D0" }}>
        <div className="destilacion-toolbar-top">
          <div className="destilacion-toolbar-title-box">
            <h2>
              <Inventory2Icon style={{ color: "#059669", fontSize: 24 }} />
              Inventarios de Mezcal a Granel (Sección II)
            </h2>
            <p>
              Inventario final, custodia en contenedores, kardex de entradas/salidas y fusiones/mezclas de producto terminado
            </p>
          </div>

          <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => {
                setFusionSources([
                  { granelId: "", volExtraer: "" }
                ]);
                setFusionForm({
                  fechaMovimiento: new Date().toISOString().split('T')[0],
                  noLote: `LOTE-GRANEL-${Date.now().toString().slice(-4)}`,
                  idContenedor: `TANQUE-01`,
                  categoria: "",
                  clase: "",
                  ingredientes: "",
                  tipoMaguey: "",
                  noAnalisis: "", // FQ queda en blanco conforme a requerimiento
                  observaciones: ""
                });
                setIsFusionModalOpen(true);
              }}
              className="btn-secondary-custom"
              style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 16px", background: "#F5F3FF", color: "#7C3AED", border: "1px solid #DDD6FE", borderRadius: "12px", fontWeight: 700, fontSize: "12.5px" }}
            >
              <MergeTypeIcon style={{ fontSize: 18 }} />
              <span>Fusión / Extracción</span>
            </button>

            <button
              type="button"
              onClick={() => handleOpenMovimientosModal()}
              className="btn-secondary-custom"
              style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 16px", background: "#CCFBF1", color: "#0F766E", border: "1px solid #99F6E4", borderRadius: "12px", fontWeight: 700, fontSize: "12.5px" }}
              title="Historial de Movimientos y Estatus de Aprobación UI"
            >
              <HistoryIcon style={{ fontSize: 18 }} />
              <span>Movimientos</span>
            </button>

            <button
              type="button"
              onClick={() => handleOpenTraslado()}
              className="btn-secondary-custom"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 14px", background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE", borderRadius: "12px", fontWeight: 700, fontSize: "12.5px" }}
              title="Trasladar mezcal a granel a otra instalación registrada"
            >
              <LocalShippingIcon style={{ fontSize: 18 }} />
              <span>Traslado Instalaciones</span>
            </button>

            <button
              type="button"
              onClick={() => handleOpenDilucion()}
              className="btn-secondary-custom"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 14px", background: "#F0FDFA", color: "#0D9488", border: "1px solid #99F6E4", borderRadius: "12px", fontWeight: 700, fontSize: "12.5px" }}
              title="Ajuste o dilución de grado alcohólico con agua"
            >
              <OpacityIcon style={{ fontSize: 18 }} />
              <span>Ajuste / Dilución</span>
            </button>

            <button
              type="button"
              onClick={() => handleOpenBaja()}
              className="btn-secondary-custom"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 14px", background: "#FFFBEB", color: "#D97706", border: "1px solid #FDE68A", borderRadius: "12px", fontWeight: 700, fontSize: "12.5px" }}
              title="Salida o baja de producto por merma, evaporación, desecho o venta"
            >
              <TrendingDownIcon style={{ fontSize: 18 }} />
              <span>Baja / Merma</span>
            </button>

            <button
              type="button"
              onClick={() => handleOpenMuestreo()}
              className="btn-secondary-custom"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 14px", background: "#EEF2FF", color: "#4F46E5", border: "1px solid #C7D2FE", borderRadius: "12px", fontWeight: 700, fontSize: "12.5px" }}
              title="Salida a muestreo para análisis de laboratorio o UI"
            >
              <BiotechIcon style={{ fontSize: 18 }} />
              <span>Muestreo</span>
            </button>

            <button
              type="button"
              onClick={() => {
                const today = new Date().toISOString().split('T')[0];
                const defInst = envasadoInstalaciones[0];
                const defInstId = defInst ? (defInst.idanexocliente || defInst.id) : "";
                const defAlias = defInst ? (defInst.informaciongeneralinstalacioncliente?.alias || defInst.alias || "") : "";
                setFormData({
                  id: null,
                  fechaMovimiento: today,
                  fecha: today,
                  id_unidad_produccion: defInstId,
                  id_anexo_cliente: defInstId,
                  instalacion: defAlias,
                  instalacionOrigen: defAlias,
                  idContenedor: "",
                  no_tanque: "",
                  noLote: "",
                  no_lote: "",
                  categoria: "Mezcal Artesanal",
                  clase: "Blanco Joven",
                  especie: "",
                  tipoMaguey: "",
                  ingrediente: "",
                  ingredientes: "",
                  clave_fq: "",
                  noAnalisis: "",
                  litros: "",
                  porcentaje_alc_vol: "",
                  invInicialVol: "0",
                  invInicialAlc: "0",
                  entradaProcedencia: "Ingreso a Envasado",
                  entradaVolumen: "0",
                  entradaAlc: "0",
                  salidaDestino: "",
                  salidaVolumen: "0",
                  salidaAlc: "0",
                  invFinalVol: "0",
                  invFinalAlc: "0",
                  observaciones: "Ingreso a inventario de mezcal a granel para envasado.",
                  origen_registro: "manual",
                  status: "borrador"
                });
                setIsFormOpen(true);
              }}
              className="destilacion-btn-new"
              style={{ background: "linear-gradient(135deg, #059669 0%, #10B981 100%)", boxShadow: "0 4px 14px rgba(5, 150, 105, 0.3)" }}
            >
              <AddIcon style={{ fontSize: 18 }} />
              <span>Nuevo Movimiento a Granel</span>
            </button>

            <button
              type="button"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`btn-refresh-granel ${isRefreshing ? "spinning" : ""}`}
              title="Sincronizar y actualizar tabla con los últimos registros de la Unidad de Inspección"
            >
              <RefreshIcon className="refresh-icon" style={{ fontSize: 18 }} />
              <span>{isRefreshing ? "Actualizando..." : "Actualizar Tabla"}</span>
            </button>
          </div>
        </div>

        <div className="destilacion-toolbar-controls">
          {/* Búsqueda */}
          <div className="destilacion-search-box">
            <SearchIcon className="destilacion-search-icon" style={{ color: "#059669" }} />
            <input
              type="text"
              placeholder="Buscar por contenedor, lote, clase, maguey..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                className="destilacion-search-clear"
                onClick={() => setSearch("")}
                title="Limpiar búsqueda"
              >
                <CloseIcon style={{ fontSize: 14 }} />
              </button>
            )}
          </div>

          {/* Filtro por estado */}
          <div className="destilacion-filter-wrapper">
            <FilterListIcon className="destilacion-filter-icon" style={{ color: "#059669" }} />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="destilacion-filter-select"
            >
              <option value="todos">Todos los Estados</option>
              <option value="aprobado">Aceptadas por UI</option>
              <option value="enviada">Enviadas / En Revisión</option>
              <option value="borrador">Borradores</option>
              <option value="rechazado">Rechazadas (Corrección)</option>
              <option value="cancelado">Canceladas</option>
            </select>
          </div>

          {/* Filtro por clase */}
          <div className="destilacion-filter-wrapper">
            <LocalDrinkIcon className="destilacion-filter-icon" style={{ color: "#059669" }} />
            <select
              value={claseFilter}
              onChange={e => setClaseFilter(e.target.value)}
              className="destilacion-filter-select"
            >
              <option value="todos">Todas las Clases</option>
              <option value="joven">Joven / Blanco</option>
              <option value="reposado">Reposado</option>
              <option value="anejo">Añejo</option>
              <option value="madurado_vidrio">Madurado en Vidrio</option>
              <option value="abocado">Abocado con</option>
              <option value="destilado_con">Destilado con</option>
            </select>
          </div>

          {/* Botón Reset */}
          {(search || statusFilter !== "todos" || claseFilter !== "todos") && (
            <button
              type="button"
              className="destilacion-btn-reset"
              onClick={() => {
                setSearch("");
                setStatusFilter("todos");
                setClaseFilter("todos");
              }}
              title="Restablecer filtros"
            >
              <CloseIcon style={{ fontSize: 15 }} />
              <span>Limpiar Filtros</span>
            </button>
          )}

          {/* Botón Refrescar Rápido */}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={`destilacion-btn-reset ${isRefreshing ? "spinning" : ""}`}
            style={{
              background: "#F0FDFA",
              color: "#0F766E",
              border: "1px solid #99F6E4",
              fontWeight: 700,
              cursor: isRefreshing ? "not-allowed" : "pointer"
            }}
            title="Refrescar datos de la tabla"
          >
            <RefreshIcon className="refresh-icon" style={{ fontSize: 16 }} />
            <span>{isRefreshing ? "Cargando..." : "Refrescar"}</span>
          </button>
        </div>
      </div>

      {/* =========================================================================
          2. TABLA HISTÓRICA DE INVENTARIO A GRANEL
      ========================================================================= */}
      <div className="table-responsive-container">
        <table className="custom-table-bitacora">
          <thead>
            <tr>
              <th>Fecha Mov.</th>
              <th>Instalación</th>
              <th>Lote / Contenedor</th>
              <th>Categoría / Clase</th>
              <th>Nº Análisis</th>
              <th>Saldo Final</th>
              <th>Inspector Asignado</th>
              <th>Estatus UI</th>
              <th style={{ textAlign: "center" }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.length === 0 ? (
              <tr>
                <td colSpan="9" className="empty-table-cell" style={{ textAlign: "center", padding: "36px", color: "var(--text-muted)" }}>
                  <Inventory2Icon style={{ fontSize: 44, opacity: 0.35, color: "#059669", marginBottom: "8px" }} />
                  <p style={{ margin: 0, fontWeight: 600 }}>No hay registros de inventario a granel disponibles.</p>
                </td>
              </tr>
            ) : (
              filteredRecords.map((rec, idx) => {
                const volFinalNum = parseFloat(String(rec.invFinalVol || rec.volFinal || 0).replace(/[^\d.]/g, '')) || 0;
                const alcFinalNum = parseFloat(String(rec.invFinalAlc || rec.alcFinal || 0).replace(/[^\d.]/g, '')) || 0;

                return (
                  <tr key={rec.id || idx}>
                    <td style={{ fontWeight: 600 }}>{rec.fechaMovimiento || "-"}</td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <ApartmentIcon style={{ fontSize: 16, color: "#2563EB", flexShrink: 0 }} />
                        <span style={{ fontWeight: 600, color: "#1E293B", fontSize: "12.5px" }} title={rec.instalacion || "Instalación Principal"}>
                          {rec.instalacion || rec.instalacionOrigen || "Instalación Principal"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <strong style={{ color: "#059669", fontSize: "13.5px" }}>Lote: {rec.noLote || rec.noProduccion || "-"}</strong>
                        <span style={{ fontSize: "11.5px", color: "var(--text-muted)" }}>{rec.idContenedor || "-"}</span>
                      </div>
                    </td>
                    <td>
                      <div>
                        <strong style={{ color: "var(--text-primary)" }}>{rec.categoria || "Mezcal"}</strong>
                        <div style={{ fontSize: "11.5px", color: "#64748B" }}>{rec.clase || "Blanco Joven"}</div>
                      </div>
                    </td>
                    <td>
                      <span style={{ fontSize: "12px", fontFamily: "monospace", background: "#F1F5F9", padding: "2px 6px", borderRadius: "6px" }}>
                        {rec.noAnalisisFisicoquimico || rec.noAnalisis || "-"}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <strong style={{ color: "#059669", fontSize: "14px" }}>{volFinalNum.toLocaleString()} L</strong>
                        <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>@ {alcFinalNum}% Alc.</span>
                        {(() => {
                          const movs = Array.isArray(rec.movimientos) ? rec.movimientos : [];
                          const obsStr = String(rec.observaciones || "");

                          const isDilApproved = Boolean(movs.some(m => m.tipo_movimiento === "dilucion" && (m.es_aprobado || m.estado_ui === "Aprobado por UI")) || obsStr.includes("[DILUCIÓN APROBADA POR UI]"));
                          const pendingDil = !isDilApproved ? (rec.dilucionPendiente || movs.find(m => m.tipo_movimiento === "dilucion" && (m.es_pendiente || (!m.es_aprobado && !m.es_rechazado && (String(m.motivo || "").toLowerCase().includes("pendiente") || String(m.observaciones || "").toLowerCase().includes("pendiente")))))) : null;
                          const hasPending = Boolean(!isDilApproved && (rec.tieneDilucionPendiente || pendingDil || (!obsStr.includes("[DILUCIÓN APROBADA POR UI]") && obsStr.includes("[SOLICITUD DILUCIÓN PENDIENTE UI]"))));
                          if (hasPending) {
                            const aguaL = pendingDil ? parseFloat(pendingDil.agua_agregada || 0) : null;
                            return (
                              <span style={{ fontSize: "10px", color: "#0F766E", fontWeight: 800, marginTop: "2px", background: "#CCFBF1", padding: "1px 5px", borderRadius: "4px", width: "fit-content" }} title="Dilución pendiente de validación UI">
                                💧 Dilución: {aguaL ? `+${aguaL} L` : "En revisión"}
                              </span>
                            );
                          }
                          if (isDilApproved) {
                            return (
                              <span style={{ fontSize: "10px", color: "#047857", fontWeight: 700, marginTop: "2px", background: "#D1FAE5", padding: "1px 5px", borderRadius: "4px", width: "fit-content" }} title="Dilución dictaminada y aplicada oficialmente">
                                💧 Dilución aplicada
                              </span>
                            );
                          }

                          const isBajaApproved = Boolean(movs.some(m => (m.tipo_movimiento === "baja" || m.tipo_movimiento === "salida") && (m.es_aprobado || m.estado_ui === "Aprobado por UI")) || obsStr.includes("[BAJA APROBADA POR UI]"));
                          const pendingBaja = !isBajaApproved ? (rec.bajaPendiente || movs.find(m => (m.tipo_movimiento === "baja" || m.tipo_movimiento === "salida") && (m.es_pendiente || (!m.es_aprobado && !m.es_rechazado && (String(m.motivo || "").toLowerCase().includes("pendiente") || String(m.observaciones || "").toLowerCase().includes("pendiente")))))) : null;
                          const hasPendingBaja = Boolean(!isBajaApproved && (rec.tieneBajaPendiente || pendingBaja || (!obsStr.includes("[BAJA APROBADA POR UI]") && obsStr.includes("[SOLICITUD BAJA PENDIENTE UI]"))));
                          if (hasPendingBaja) {
                            const volB = pendingBaja ? parseFloat(pendingBaja.litros_transferidos || 0) : null;
                            return (
                              <span style={{ fontSize: "10px", color: "#C2410C", fontWeight: 800, marginTop: "2px", background: "#FFEDD5", padding: "1px 5px", borderRadius: "4px", width: "fit-content" }} title="Baja de volumen pendiente de dictamen UI">
                                📦 Baja: {volB ? `-${volB} L` : "En revisión"}
                              </span>
                            );
                          }

                          const isMuesApproved = Boolean(movs.some(m => m.tipo_movimiento === "muestreo" && (m.es_aprobado || m.estado_ui === "Aprobado por UI")) || obsStr.includes("[MUESTREO APROBADO POR UI]"));
                          const pendingMues = !isMuesApproved ? (rec.muestreoPendiente || movs.find(m => m.tipo_movimiento === "muestreo" && (m.es_pendiente || (!m.es_aprobado && !m.es_rechazado && (String(m.motivo || "").toLowerCase().includes("pendiente") || String(m.observaciones || "").toLowerCase().includes("pendiente")))))) : null;
                          const hasPendingMues = Boolean(!isMuesApproved && (rec.tieneMuestreoPendiente || pendingMues || (!obsStr.includes("[MUESTREO APROBADO POR UI]") && obsStr.includes("[SOLICITUD MUESTREO PENDIENTE UI]"))));
                          if (hasPendingMues) {
                            const volM = pendingMues ? parseFloat(pendingMues.litros_transferidos || 0) : null;
                            return (
                              <span style={{ fontSize: "10px", color: "#4338CA", fontWeight: 800, marginTop: "2px", background: "#EEF2FF", padding: "1px 5px", borderRadius: "4px", width: "fit-content" }} title="Extracción de muestra pendiente de dictamen UI">
                                🧪 Muestra: {volM ? `-${volM} L` : "En revisión"}
                              </span>
                            );
                          }

                          const isTrasApproved = Boolean(movs.some(m => m.tipo_movimiento === "traslado" && (m.es_aprobado || m.estado_ui === "Aprobado por UI")) || obsStr.includes("[TRASLADO APROBADO POR UI]"));
                          const pendingTras = !isTrasApproved ? (rec.trasladoPendiente || movs.find(m => m.tipo_movimiento === "traslado" && (m.es_pendiente || (!m.es_aprobado && !m.es_rechazado && (String(m.motivo || "").toLowerCase().includes("pendiente") || String(m.observaciones || "").toLowerCase().includes("pendiente")))))) : null;
                          const hasPendingTras = Boolean(!isTrasApproved && (rec.tieneTrasladoPendiente || pendingTras || (!obsStr.includes("[TRASLADO APROBADO POR UI]") && obsStr.includes("[SOLICITUD TRASLADO PENDIENTE UI]"))));
                          if (hasPendingTras) {
                            const volT = pendingTras ? parseFloat(pendingTras.litros_transferidos || 0) : null;
                            return (
                              <span style={{ fontSize: "10px", color: "#1D4ED8", fontWeight: 800, marginTop: "2px", background: "#EFF6FF", padding: "1px 5px", borderRadius: "4px", width: "fit-content" }} title="Traslado a otra instalación pendiente de dictamen UI">
                                🚛 Traslado: {volT ? `-${volT} L` : "En revisión"}
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span style={{ fontSize: "14px" }}>👮‍♂️</span>
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: (rec.inspector && rec.inspector !== 'Sin asignar' && !String(rec.inspector).includes('Pendiente')) ? '#0F766E' : '#64748B'
                          }}
                          title={rec.inspector || 'Pendiente de Asignación UI'}
                        >
                          {rec.inspector || 'Pendiente de Asignación UI'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        {(() => {
                          const raw = String(rec.status || rec.statusUI || rec.estatus || 'borrador').toLowerCase();
                          let badgeClass = 'borrador';
                          let label = 'Borrador';

                          if (raw.includes('aprobad') || raw.includes('aceptad')) {
                            badgeClass = 'aprobado';
                            label = 'Aprobado por UI';
                          } else if (raw.includes('rechazad')) {
                            badgeClass = 'rechazado';
                            label = 'Rechazado';
                          } else if (raw.includes('revision') || raw.includes('enviad') || raw.includes('pendiente')) {
                            badgeClass = 'revision';
                            label = 'En Revisión UI';
                          } else if (raw.includes('cancelad')) {
                            badgeClass = 'cancelado';
                            label = 'Cancelado';
                          }

                          return (
                            <span className={`badge-status ${badgeClass}`}>
                              {label}
                            </span>
                          );
                        })()}

                        {/* Observaciones de corrección/rechazo de UI */}
                        {(() => {
                          const raw = String(rec.status || rec.statusUI || rec.estatus || '').toLowerCase();
                          const obsRechazo = rec.observaciones_correccion || rec.observacionesCorreccion || rec.observaciones_rechazo;
                          if (raw.includes('rechazad') && obsRechazo) {
                            return (
                              <span
                                style={{
                                  fontSize: "10.5px",
                                  background: "#FEF2F2",
                                  color: "#991B1B",
                                  border: "1px solid #FCA5A5",
                                  borderRadius: "6px",
                                  padding: "2px 6px",
                                  fontWeight: 600,
                                  lineHeight: 1.3,
                                  marginTop: "2px",
                                  display: "inline-block"
                                }}
                                title={`Motivo de rechazo UI: ${obsRechazo}`}
                              >
                                ⚠️ Motivo: {obsRechazo}
                              </span>
                            );
                          }
                          return null;
                        })()}
                        {(() => {
                          const movs = Array.isArray(rec.movimientos) ? rec.movimientos : [];
                          const obsStr = String(rec.observaciones || "");

                          const isDilApproved = Boolean(movs.some(m => m.tipo_movimiento === "dilucion" && (m.es_aprobado || m.estado_ui === "Aprobado por UI")) || obsStr.includes("[DILUCIÓN APROBADA POR UI]"));
                          const hasPendingDil = Boolean(!isDilApproved && (rec.tieneDilucionPendiente || movs.some(m => m.tipo_movimiento === "dilucion" && (m.es_pendiente || (!m.es_aprobado && !m.es_rechazado && (String(m.motivo || "").toLowerCase().includes("pendiente") || String(m.observaciones || "").toLowerCase().includes("pendiente"))))) || (!obsStr.includes("[DILUCIÓN APROBADA POR UI]") && obsStr.includes("[SOLICITUD DILUCIÓN PENDIENTE UI]"))));
                          if (hasPendingDil) {
                            return (
                              <span style={{ fontSize: "10px", background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D", borderRadius: "6px", padding: "2px 6px", fontWeight: 800, whiteSpace: "nowrap" }} title="Solicitud de ajuste de agua en espera de dictamen UI">
                                ⏳ Revisión Dilución UI
                              </span>
                            );
                          }

                          const isBajaApproved = Boolean(movs.some(m => (m.tipo_movimiento === "baja" || m.tipo_movimiento === "salida") && (m.es_aprobado || m.estado_ui === "Aprobado por UI")) || obsStr.includes("[BAJA APROBADA POR UI]"));
                          const hasPendingBaja = Boolean(!isBajaApproved && (rec.tieneBajaPendiente || movs.some(m => (m.tipo_movimiento === "baja" || m.tipo_movimiento === "salida") && (m.es_pendiente || (!m.es_aprobado && !m.es_rechazado && (String(m.motivo || "").toLowerCase().includes("pendiente") || String(m.observaciones || "").toLowerCase().includes("pendiente"))))) || (!obsStr.includes("[BAJA APROBADA POR UI]") && obsStr.includes("[SOLICITUD BAJA PENDIENTE UI]"))));
                          if (hasPendingBaja) {
                            return (
                              <span style={{ fontSize: "10px", background: "#FFEDD5", color: "#C2410C", border: "1px solid #FDBA74", borderRadius: "6px", padding: "2px 6px", fontWeight: 800, whiteSpace: "nowrap" }} title="Solicitud de egreso/baja en espera de dictamen UI">
                                ⏳ Revisión Baja UI
                              </span>
                            );
                          }

                          const isMuesApproved = Boolean(movs.some(m => m.tipo_movimiento === "muestreo" && (m.es_aprobado || m.estado_ui === "Aprobado por UI")) || obsStr.includes("[MUESTREO APROBADO POR UI]"));
                          const hasPendingMues = Boolean(!isMuesApproved && (rec.tieneMuestreoPendiente || movs.some(m => m.tipo_movimiento === "muestreo" && (m.es_pendiente || (!m.es_aprobado && !m.es_rechazado && (String(m.motivo || "").toLowerCase().includes("pendiente") || String(m.observaciones || "").toLowerCase().includes("pendiente"))))) || (!obsStr.includes("[MUESTREO APROBADO POR UI]") && obsStr.includes("[SOLICITUD MUESTREO PENDIENTE UI]"))));
                          if (hasPendingMues) {
                            return (
                              <span style={{ fontSize: "10px", background: "#EEF2FF", color: "#4338CA", border: "1px solid #C7D2FE", borderRadius: "6px", padding: "2px 6px", fontWeight: 800, whiteSpace: "nowrap" }} title="Extracción de muestra en espera de dictamen UI">
                                ⏳ Revisión Muestra UI
                              </span>
                            );
                          }

                          const isTrasApproved = Boolean(movs.some(m => m.tipo_movimiento === "traslado" && (m.es_aprobado || m.estado_ui === "Aprobado por UI")) || obsStr.includes("[TRASLADO APROBADO POR UI]"));
                          const hasPendingTras = Boolean(!isTrasApproved && (rec.tieneTrasladoPendiente || movs.some(m => m.tipo_movimiento === "traslado" && (m.es_pendiente || (!m.es_aprobado && !m.es_rechazado && (String(m.motivo || "").toLowerCase().includes("pendiente") || String(m.observaciones || "").toLowerCase().includes("pendiente"))))) || (!obsStr.includes("[TRASLADO APROBADO POR UI]") && obsStr.includes("[SOLICITUD TRASLADO PENDIENTE UI]"))));
                          if (hasPendingTras) {
                            return (
                              <span style={{ fontSize: "10px", background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #93C5FD", borderRadius: "6px", padding: "2px 6px", fontWeight: 800, whiteSpace: "nowrap" }} title="Solicitud de traslado en espera de dictamen UI">
                                ⏳ Revisión Traslado UI
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "6px", justifyContent: "center", alignItems: "center" }}>
                        <button
                          type="button"
                          onClick={() => setSelectedRecord(rec)}
                          title="Ver Detalle Completo (Identificación e Inventarios)"
                          className="btn-action-icon view"
                        >
                          <VisibilityIcon style={{ fontSize: 16 }} />
                        </button>

                        <button
                          type="button"
                          onClick={() => handleOpenMovimientosModal(rec)}
                          title="Ver Movimientos y Estatus de este Lote"
                          className="btn-action-icon"
                          style={{ color: "#0F766E", background: "#F0FDFA", border: "1px solid #CCFBF1" }}
                        >
                          <HistoryIcon style={{ fontSize: 16 }} />
                        </button>

                        {String(rec.status || rec.statusUI || '').toLowerCase().includes('borrador') && handleUndoGranel && (
                          <button
                            type="button"
                            onClick={() => handleUndoGranel(rec)}
                            title="Deshacer Movimiento (Solo en Borrador) y Regresar Volumen"
                            className="btn-action-icon delete"
                          >
                            <UndoIcon style={{ fontSize: 16 }} />
                          </button>
                        )}

                        {(String(rec.status || rec.statusUI || rec.estatus || '').toLowerCase().includes('borrador') || String(rec.status || rec.statusUI || rec.estatus || '').toLowerCase().includes('rechaz')) && handleSaveGranel && (
                          <button
                            type="button"
                            onClick={() => handleSaveGranel(null, "revision", rec)}
                            title={String(rec.status || rec.statusUI || rec.estatus || '').toLowerCase().includes('rechaz') ? "Reenviar Lote a Revisión de Unidad de Inspección" : "Enviar Borrador a Revisión de Unidad de Inspección"}
                            className="btn-action-icon"
                            style={{ color: "#059669", background: "#ECFDF5", border: "1px solid #A7F3D0" }}
                          >
                            <SendIcon style={{ fontSize: 16 }} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Controles de Paginación */}
      {(() => {
        const totalRecs = Number(pagination?.total ?? (granelRecords?.length || 0));
        const effectiveLimit = Number(limit || pagination?.limit || 10);
        const totalPages = Number(pagination?.totalPages ?? Math.max(1, Math.ceil(totalRecs / effectiveLimit)));
        const pageNum = Number(currentPage || pagination?.page || 1);

        if (totalRecs <= 0 && (!granelRecords || granelRecords.length === 0)) return null;

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "16px",
              marginBottom: "16px",
              padding: "12px 20px",
              background: "#FFFFFF",
              borderRadius: "12px",
              border: "1px solid #E2E8F0",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              flexWrap: "wrap",
              gap: "14px"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <span style={{ fontSize: "13px", color: "var(--text-secondary, #64748B)", fontWeight: 500 }}>
                Mostrando página <strong>{pageNum}</strong> de <strong>{totalPages || 1}</strong> ({totalRecs} lotes en total)
              </span>

              {onLimitChange && (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <span style={{ fontSize: "12px", color: "#94A3B8" }}>Por página:</span>
                  <select
                    className="form-select form-select-sm"
                    value={effectiveLimit}
                    onChange={(e) => onLimitChange(Number(e.target.value))}
                    style={{
                      padding: "3px 24px 3px 8px",
                      fontSize: "12px",
                      borderRadius: "6px",
                      borderColor: "#CBD5E1",
                      backgroundColor: "#F8FAFC",
                      cursor: "pointer",
                      width: "auto"
                    }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              )}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                type="button"
                disabled={pageNum <= 1}
                onClick={() => onPageChange ? onPageChange(Math.max(1, pageNum - 1)) : (setCurrentPage && setCurrentPage(prev => Math.max(1, prev - 1)))}
                className="btn-secondary-custom"
                style={{
                  padding: "6px 14px",
                  fontSize: "12px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: pageNum <= 1 ? "not-allowed" : "pointer",
                  opacity: pageNum <= 1 ? 0.45 : 1,
                  borderRadius: "8px",
                  border: "1px solid #D1D5DB",
                  backgroundColor: "white",
                  color: "#374151"
                }}
              >
                <NavigateBeforeIcon style={{ fontSize: 18 }} /> Anterior
              </button>

              <span
                style={{
                  fontSize: "12.5px",
                  fontWeight: 700,
                  padding: "5px 12px",
                  background: "#F1F5F9",
                  borderRadius: "8px",
                  color: "#1E293B",
                  border: "1px solid #E2E8F0"
                }}
              >
                {pageNum} / {totalPages || 1}
              </span>

              <button
                type="button"
                disabled={pageNum >= totalPages}
                onClick={() => onPageChange ? onPageChange(pageNum + 1) : (setCurrentPage && setCurrentPage(prev => (pageNum < totalPages ? prev + 1 : prev)))}
                className="btn-secondary-custom"
                style={{
                  padding: "6px 14px",
                  fontSize: "12px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: pageNum >= totalPages ? "not-allowed" : "pointer",
                  opacity: pageNum >= totalPages ? 0.45 : 1,
                  borderRadius: "8px",
                  border: "1px solid #D1D5DB",
                  backgroundColor: "white",
                  color: "#374151"
                }}
              >
                Siguiente <NavigateNextIcon style={{ fontSize: 18 }} />
              </button>
            </div>
          </div>
        );
      })()}

      {/* =========================================================================
          3. MODAL DE REGISTRO / EDICIÓN (IMAGEN 1: IDENTIFICACIÓN DEL MEZCAL Y CONTENEDOR)
      ========================================================================= */}
      {isFormOpen && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setIsFormOpen(false)}>
          <div
            className="detail-modal-card animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "850px" }}
          >
            {/* Header del Modal */}
            <div className="detail-modal-header header-theme-granel" style={{ background: "linear-gradient(135deg, #065F46 0%, #059669 100%)", color: "white", padding: "16px 22px" }}>
              <div className="detail-modal-title-box">
                <div className="detail-modal-badge-row" style={{ marginBottom: "4px" }}>
                  {(() => {
                    const isFormRechazado = String(formData.status || formData.statusUI || formData.estatus || '').toLowerCase().includes('rechaz');
                    return (
                      <span className="badge-modal-tag" style={{ background: isFormRechazado ? "#DC2626" : "rgba(255,255,255,0.2)", color: "white", fontWeight: isFormRechazado ? 700 : 500 }}>
                        {isFormRechazado ? 'CORRECCIÓN POR RECHAZO UI' : (formData.id ? 'EDITAR IDENTIFICACIÓN' : 'NUEVO REGISTRO')}
                      </span>
                    );
                  })()}
                  <span className="badge-status enviada" style={{ background: "rgba(255,255,255,0.25)", color: "#FFFFFF" }}>
                    SECCIÓN II: GRANEL
                  </span>
                </div>
                <h1 className="detail-modal-title" style={{ color: "white", fontSize: "19px" }}>
                  {formData.id ? `Editar Contenedor: ${formData.idContenedor || formData.noLote || ''}` : 'Registro de Ingreso a Granel de Envasado'}
                </h1>
              </div>

              <button
                type="button"
                onClick={() => setIsFormOpen(false)}
                className="detail-modal-close-btn"
                title="Cerrar ventana"
                aria-label="Cerrar modal"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Formulario (IMAGEN 1) */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSaveGranel(e, "revision", formData);
              }}
              className="detail-modal-body"
              style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: "16px" }}
            >
              {/* Alerta de Rechazo UI si aplica */}
              {Boolean(formData.observaciones_correccion || formData.observacionesCorreccion || String(formData.status || formData.statusUI || formData.estatus || '').toLowerCase().includes('rechaz')) && (
                <div style={{
                  background: "#FEF2F2",
                  border: "1px solid #F87171",
                  borderRadius: "10px",
                  padding: "14px 18px",
                  color: "#991B1B",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  boxShadow: "0 2px 8px rgba(220, 38, 38, 0.08)"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 700, fontSize: "13.5px" }}>
                    <ReportProblemIcon style={{ fontSize: 20, color: "#DC2626" }} />
                    <span>Observaciones de Revisión de la Unidad de Inspección:</span>
                  </div>
                  <p style={{ margin: 0, fontSize: "13px", color: "#B91C1C", lineHeight: 1.4 }}>
                    {formData.observaciones_correccion || formData.observacionesCorreccion || "Este lote requiere correcciones indicadas por el inspector. Por favor actualice los datos y envíe nuevamente a validación."}
                  </p>
                </div>
              )}

              {/* TARJETA IDENTIFICACIÓN DEL MEZCAL Y CONTENEDOR (IMAGEN 1) */}
              <div className="granel-section-card">
                <div className="granel-section-header">
                  <div className="granel-icon-wrapper-green">
                    <LayersIcon style={{ fontSize: 24 }} />
                  </div>
                  <div className="granel-section-header-titles">
                    <h3>Identificación del Mezcal y Contenedor</h3>
                    <p>Registro de lote a granel para envasado en instalaciones autorizadas</p>
                  </div>
                </div>

                {/* Selector de Instalación */}
                <div style={{ marginBottom: "14px" }}>
                  <label className="granel-input-label">
                    Instalación de Custodia / Procedencia (Envasado) <span className="granel-required-star">*</span>
                  </label>
                  <select
                    name="id_unidad_produccion"
                    value={formData?.id_unidad_produccion || formData?.id_anexo_cliente || ""}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      const foundInst = envasadoInstalaciones.find(i => String(i.idanexocliente || i.id) === String(selectedId));
                      const alias = foundInst?.informaciongeneralinstalacioncliente?.alias || foundInst?.alias || "";
                      setFormData(prev => ({
                        ...prev,
                        id_unidad_produccion: selectedId,
                        id_anexo_cliente: selectedId,
                        instalacion: alias,
                        instalacionOrigen: alias
                      }));
                    }}
                    className="granel-input-control"
                    required
                  >
                    <option value="">-- Seleccionar Instalación de Envasado ({envasadoInstalaciones.length} registradas) --</option>
                    {envasadoInstalaciones.map(inst => {
                      const instId = inst.idanexocliente || inst.id;
                      const aliasLabel = inst.informaciongeneralinstalacioncliente?.alias || inst.alias || `Instalación de Envasado #${instId}`;
                      const dirLabel = inst.direccion || inst.informaciongeneralinstalacioncliente?.direccion || "";
                      return (
                        <option key={instId} value={instId}>
                          [Unidad de Envasado] {aliasLabel} {dirLabel ? ` - ${dirLabel}` : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* FILA 1 (4 COLUMNAS): Fecha * | Número de Tanque * | No. de Lote * | Clave FQ */}
                <div className="granel-grid-row-4">
                  <div className="granel-input-group">
                    <label className="granel-input-label">
                      Fecha <span className="granel-required-star">*</span>
                    </label>
                    <input
                      type="date"
                      name="fechaMovimiento"
                      required
                      value={formData?.fechaMovimiento || formData?.fecha || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData(prev => ({ ...prev, fechaMovimiento: val, fecha: val }));
                      }}
                      className="granel-input-control"
                    />
                  </div>

                  <div className="granel-input-group">
                    <label className="granel-input-label">
                      Número de Tanque <span className="granel-required-star">*</span>
                    </label>
                    <input
                      type="text"
                      name="idContenedor"
                      required
                      placeholder="Ej: CLS-05, C"
                      value={formData?.idContenedor || formData?.no_tanque || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData(prev => ({ ...prev, idContenedor: val, no_tanque: val }));
                      }}
                      className="granel-input-control"
                    />
                  </div>

                  <div className="granel-input-group">
                    <label className="granel-input-label">
                      No. de Lote <span className="granel-required-star">*</span>
                    </label>
                    <input
                      type="text"
                      name="noLote"
                      required
                      placeholder="Ej: AMM-12, A"
                      value={formData?.noLote || formData?.no_lote || ""}
                      onChange={handleLoteSelect}
                      className="granel-input-control"
                    />
                  </div>

                  <div className="granel-input-group">
                    <label className="granel-input-label">
                      Clave FQ
                    </label>
                    <input
                      type="text"
                      name="clave_fq"
                      placeholder="Ej: NNMZ-38"
                      value={formData?.clave_fq || formData?.noAnalisis || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData(prev => ({ ...prev, clave_fq: val, noAnalisis: val }));
                      }}
                      className="granel-input-control"
                    />
                  </div>
                </div>

                {/* FILA 2 (3 COLUMNAS): Especie | Categoría | Clase */}
                <div className="granel-grid-row-3">
                  <div className="granel-input-group">
                    <label className="granel-input-label">
                      Especie <span className="granel-required-star">*</span>
                    </label>
                    <select
                      name="especie"
                      required
                      value={formData?.especie || formData?.tipoMaguey || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          especie: val,
                          tipoMaguey: val,
                          materiaPrima: val
                        }));
                      }}
                      className="granel-input-control"
                    >
                      <option value="">-- Seleccionar Especie del Catálogo ({activeCatalog.length} disponibles) --</option>
                      {activeCatalog.map((cat, idx) => {
                        const comun = cat.nombre_comun || cat.nombreComun || '';
                        const cientifico = cat.nombre_cientifico || cat.nombreCientifico || '';
                        const label = comun && cientifico
                          ? `${comun} (${cientifico})`
                          : (comun || cientifico || `Especie ${idx + 1}`);
                        return (
                          <option key={cat.id_catalogo_maguey || cat.idCatalogoMaguey || cat.nc || idx} value={label}>
                            {label}
                          </option>
                        );
                      })}
                      {formData?.especie && !activeCatalog.some(cat => {
                        const comun = cat.nombre_comun || cat.nombreComun || '';
                        const cientifico = cat.nombre_cientifico || cat.nombreCientifico || '';
                        const label = comun && cientifico ? `${comun} (${cientifico})` : (comun || cientifico);
                        return label === (formData.especie || formData.tipoMaguey);
                      }) && (
                          <option value={formData.especie || formData.tipoMaguey}>
                            {formData.especie || formData.tipoMaguey}
                          </option>
                        )}
                    </select>
                  </div>

                  <div className="granel-input-group">
                    <label className="granel-input-label">
                      Categoría
                    </label>
                    <select
                      name="categoria"
                      value={formData?.categoria || "Mezcal Artesanal"}
                      onChange={handleChange}
                      className="granel-input-control"
                    >
                      <option value="Mezcal">Mezcal</option>
                      <option value="Mezcal Artesanal">Mezcal Artesanal</option>
                      <option value="Mezcal Ancestral">Mezcal Ancestral</option>
                    </select>
                  </div>

                  <div className="granel-input-group">
                    <label className="granel-input-label">
                      Clase
                    </label>
                    <select
                      name="clase"
                      value={formData?.clase || "Blanco Joven"}
                      onChange={(e) => {
                        const newClase = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          clase: newClase,
                          ingrediente: newClase === "Destilado con" ? prev.ingrediente : "",
                          ingredientes: newClase === "Destilado con" ? prev.ingredientes : ""
                        }));
                      }}
                      className="granel-input-control"
                    >
                      <option value="Blanco Joven">Blanco Joven</option>
                      <option value="Destilado con">Destilado con</option>
                      <option value="Reposado">Reposado</option>
                      <option value="Añejo">Añejo</option>
                      <option value="Madurado en Vidrio">Madurado en Vidrio</option>
                      <option value="Abocado con">Abocado con</option>
                    </select>
                  </div>
                </div>

                {/* FILA 3 (3 COLUMNAS): Ingrediente | Litros * | % Alc. Volumen * */}
                <div className="granel-grid-row-3">
                  <div className="granel-input-group">
                    <label className="granel-input-label">
                      Ingrediente
                    </label>
                    <input
                      type="text"
                      name="ingrediente"
                      placeholder="Ej: ---"
                      disabled={formData?.clase !== "Destilado con"}
                      value={formData?.clase === "Destilado con" ? (formData?.ingrediente || formData?.ingredientes || "") : ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData(prev => ({ ...prev, ingrediente: val, ingredientes: val }));
                      }}
                      className="granel-input-control"
                      title={formData?.clase !== "Destilado con" ? "Habilitado únicamente para 'Destilado con'" : ""}
                    />
                  </div>

                  <div className="granel-input-group">
                    <label className="granel-input-label">
                      Litros <span className="granel-required-star">*</span>
                    </label>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      name="litros"
                      required
                      placeholder="Ej: 2,554"
                      value={formData?.litros ?? (formData?.invFinalVol || formData?.entradaVolumen || "")}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          litros: val,
                          invFinalVol: val,
                          entradaVolumen: val,
                          invInicialVol: prev.invInicialVol || val
                        }));
                      }}
                      className="granel-input-control"
                    />
                  </div>

                  <div className="granel-input-group">
                    <label className="granel-input-label">
                      % Alc. Volumen <span className="granel-required-star">*</span>
                    </label>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      max="100"
                      name="porcentaje_alc_vol"
                      required
                      placeholder="Ej: 50.26"
                      value={formData?.porcentaje_alc_vol ?? (formData?.invFinalAlc || formData?.entradaAlc || "")}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          porcentaje_alc_vol: val,
                          invFinalAlc: val,
                          entradaAlc: val,
                          invInicialAlc: prev.invInicialAlc || val
                        }));
                      }}
                      className="granel-input-control"
                    />
                  </div>
                </div>

                {/* FILA 4: NO. DE BARRICAS / RECIPIENTES (DESDE MADURACIÓN O ENTRADA MANUAL) */}
                <div style={{ marginTop: "12px" }}>
                  <div className="granel-input-group">
                    <label className="granel-input-label">
                      No. de Barricas / Recipientes (Opcional - Maduración / Almacén)
                    </label>
                    <input
                      type="text"
                      name="no_barricas"
                      placeholder="Ej: 2 Barricas de Roble Blanco / 10 Garrafas de Vidrio"
                      value={formData?.no_barricas || formData?.noBarricas || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData(prev => ({ ...prev, no_barricas: val, noBarricas: val }));
                      }}
                      className="granel-input-control"
                    />
                  </div>
                </div>
              </div>

              {/* Footer de Acciones del Modal */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "12px", borderTop: "1px solid #E2E8F0" }}>
                <button
                  type="button"
                  onClick={() => setIsFormOpen(false)}
                  className="btn-cancel-modal"
                >
                  Cancelar
                </button>

                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    type="button"
                    onClick={(e) => handleSaveGranel(e, "borrador", formData)}
                    className="btn-cancel-modal"
                    style={{ background: "#F1F5F9", color: "#475569", border: "1px solid #CBD5E1", fontWeight: 700 }}
                  >
                    Guardar Borrador
                  </button>

                  <button
                    type="submit"
                    className="destilacion-btn-new"
                    style={{ background: "linear-gradient(135deg, #059669 0%, #10B981 100%)", boxShadow: "0 4px 14px rgba(5, 150, 105, 0.3)" }}
                  >
                    <CheckCircleIcon style={{ fontSize: 18 }} />
                    <span>
                      {formData.id
                        ? (String(formData.status || formData.statusUI || formData.estatus || '').toLowerCase().includes('rechaz')
                          ? 'Corregir y Enviar a Revisión UI'
                          : 'Guardar y Enviar a Revisión UI')
                        : 'Registrar y Enviar a Revisión UI'}
                    </span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* =========================================================================
          4. MODAL DETALLE COMPLETO (IMAGEN 1 + IMAGEN 2: INVENTARIOS, ENTRADAS Y SALIDAS)
      ========================================================================= */}
      {selectedRecord && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setSelectedRecord(null)}>
          <div
            className="detail-modal-card animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "900px" }}
          >
            {/* Header */}
            <div className="detail-modal-header header-theme-granel" style={{ background: "linear-gradient(135deg, #065F46 0%, #059669 100%)", color: "white", padding: "16px 22px" }}>
              <div className="detail-modal-title-box">
                <span className="badge-modal-tag" style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>
                  DETALLE DE INVENTARIO A GRANEL
                </span>
                <h1 className="detail-modal-title" style={{ color: "white", fontSize: "19px" }}>
                  {selectedRecord.idContenedor || 'Contenedor'} (Lote: {selectedRecord.noLote || selectedRecord.noProduccion})
                </h1>
              </div>
              <button
                type="button"
                onClick={() => setSelectedRecord(null)}
                className="detail-modal-close-btn"
              >
                <CloseIcon />
              </button>
            </div>

            <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: "16px", maxHeight: "80vh", overflowY: "auto" }}>
              {/* SECCIÓN 1: IDENTIFICACIÓN DEL MEZCAL Y CONTENEDOR (IMAGEN 1) */}
              <div className="granel-section-card accent-green" style={{ marginBottom: "12px" }}>
                <div className="granel-section-header">
                  <div className="granel-icon-wrapper-green">
                    <LayersIcon style={{ fontSize: 24 }} />
                  </div>
                  <div className="granel-section-header-titles">
                    <h3>Identificación del Mezcal y Contenedor</h3>
                    <p>Selección de lote validado de destilación o ingreso manual</p>
                  </div>
                </div>

                {/* FILA 1: FECHA | NÚMERO DE TANQUE | NO. DE LOTE | CLAVE FQ */}
                <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: "10px", padding: "10px 14px", marginBottom: "12px", display: "flex", alignItems: "center", gap: "10px" }}>
                  <ApartmentIcon style={{ color: "#2563EB", fontSize: 22 }} />
                  <div>
                    <span style={{ fontSize: "11px", fontWeight: 800, color: "#1E40AF", textTransform: "uppercase" }}>Instalación de Custodia / Procedencia:</span>
                    <div style={{ fontSize: "13.5px", fontWeight: 700, color: "#1E293B" }}>
                      {selectedRecord?.instalacion || selectedRecord?.instalacionOrigen || "Instalación Principal"}
                    </div>
                  </div>
                </div>

                <div className="granel-grid-row-4">
                  <div className="granel-input-group">
                    <label className="granel-input-label">Fecha</label>
                    <input type="text" readOnly value={selectedRecord.fechaMovimiento || selectedRecord.fecha || "-"} className="granel-input-control" />
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">Número de Tanque</label>
                    <input type="text" readOnly value={selectedRecord.idContenedor || selectedRecord.no_tanque || "-"} className="granel-input-control" style={{ fontWeight: 700 }} />
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">No. de Lote</label>
                    <input type="text" readOnly value={selectedRecord.noLote || selectedRecord.no_lote || selectedRecord.noProduccion || "-"} className="granel-input-control" style={{ fontWeight: 800, color: "#059669" }} />
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">Clave FQ</label>
                    <input type="text" readOnly value={selectedRecord.clave_fq || selectedRecord.noAnalisisFisicoquimico || selectedRecord.noAnalisis || "-"} className="granel-input-control" />
                  </div>
                </div>

                {/* FILA 2: ESPECIE | CATEGORÍA | CLASE */}
                <div className="granel-grid-row-3">
                  <div className="granel-input-group">
                    <label className="granel-input-label">Especie</label>
                    <input type="text" readOnly value={selectedRecord.especie || selectedRecord.tipoMaguey || "-"} className="granel-input-control" />
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">Categoría</label>
                    <input type="text" readOnly value={selectedRecord.categoria || "Mezcal Artesanal"} className="granel-input-control" />
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">Clase</label>
                    <input type="text" readOnly value={selectedRecord.clase || "Blanco Joven"} className="granel-input-control" />
                  </div>
                </div>

                {/* FILA 3: INGREDIENTE | LITROS | % ALC. VOLUMEN */}
                <div className="granel-grid-row-3">
                  <div className="granel-input-group">
                    <label className="granel-input-label">Ingrediente</label>
                    <input type="text" readOnly value={selectedRecord.ingrediente || selectedRecord.ingredientes || "---"} className="granel-input-control" />
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">Litros</label>
                    <input type="text" readOnly value={parseFloat(String(selectedRecord.litros ?? selectedRecord.invFinalVol ?? selectedRecord.entradaVolumen ?? 0).replace(/[^\d.]/g, '') || 0).toLocaleString()} className="granel-input-control has-suffix" style={{ fontWeight: 700 }} />
                    <span className="granel-input-suffix-badge">L</span>
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">% Alc. Volumen</label>
                    <input type="text" readOnly value={parseFloat(String(selectedRecord.porcentaje_alc_vol ?? selectedRecord.invFinalAlc ?? selectedRecord.entradaAlc ?? 0).replace(/[^\d.]/g, '') || 0)} className="granel-input-control has-suffix" style={{ fontWeight: 700 }} />
                    <span className="granel-input-suffix-badge">%</span>
                  </div>
                </div>

                {/* NO. DE BARRICAS / RECIPIENTES (DESDE MADURACIÓN) */}
                {(selectedRecord.no_barricas || selectedRecord.noBarricas) && (
                  <div style={{ marginTop: "12px", background: "rgba(217, 119, 6, 0.08)", border: "1px solid rgba(217, 119, 6, 0.25)", borderRadius: "8px", padding: "8px 14px", display: "flex", alignItems: "center", gap: "10px" }}>
                    <strong style={{ color: "#92400E", fontSize: "12.5px" }}>🛢️ Cantidad / Tipo de Recipiente:</strong>
                    <span style={{ color: "#78350F", fontWeight: 800, fontSize: "13px" }}>
                      {selectedRecord.no_barricas || selectedRecord.noBarricas}
                    </span>
                  </div>
                )}

                {/* INSPECTOR ASIGNADO A LA ZONA */}
                <div style={{ marginTop: "12px", background: "#F0FDFA", border: "1px solid #CCFBF1", borderRadius: "8px", padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span style={{ fontSize: "20px" }}>👮‍♂️</span>
                    <div>
                      <strong style={{ color: "#0F766E", fontSize: "12.5px", display: "block" }}>Inspector Asignado a la Zona:</strong>
                      <span style={{ color: "#115E59", fontWeight: 800, fontSize: "13.5px" }}>
                        {selectedRecord.inspector || "Pendiente de Asignación UI"}
                      </span>
                    </div>
                  </div>
                  <span style={{ background: "#CCFBF1", color: "#0F766E", padding: "3px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: 700 }}>
                    Asignación por Zona Oficial
                  </span>
                </div>
              </div>

              {/* SECCIÓN 2: INVENTARIOS, ENTRADAS Y SALIDAS (IMAGEN 2) */}
              <div className="granel-section-card accent-copper">
                <div className="granel-section-header">
                  <div className="granel-icon-wrapper-copper">
                    <ScienceIcon style={{ fontSize: 24 }} />
                  </div>
                  <div className="granel-section-header-titles">
                    <h3>Inventarios, Entradas y Salidas</h3>
                    <p>Saldos de Inicio, adición de agua de dilución o mezclas</p>
                  </div>
                </div>

                {/* 1. INVENTARIO INICIAL */}
                <div className="granel-subheading-copper">INVENTARIO INICIAL</div>
                <div className="granel-grid-row-2">
                  <div className="granel-input-group">
                    <label className="granel-input-label">VOLUMEN INICIAL (L)</label>
                    <input type="text" readOnly value={parseFloat(String(selectedRecord.invInicialVol || 0).replace(/[^\d.]/g, '') || 0).toLocaleString()} className="granel-input-control has-suffix" />
                    <span className="granel-input-suffix-badge">L</span>
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">% ALC. VOL. INICIAL</label>
                    <input type="text" readOnly value={parseFloat(String(selectedRecord.invInicialAlc || 0).replace(/[^\d.]/g, '') || 0)} className="granel-input-control has-suffix" />
                    <span className="granel-input-suffix-badge">%</span>
                  </div>
                </div>

                {/* 2. ENTRADAS */}
                <div className="granel-subheading-green">ENTRADAS</div>
                <div className="granel-grid-row-2">
                  <div className="granel-input-group">
                    <label className="granel-input-label">PROCEDENCIA DE ENTRADA</label>
                    <input type="text" readOnly value={selectedRecord.entradaProcedencia || `2da Destilación (Lote ${selectedRecord.noLote})`} className="granel-input-control" />
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">VOLUMEN ENTRADA (L)</label>
                    <input type="text" readOnly value={parseFloat(String(selectedRecord.entradaVolumen || selectedRecord.invInicialVol || 0).replace(/[^\d.]/g, '') || 0).toLocaleString()} className="granel-input-control has-suffix" />
                    <span className="granel-input-suffix-badge">L</span>
                  </div>
                </div>
                <div className="granel-grid-row-2">
                  <div className="granel-input-group">
                    <label className="granel-input-label">% ALC. VOL.</label>
                    <input type="text" readOnly value={parseFloat(String(selectedRecord.entradaAlc || selectedRecord.invInicialAlc || 0).replace(/[^\d.]/g, '') || 0)} className="granel-input-control has-suffix" />
                    <span className="granel-input-suffix-badge">%</span>
                  </div>
                  <div></div>
                </div>

                {/* 3. SALIDAS */}
                <div className="granel-subheading-dark">SALIDAS</div>
                <div className="granel-grid-row-1">
                  <div className="granel-input-group">
                    <label className="granel-input-label">DESTINO DE SALIDA (EJ. MADURACIÓN / ENVASADO)</label>
                    <input type="text" readOnly value={selectedRecord.salidaDestino || "-"} className="granel-input-control" />
                  </div>
                </div>
                <div className="granel-grid-row-2">
                  <div className="granel-input-group">
                    <label className="granel-input-label">VOLUMEN SALIDA (L)</label>
                    <input type="text" readOnly value={parseFloat(String(selectedRecord.salidaVolumen || 0).replace(/[^\d.]/g, '') || 0).toLocaleString()} className="granel-input-control has-suffix" />
                    <span className="granel-input-suffix-badge">L</span>
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">% ALC. VOL. SALIDA</label>
                    <input type="text" readOnly value={parseFloat(String(selectedRecord.salidaAlc || 0).replace(/[^\d.]/g, '') || 0)} className="granel-input-control has-suffix" />
                    <span className="granel-input-suffix-badge">%</span>
                  </div>
                </div>

                {/* 4. INVENTARIO FINAL */}
                <div className="granel-subheading-green">INVENTARIO FINAL</div>
                <div className="granel-grid-row-2">
                  <div className="granel-input-group">
                    <label className="granel-input-label">VOLUMEN FINAL (L)</label>
                    <input type="text" readOnly value={parseFloat(String(selectedRecord.invFinalVol || selectedRecord.entradaVolumen || 0).replace(/[^\d.]/g, '') || 0).toLocaleString()} className="granel-input-control has-suffix" style={{ fontWeight: 800, color: "#059669" }} />
                    <span className="granel-input-suffix-badge">L</span>
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">% ALC. VOL. FINAL</label>
                    <input type="text" readOnly value={parseFloat(String(selectedRecord.invFinalAlc || selectedRecord.entradaAlc || 0).replace(/[^\d.]/g, '') || 0)} className="granel-input-control has-suffix" style={{ fontWeight: 800, color: "#1D4ED8" }} />
                    <span className="granel-input-suffix-badge">%</span>
                  </div>
                </div>
              </div>

              {/* ALERTA DE DILUCIÓN PENDIENTE O APROBADA */}
              {(() => {
                const movs = Array.isArray(selectedRecord.movimientos) ? selectedRecord.movimientos : [];
                const obsStr = String(selectedRecord.observaciones || "");

                const approvedDil = movs.find(m => m.tipo_movimiento === "dilucion" && (m.es_aprobado || m.estado_ui === "Aprobado por UI"))
                  || (obsStr.includes("[DILUCIÓN APROBADA POR UI]") ? {
                    agua_agregada: 50,
                    grado_obtenido: selectedRecord.porcentaje_alc_vol || selectedRecord.invFinalAlc,
                    folio_documento: "LOT-001"
                  } : null);

                const pendingDil = !approvedDil ? (selectedRecord.dilucionPendiente || movs.find(m => m.tipo_movimiento === "dilucion" && (m.es_pendiente || (!m.es_aprobado && !m.es_rechazado && (String(m.motivo || "").toLowerCase().includes("pendiente") || String(m.observaciones || "").toLowerCase().includes("pendiente")))))) : null;

                const hasPending = Boolean(!approvedDil && (selectedRecord.tieneDilucionPendiente || pendingDil || (!obsStr.includes("[DILUCIÓN APROBADA POR UI]") && obsStr.includes("[SOLICITUD DILUCIÓN PENDIENTE UI]"))));
                const isApproved = Boolean(approvedDil || obsStr.includes("[DILUCIÓN APROBADA POR UI]"));

                if (!hasPending && !isApproved) return null;

                const volActual = parseFloat(String(selectedRecord.litros ?? selectedRecord.invFinalVol ?? 0).replace(/[^\d.]/g, "") || 0);
                const aguaL = (hasPending && pendingDil) ? parseFloat(pendingDil.agua_agregada || 0) : (approvedDil ? parseFloat(approvedDil.agua_agregada || 0) : 0);
                const gradoObj = (hasPending && pendingDil) ? parseFloat(pendingDil.grado_obtenido || pendingDil.porcentaje_alc_vol || 0) : parseFloat(String(selectedRecord.porcentaje_alc_vol ?? selectedRecord.invFinalAlc ?? 0).replace(/[^\d.]/g, "") || 0);
                const volEstimado = hasPending ? parseFloat((volActual + aguaL).toFixed(2)) : volActual;

                if (hasPending) {
                  return (
                    <div style={{ background: "linear-gradient(135deg, #F0FDFA 0%, #CCFBF1 100%)", border: "1.5px solid #5EEAD4", borderRadius: "12px", padding: "14px 18px", display: "flex", flexDirection: "column", gap: "8px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span style={{ fontSize: "20px" }}>💧</span>
                          <strong style={{ color: "#0F766E", fontSize: "14px" }}>Solicitud de Dilución de Grado Alcohólico en Revisión UI</strong>
                        </div>
                        <span style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D", borderRadius: "8px", padding: "3px 10px", fontSize: "11px", fontWeight: 800 }}>
                          ⏳ Pendiente de Dictamen UI
                        </span>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "10px", marginTop: "4px" }}>
                        <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #99F6E4" }}>
                          <span style={{ fontSize: "10.5px", color: "#64748B", fontWeight: 700, display: "block" }}>VOLUMEN ACTUAL:</span>
                          <strong style={{ fontSize: "14px", color: "#0F172A" }}>{volActual.toLocaleString()} L</strong>
                        </div>
                        <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #5EEAD4" }}>
                          <span style={{ fontSize: "10.5px", color: "#0F766E", fontWeight: 700, display: "block" }}>AGUA SOLICITADA:</span>
                          <strong style={{ fontSize: "14px", color: "#0D9488" }}>+{aguaL} L</strong>
                        </div>
                        <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #99F6E4" }}>
                          <span style={{ fontSize: "10.5px", color: "#2563EB", fontWeight: 700, display: "block" }}>VOLUMEN PROYECTADO:</span>
                          <strong style={{ fontSize: "14px", color: "#1D4ED8" }}>{volEstimado.toLocaleString()} L</strong>
                        </div>
                        <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #99F6E4" }}>
                          <span style={{ fontSize: "10.5px", color: "#059669", fontWeight: 700, display: "block" }}>GRADO PROYECTADO:</span>
                          <strong style={{ fontSize: "14px", color: "#059669" }}>{gradoObj}% Alc.</strong>
                        </div>
                      </div>
                      <small style={{ color: "#0F766E", fontSize: "11.5px", fontWeight: 500 }}>
                        ℹ️ El volumen oficial del lote permanecerá en {volActual} L hasta que el Inspector de la Unidad de Inspección valide y emita su aprobación.
                      </small>
                    </div>
                  );
                }

                return (
                  <div style={{ background: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)", border: "1.5px solid #10B981", borderRadius: "12px", padding: "14px 18px", display: "flex", flexDirection: "column", gap: "8px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "20px" }}>💧</span>
                        <strong style={{ color: "#065F46", fontSize: "14px" }}>Dilución de Grado Alcohólico Aprobada y Aplicada</strong>
                      </div>
                      <span style={{ background: "#10B981", color: "white", borderRadius: "8px", padding: "3px 10px", fontSize: "11px", fontWeight: 800 }}>
                        ✅ Dictaminada y Aplicada por UI
                      </span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "10px", marginTop: "4px" }}>
                      <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #A7F3D0" }}>
                        <span style={{ fontSize: "10.5px", color: "#64748B", fontWeight: 700, display: "block" }}>VOLUMEN OFICIAL:</span>
                        <strong style={{ fontSize: "14px", color: "#0F172A" }}>{volActual.toLocaleString()} L</strong>
                      </div>
                      <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #10B981" }}>
                        <span style={{ fontSize: "10.5px", color: "#047857", fontWeight: 700, display: "block" }}>AGUA INCORPORADA:</span>
                        <strong style={{ fontSize: "14px", color: "#059669" }}>+{aguaL} L</strong>
                      </div>
                      <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #A7F3D0" }}>
                        <span style={{ fontSize: "10.5px", color: "#047857", fontWeight: 700, display: "block" }}>GRADO OFICIAL:</span>
                        <strong style={{ fontSize: "14px", color: "#047857" }}>{gradoObj}% Alc.</strong>
                      </div>
                      <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #A7F3D0" }}>
                        <span style={{ fontSize: "10.5px", color: "#2563EB", fontWeight: 700, display: "block" }}>ESTATUS FQ:</span>
                        <strong style={{ fontSize: "13px", color: "#1D4ED8" }}>Requiere Nuevo Análisis</strong>
                      </div>
                    </div>
                    <small style={{ color: "#065F46", fontSize: "11.5px", fontWeight: 500 }}>
                      ℹ️ Dilución dictaminada favorablemente por la Unidad de Inspección. El volumen oficial ({volActual} L) ya contempla el agua incorporada conforme a la NOM-070-SCFI-2016.
                    </small>
                  </div>
                );
              })()}

              {/* ALERTA DE BAJA PENDIENTE O APROBADA */}
              {(() => {
                const movs = Array.isArray(selectedRecord.movimientos) ? selectedRecord.movimientos : [];
                const obsStr = String(selectedRecord.observaciones || "");

                const approvedBaja = movs.find(m => (m.tipo_movimiento === "baja" || m.tipo_movimiento === "salida") && (m.es_aprobado || m.estado_ui === "Aprobado por UI"))
                  || (obsStr.includes("[BAJA APROBADA POR UI]") ? { litros_transferidos: 0, motivo: "Merma / Salida de producto" } : null);

                const pendingBaja = !approvedBaja ? (selectedRecord.bajaPendiente || movs.find(m => (m.tipo_movimiento === "baja" || m.tipo_movimiento === "salida") && (m.es_pendiente || (!m.es_aprobado && !m.es_rechazado && (String(m.motivo || "").toLowerCase().includes("pendiente") || String(m.observaciones || "").toLowerCase().includes("pendiente")))))) : null;

                const hasPendingBaja = Boolean(!approvedBaja && (selectedRecord.tieneBajaPendiente || pendingBaja || (!obsStr.includes("[BAJA APROBADA POR UI]") && obsStr.includes("[SOLICITUD BAJA PENDIENTE UI]"))));
                const isApprovedBaja = Boolean(approvedBaja || obsStr.includes("[BAJA APROBADA POR UI]"));

                if (!hasPendingBaja && !isApprovedBaja) return null;

                const volB = (hasPendingBaja && pendingBaja) ? parseFloat(pendingBaja.litros_transferidos || 0) : (approvedBaja ? parseFloat(approvedBaja.litros_transferidos || 0) : 0);
                const volActual = parseFloat(String(selectedRecord.litros ?? selectedRecord.invFinalVol ?? 0).replace(/[^\d.]/g, "") || 0);
                const volRestante = hasPendingBaja ? Math.max(0, parseFloat((volActual - volB).toFixed(2))) : volActual;
                const motivoBaja = (pendingBaja || approvedBaja)?.motivo || "Merma / Salida de producto";

                if (hasPendingBaja) {
                  return (
                    <div style={{ background: "linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)", border: "1.5px solid #FDBA74", borderRadius: "12px", padding: "14px 18px", display: "flex", flexDirection: "column", gap: "8px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span style={{ fontSize: "20px" }}>📦</span>
                          <strong style={{ color: "#C2410C", fontSize: "14px" }}>Solicitud de Baja / Egreso de Inventario en Revisión UI</strong>
                        </div>
                        <span style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D", borderRadius: "8px", padding: "3px 10px", fontSize: "11px", fontWeight: 800 }}>
                          ⏳ Pendiente de Dictamen UI
                        </span>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "10px", marginTop: "4px" }}>
                        <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #FED7AA" }}>
                          <span style={{ fontSize: "10.5px", color: "#64748B", fontWeight: 700, display: "block" }}>SALDO ACTUAL:</span>
                          <strong style={{ fontSize: "14px", color: "#0F172A" }}>{volActual.toLocaleString()} L</strong>
                        </div>
                        <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #FB923C" }}>
                          <span style={{ fontSize: "10.5px", color: "#C2410C", fontWeight: 700, display: "block" }}>VOLUMEN A DESCONTAR:</span>
                          <strong style={{ fontSize: "14px", color: "#DC2626" }}>-{volB} L</strong>
                        </div>
                        <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #FED7AA" }}>
                          <span style={{ fontSize: "10.5px", color: "#059669", fontWeight: 700, display: "block" }}>SALDO PROYECTADO:</span>
                          <strong style={{ fontSize: "14px", color: "#059669" }}>{volRestante.toLocaleString()} L</strong>
                        </div>
                      </div>
                      <small style={{ color: "#9A3412", fontSize: "11.5px", fontWeight: 500 }}>
                        ℹ️ <strong>Motivo:</strong> {motivoBaja}. El volumen oficial permanecerá en {volActual} L hasta que el Inspector de la Unidad de Inspección valide y emita su dictamen favorable.
                      </small>
                    </div>
                  );
                }

                return (
                  <div style={{ background: "linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)", border: "1.5px solid #F97316", borderRadius: "12px", padding: "14px 18px", display: "flex", flexDirection: "column", gap: "8px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "20px" }}>📦</span>
                        <strong style={{ color: "#9A3412", fontSize: "14px" }}>Baja / Egreso Dictaminado y Descontado</strong>
                      </div>
                      <span style={{ background: "#EA580C", color: "white", borderRadius: "8px", padding: "3px 10px", fontSize: "11px", fontWeight: 800 }}>
                        ✅ Dictaminada y Descontada por UI
                      </span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "10px", marginTop: "4px" }}>
                      <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #FED7AA" }}>
                        <span style={{ fontSize: "10.5px", color: "#64748B", fontWeight: 700, display: "block" }}>SALDO OFICIAL:</span>
                        <strong style={{ fontSize: "14px", color: "#0F172A" }}>{volActual.toLocaleString()} L</strong>
                      </div>
                      <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #FB923C" }}>
                        <span style={{ fontSize: "10.5px", color: "#C2410C", fontWeight: 700, display: "block" }}>VOLUMEN DESCONTADO:</span>
                        <strong style={{ fontSize: "14px", color: "#DC2626" }}>-{volB} L</strong>
                      </div>
                      <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #FED7AA" }}>
                        <span style={{ fontSize: "10.5px", color: "#047857", fontWeight: 700, display: "block" }}>MOTIVO:</span>
                        <strong style={{ fontSize: "13px", color: "#047857" }}>{motivoBaja}</strong>
                      </div>
                    </div>
                    <small style={{ color: "#9A3412", fontSize: "11.5px", fontWeight: 500 }}>
                      ℹ️ Salida validada y descontada del saldo oficial por la Unidad de Inspección.
                    </small>
                  </div>
                );
              })()}

              {/* ALERTA DE MUESTREO PENDIENTE O APROBADA */}
              {(() => {
                const movs = Array.isArray(selectedRecord.movimientos) ? selectedRecord.movimientos : [];
                const obsStr = String(selectedRecord.observaciones || "");

                const approvedMues = movs.find(m => m.tipo_movimiento === "muestreo" && (m.es_aprobado || m.estado_ui === "Aprobado por UI"))
                  || (obsStr.includes("[MUESTREO APROBADO POR UI]") ? { litros_transferidos: 0, motivo: "Muestreo Oficial NOM-070" } : null);

                const pendingMues = !approvedMues ? (selectedRecord.muestreoPendiente || movs.find(m => m.tipo_movimiento === "muestreo" && (m.es_pendiente || (!m.es_aprobado && !m.es_rechazado && (String(m.motivo || "").toLowerCase().includes("pendiente") || String(m.observaciones || "").toLowerCase().includes("pendiente")))))) : null;

                const hasPendingMues = Boolean(!approvedMues && (selectedRecord.tieneMuestreoPendiente || pendingMues || (!obsStr.includes("[MUESTREO APROBADO POR UI]") && obsStr.includes("[SOLICITUD MUESTREO PENDIENTE UI]"))));
                const isApprovedMues = Boolean(approvedMues || obsStr.includes("[MUESTREO APROBADO POR UI]"));

                if (!hasPendingMues && !isApprovedMues) return null;

                const volM = (hasPendingMues && pendingMues) ? parseFloat(pendingMues.litros_transferidos || 0) : (approvedMues ? parseFloat(approvedMues.litros_transferidos || 0) : 0);
                const volActual = parseFloat(String(selectedRecord.litros ?? selectedRecord.invFinalVol ?? 0).replace(/[^\d.]/g, "") || 0);
                const volRestante = hasPendingMues ? Math.max(0, parseFloat((volActual - volM).toFixed(2))) : volActual;
                const tipoMuestreo = (pendingMues || approvedMues)?.motivo || "Muestreo Oficial NOM-070";

                if (hasPendingMues) {
                  return (
                    <div style={{ background: "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)", border: "1.5px solid #A5B4FC", borderRadius: "12px", padding: "14px 18px", display: "flex", flexDirection: "column", gap: "8px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span style={{ fontSize: "20px" }}>🧪</span>
                          <strong style={{ color: "#3730A3", fontSize: "14px" }}>Solicitud de Extracción de Muestra en Revisión UI</strong>
                        </div>
                        <span style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D", borderRadius: "8px", padding: "3px 10px", fontSize: "11px", fontWeight: 800 }}>
                          ⏳ Pendiente de Dictamen UI
                        </span>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "10px", marginTop: "4px" }}>
                        <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #C7D2FE" }}>
                          <span style={{ fontSize: "10.5px", color: "#64748B", fontWeight: 700, display: "block" }}>SALDO ACTUAL:</span>
                          <strong style={{ fontSize: "14px", color: "#0F172A" }}>{volActual.toLocaleString()} L</strong>
                        </div>
                        <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #818CF8" }}>
                          <span style={{ fontSize: "10.5px", color: "#4F46E5", fontWeight: 700, display: "block" }}>VOLUMEN DE MUESTRA:</span>
                          <strong style={{ fontSize: "14px", color: "#4F46E5" }}>-{volM} L</strong>
                        </div>
                        <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #C7D2FE" }}>
                          <span style={{ fontSize: "10.5px", color: "#059669", fontWeight: 700, display: "block" }}>SALDO PROYECTADO:</span>
                          <strong style={{ fontSize: "14px", color: "#059669" }}>{volRestante.toLocaleString()} L</strong>
                        </div>
                      </div>
                      <small style={{ color: "#3730A3", fontSize: "11.5px", fontWeight: 500 }}>
                        ℹ️ <strong>Destino:</strong> {tipoMuestreo}. El volumen oficial permanecerá en {volActual} L hasta que el Inspector de la Unidad de Inspección valide y emita su dictamen favorable.
                      </small>
                    </div>
                  );
                }

                return (
                  <div style={{ background: "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)", border: "1.5px solid #6366F1", borderRadius: "12px", padding: "14px 18px", display: "flex", flexDirection: "column", gap: "8px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "20px" }}>🧪</span>
                        <strong style={{ color: "#312E81", fontSize: "14px" }}>Extracción de Muestra Dictaminada y Descontada</strong>
                      </div>
                      <span style={{ background: "#4F46E5", color: "white", borderRadius: "8px", padding: "3px 10px", fontSize: "11px", fontWeight: 800 }}>
                        ✅ Dictaminada y Descontada por UI
                      </span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "10px", marginTop: "4px" }}>
                      <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #C7D2FE" }}>
                        <span style={{ fontSize: "10.5px", color: "#64748B", fontWeight: 700, display: "block" }}>SALDO OFICIAL:</span>
                        <strong style={{ fontSize: "14px", color: "#0F172A" }}>{volActual.toLocaleString()} L</strong>
                      </div>
                      <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #818CF8" }}>
                        <span style={{ fontSize: "10.5px", color: "#4F46E5", fontWeight: 700, display: "block" }}>VOLUMEN EXTRAÍDO:</span>
                        <strong style={{ fontSize: "14px", color: "#4F46E5" }}>-{volM} L</strong>
                      </div>
                      <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #C7D2FE" }}>
                        <span style={{ fontSize: "10.5px", color: "#047857", fontWeight: 700, display: "block" }}>DESTINO:</span>
                        <strong style={{ fontSize: "13px", color: "#047857" }}>{tipoMuestreo}</strong>
                      </div>
                    </div>
                    <small style={{ color: "#312E81", fontSize: "11.5px", fontWeight: 500 }}>
                      ℹ️ Muestra oficial extraída y descontada del balance oficial por la Unidad de Inspección.
                    </small>
                  </div>
                );
              })()}

              {/* ALERTA DE TRASLADO PENDIENTE O APROBADO */}
              {(() => {
                const movs = Array.isArray(selectedRecord.movimientos) ? selectedRecord.movimientos : [];
                const obsStr = String(selectedRecord.observaciones || "");

                const approvedTras = movs.find(m => m.tipo_movimiento === "traslado" && (m.es_aprobado || m.estado_ui === "Aprobado por UI"))
                  || (obsStr.includes("[TRASLADO APROBADO POR UI]") ? { litros_transferidos: 0, instalacion_destino: "Instalación Destino" } : null);

                const pendingTras = !approvedTras ? (selectedRecord.trasladoPendiente || movs.find(m => m.tipo_movimiento === "traslado" && (m.es_pendiente || (!m.es_aprobado && !m.es_rechazado && (String(m.motivo || "").toLowerCase().includes("pendiente") || String(m.observaciones || "").toLowerCase().includes("pendiente")))))) : null;

                const hasPendingTras = Boolean(!approvedTras && (selectedRecord.tieneTrasladoPendiente || pendingTras || (!obsStr.includes("[TRASLADO APROBADO POR UI]") && obsStr.includes("[SOLICITUD TRASLADO PENDIENTE UI]"))));
                const isApprovedTras = Boolean(approvedTras || obsStr.includes("[TRASLADO APROBADO POR UI]"));

                if (!hasPendingTras && !isApprovedTras) return null;

                const volT = (hasPendingTras && pendingTras) ? parseFloat(pendingTras.litros_transferidos || 0) : (approvedTras ? parseFloat(approvedTras.litros_transferidos || 0) : 0);
                const volActual = parseFloat(String(selectedRecord.litros ?? selectedRecord.invFinalVol ?? 0).replace(/[^\d.]/g, "") || 0);
                const volRestante = hasPendingTras ? Math.max(0, parseFloat((volActual - volT).toFixed(2))) : volActual;
                const instDest = (pendingTras || approvedTras)?.instalacion_destino || "Instalación Destino";

                if (hasPendingTras) {
                  return (
                    <div style={{ background: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)", border: "1.5px solid #93C5FD", borderRadius: "12px", padding: "14px 18px", display: "flex", flexDirection: "column", gap: "8px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span style={{ fontSize: "20px" }}>🚛</span>
                          <strong style={{ color: "#1E40AF", fontSize: "14px" }}>Solicitud de Traslado entre Instalaciones en Revisión UI</strong>
                        </div>
                        <span style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D", borderRadius: "8px", padding: "3px 10px", fontSize: "11px", fontWeight: 800 }}>
                          ⏳ Pendiente de Dictamen UI
                        </span>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "10px", marginTop: "4px" }}>
                        <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #BFDBFE" }}>
                          <span style={{ fontSize: "10.5px", color: "#64748B", fontWeight: 700, display: "block" }}>SALDO ACTUAL:</span>
                          <strong style={{ fontSize: "14px", color: "#0F172A" }}>{volActual.toLocaleString()} L</strong>
                        </div>
                        <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #60A5FA" }}>
                          <span style={{ fontSize: "10.5px", color: "#1D4ED8", fontWeight: 700, display: "block" }}>VOLUMEN A TRASLADAR:</span>
                          <strong style={{ fontSize: "14px", color: "#1D4ED8" }}>-{volT} L</strong>
                        </div>
                        <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #BFDBFE" }}>
                          <span style={{ fontSize: "10.5px", color: "#059669", fontWeight: 700, display: "block" }}>SALDO PROYECTADO:</span>
                          <strong style={{ fontSize: "14px", color: "#059669" }}>{volRestante.toLocaleString()} L</strong>
                        </div>
                      </div>
                      <small style={{ color: "#1E40AF", fontSize: "11.5px", fontWeight: 500 }}>
                        ℹ️ <strong>Destino:</strong> {instDest}. El volumen oficial permanecerá en {volActual} L hasta que el Inspector de la Unidad de Inspección valide y emita su dictamen favorable, momento en el cual se creará el nuevo registro a granel en la instalación receptora.
                      </small>
                    </div>
                  );
                }

                return (
                  <div style={{ background: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)", border: "1.5px solid #3B82F6", borderRadius: "12px", padding: "14px 18px", display: "flex", flexDirection: "column", gap: "8px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "20px" }}>🚛</span>
                        <strong style={{ color: "#1E3A8A", fontSize: "14px" }}>Traslado entre Instalaciones Dictaminado y Completado</strong>
                      </div>
                      <span style={{ background: "#2563EB", color: "white", borderRadius: "8px", padding: "3px 10px", fontSize: "11px", fontWeight: 800 }}>
                        ✅ Dictaminado y Transferido por UI
                      </span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "10px", marginTop: "4px" }}>
                      <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #BFDBFE" }}>
                        <span style={{ fontSize: "10.5px", color: "#64748B", fontWeight: 700, display: "block" }}>SALDO OFICIAL EN ORIGEN:</span>
                        <strong style={{ fontSize: "14px", color: "#0F172A" }}>{volActual.toLocaleString()} L</strong>
                      </div>
                      <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #60A5FA" }}>
                        <span style={{ fontSize: "10.5px", color: "#1D4ED8", fontWeight: 700, display: "block" }}>VOLUMEN TRANSFERIDO:</span>
                        <strong style={{ fontSize: "14px", color: "#1D4ED8" }}>-{volT} L</strong>
                      </div>
                      <div style={{ background: "white", padding: "8px 12px", borderRadius: "8px", border: "1px solid #BFDBFE" }}>
                        <span style={{ fontSize: "10.5px", color: "#047857", fontWeight: 700, display: "block" }}>INSTALACIÓN DESTINO:</span>
                        <strong style={{ fontSize: "13px", color: "#047857" }}>{instDest}</strong>
                      </div>
                    </div>
                    <small style={{ color: "#1E3A8A", fontSize: "11.5px", fontWeight: 500 }}>
                      ℹ️ Traslado validado por la Unidad de Inspección. Se creó exitosamente el nuevo registro a granel en la instalación receptora.
                    </small>
                  </div>
                );
              })()}

              {/* SECCIÓN 3: HISTORIAL DE MOVIMIENTOS Y TRAZABILIDAD DEL LOTE */}
              <div className="granel-section-card" style={{ borderLeft: "4px solid #0D9488", padding: "16px", background: "#FFFFFF", borderRadius: "12px", border: "1px solid #E2E8F0" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px", paddingBottom: "8px", borderBottom: "1px solid #E2E8F0" }}>
                  <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "#CCFBF1", display: "flex", alignItems: "center", justifyContent: "center", color: "#0F766E" }}>
                    <HistoryIcon style={{ fontSize: 20 }} />
                  </div>
                  <div>
                    <h4 style={{ margin: 0, fontSize: "14.5px", fontWeight: 800, color: "#0F172A" }}>
                      Trazabilidad e Historial de Movimientos del Lote
                    </h4>
                    <p style={{ margin: 0, fontSize: "11.5px", color: "#64748B" }}>
                      Registro de diluciones con agua, mezclas, trasvases y dictámenes UI
                    </p>
                  </div>
                </div>

                {(!selectedRecord.movimientos || selectedRecord.movimientos.length === 0) ? (
                  <div style={{ textAlign: "center", padding: "20px", background: "#F8FAFC", borderRadius: "8px", color: "#64748B", fontSize: "12.5px" }}>
                    No se registran movimientos ni diluciones adicionales para este lote.
                  </div>
                ) : (
                  <div className="table-responsive-container" style={{ margin: 0, boxShadow: "none" }}>
                    <table className="custom-table-bitacora" style={{ fontSize: "12px" }}>
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Tipo</th>
                          <th>Litros / Agua</th>
                          <th>Grado (% Alc.)</th>
                          <th>Folio / Lote Agua</th>
                          <th>Estado UI</th>
                          <th>Observaciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedRecord.movimientos.map((m, mIdx) => (
                          <tr key={m.id_movimiento || mIdx} style={m.es_pendiente ? { background: "#FEFCE8" } : {}}>
                            <td style={{ fontWeight: 600 }}>{m.fecha || "-"}</td>
                            <td>
                              <span style={{
                                padding: "3px 8px",
                                borderRadius: "6px",
                                fontSize: "11px",
                                fontWeight: 700,
                                background: m.tipo_movimiento === "dilucion" ? "#CCFBF1" : (m.tipo_movimiento === "mezcla" ? "#EDE9FE" : (m.tipo_movimiento === "muestreo" ? "#EEF2FF" : (m.tipo_movimiento === "traslado" ? "#EFF6FF" : "#F1F5F9"))),
                                color: m.tipo_movimiento === "dilucion" ? "#0F766E" : (m.tipo_movimiento === "mezcla" ? "#6B21A8" : (m.tipo_movimiento === "muestreo" ? "#3730A3" : (m.tipo_movimiento === "traslado" ? "#1D4ED8" : "#475569")))
                              }}>
                                {m.tipo_movimiento === "dilucion" ? "💧 Dilución de Grado" : (m.tipo_movimiento === "mezcla" ? "🔄 Fusión / Mezcla" : (m.tipo_movimiento === "muestreo" ? "🧪 Muestreo Oficial" : (m.tipo_movimiento === "traslado" ? "🚛 Traslado Instalación" : (m.tipo_movimiento === "salida" || m.tipo_movimiento === "baja" ? "📦 Salida / Baja" : (m.tipo_movimiento || "Movimiento")))))}
                              </span>
                            </td>
                            <td>
                              {m.tipo_movimiento === "dilucion" && m.agua_agregada > 0 ? (
                                <strong style={{ color: "#0F766E" }}>+{m.agua_agregada} L agua</strong>
                              ) : m.tipo_movimiento === "muestreo" ? (
                                <strong style={{ color: "#4F46E5" }}>-{m.litros_transferidos || 0} L muestra</strong>
                              ) : (m.tipo_movimiento === "salida" || m.tipo_movimiento === "baja") ? (
                                <strong style={{ color: "#DC2626" }}>-{m.litros_transferidos || 0} L baja</strong>
                              ) : m.tipo_movimiento === "traslado" ? (
                                <strong style={{ color: "#1D4ED8" }}>-{m.litros_transferidos || 0} L traslado</strong>
                              ) : (
                                <span>{m.litros_transferidos || 0} L</span>
                              )}
                            </td>
                            <td>
                              {m.grado_obtenido > 0 ? `${m.grado_obtenido}%` : (m.porcentaje_alc_vol ? `${m.porcentaje_alc_vol}%` : "-")}
                            </td>
                            <td style={{ fontFamily: "monospace", fontSize: "11px" }}>
                              {m.folio_documento || "-"}
                            </td>
                            <td>
                              <span style={{
                                padding: "2px 8px",
                                borderRadius: "6px",
                                fontSize: "10.5px",
                                fontWeight: 700,
                                background: m.es_pendiente ? "#FEF3C7" : (m.es_aprobado ? "#DCFCE7" : (m.es_rechazado ? "#FEE2E2" : "#F1F5F9")),
                                color: m.es_pendiente ? "#92400E" : (m.es_aprobado ? "#166534" : (m.es_rechazado ? "#991B1B" : "#475569")),
                                border: `1px solid ${m.es_pendiente ? "#FCD34D" : (m.es_aprobado ? "#86EFAC" : (m.es_rechazado ? "#FCA5A5" : "#E2E8F0"))}`
                              }}>
                                {m.es_pendiente ? "⏳ Pendiente UI" : (m.es_aprobado ? "✅ Aprobado y Aplicado" : (m.es_rechazado ? "❌ Rechazado" : (m.estado_ui || "Registrado")))}
                              </span>
                            </td>
                            <td style={{ color: "#64748B", maxWidth: "260px" }}>
                              {m.observaciones || m.motivo || "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* OBSERVACIONES */}
              {selectedRecord.observaciones && (
                <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "12px", padding: "14px 18px" }}>
                  <strong style={{ fontSize: "11px", color: "#64748B", textTransform: "uppercase", display: "block", marginBottom: "4px" }}>
                    Observaciones Generales de Almacén:
                  </strong>
                  <p style={{ margin: 0, fontSize: "13.5px", color: "#334155", fontWeight: 500 }}>
                    {selectedRecord.observaciones}
                  </p>
                </div>
              )}

              {/* Botones del Modal de Detalle */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "12px", borderTop: "1px solid #E2E8F0" }}>
                {String(selectedRecord?.status || selectedRecord?.statusUI || '').toLowerCase().includes('borrador') && handleUndoGranel ? (
                  <button
                    type="button"
                    onClick={() => {
                      const rec = selectedRecord;
                      setSelectedRecord(null);
                      handleUndoGranel(rec);
                    }}
                    className="btn-action-icon delete"
                    style={{ width: "auto", padding: "8px 16px", borderRadius: "10px", display: "inline-flex", gap: "6px", alignItems: "center", fontSize: "12.5px", fontWeight: 700 }}
                    title="Deshacer movimiento en borrador y devolver volumen al lote de origen"
                  >
                    <UndoIcon style={{ fontSize: 16 }} />
                    <span>Deshacer Movimiento (Borrador)</span>
                  </button>
                ) : <div />}

                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  {(() => {
                    const rawSt = String(selectedRecord?.status || selectedRecord?.statusUI || selectedRecord?.estatus || '').toLowerCase();
                    const isAprobado = rawSt.includes('aprobad') || rawSt.includes('aceptad') || rawSt === 'activo';

                    if (isAprobado) {
                      return (
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            background: "#ECFDF5",
                            color: "#065F46",
                            border: "1px solid " + (rawSt.includes('aprobad') ? "#A7F3D0" : "#A7F3D0"),
                            padding: "6px 14px",
                            borderRadius: "8px",
                            fontSize: "12px",
                            fontWeight: 700
                          }}
                          title="Este lote ya fue validado y aprobado por la Unidad de Inspección, por lo que está bloqueado para edición"
                        >
                          <CheckCircleIcon style={{ fontSize: 16, color: "#10B981" }} />
                          <span>Validado por UI (Solo Lectura)</span>
                        </div>
                      );
                    }

                    if (handleEditRecord) {
                      return (
                        <button
                          type="button"
                          className="btn-secondary-custom"
                          onClick={() => {
                            const rec = selectedRecord;
                            setSelectedRecord(null);
                            handleEditRecord(rec);
                          }}
                          style={{ padding: "8px 18px", display: "inline-flex", gap: "6px", alignItems: "center", background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D", fontWeight: 700 }}
                        >
                          <EditIcon style={{ fontSize: 16 }} />
                          <span>Editar Registro</span>
                        </button>
                      );
                    }
                    return null;
                  })()}
                  <button type="button" className="btn-secondary-custom" onClick={() => setSelectedRecord(null)} style={{ padding: "8px 22px" }}>
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* =========================================================================
          5. MODAL FUSIÓN / MEZCLA / EXTRACCIÓN PARCIAL DE GRANELES
      ========================================================================= */}
      {isFusionModalOpen && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setIsFusionModalOpen(false)}>
          <div
            className="detail-modal-card animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "850px" }}
          >
            <div className="detail-modal-header" style={{ background: "linear-gradient(135deg, #6B21A8 0%, #9333EA 100%)", color: "white", padding: "16px 22px" }}>
              <div className="detail-modal-title-box">
                <span className="badge-modal-tag" style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>
                  FUSIÓN / MEZCLA DE GRANELES
                </span>
                <h1 className="detail-modal-title" style={{ color: "white", fontSize: "19px" }}>
                  Consolidación & Extracción de Lotes a Granel
                </h1>
              </div>
              <button
                type="button"
                onClick={() => setIsFusionModalOpen(false)}
                className="detail-modal-close-btn"
              >
                <CloseIcon />
              </button>
            </div>

            <form onSubmit={(e) => handleExecuteFusion(e, "revision")} style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: "16px", maxHeight: "80vh", overflowY: "auto" }}>
              <p style={{ margin: 0, fontSize: "13px", color: "#64748B" }}>
                Seleccione 1 lote para realizar una extracción parcial, o 2 o más lotes para una mezcla o consolidación. Conforme a la <strong>NOM-070</strong>, las mezclas solo son permitidas entre lotes de la <strong>misma categoría y clase</strong>. El orden de magueyes se ordenará de mayor a menor proporción y el análisis FQ anterior quedará invalidado (en blanco).
              </p>

              {/* SELECCIÓN DE LOTES ORIGEN */}
              <div style={{ background: "#FAF5FF", border: "1px solid #E9D5FF", borderRadius: "12px", padding: "16px" }}>
                <h4 style={{ margin: "0 0 12px 0", fontSize: "12px", fontWeight: 800, color: "#7C3AED", textTransform: "uppercase" }}>
                  1. Lotes de Origen & Volúmenes a Extraer ({fusionSources.length} {fusionSources.length === 1 ? 'lote origen' : 'lotes origen'})
                </h4>

                {fusionSources.map((src, idx) => {
                  const selectedG = (allAvailableGraneles || []).find(g => (
                    String(g.id_bitacora_granel) === String(src.granelId) ||
                    String(g.id) === String(src.granelId) ||
                    String(g.dbId) === String(src.granelId) ||
                    String(g.noLote || g.no_lote) === String(src.granelId) ||
                    String(g.idContenedor || g.no_tanque) === String(src.granelId) ||
                    String(g._optVal) === String(src.granelId)
                  ));
                  const saldoDisp = selectedG ? (selectedG._vDisp ?? (parseFloat(String(selectedG.invFinalVol ?? selectedG.saldoFinal ?? selectedG.volFinal ?? selectedG.volumen ?? selectedG.litros ?? selectedG.litrosTotal ?? selectedG.entradaVolumen ?? 0).replace(/[^\d.]/g, '')) || 0)) : 0;

                  const dispOptions = (allAvailableGraneles || []).filter(g => g._vDisp > 0 || String(g._optVal) === String(src.granelId));

                  return (
                    <div key={idx} style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "10px" }}>
                      <div style={{ flex: 2 }}>
                        <select
                          value={src.granelId}
                          onChange={e => handleFusionSourceChange(idx, "granelId", e.target.value)}
                          className="granel-input-control"
                          style={{ height: "40px" }}
                          required
                        >
                          <option value="">-- Granel Origen #{idx + 1} ({dispOptions.length} Disponibles) --</option>
                          {dispOptions.map((g, gIdx) => (
                            <option key={g._optVal || gIdx} value={g._optVal}>
                              {g._label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div style={{ flex: 1, position: "relative" }}>
                        <input
                          type="number"
                          step="any"
                          min="0.1"
                          max={saldoDisp > 0 ? saldoDisp : undefined}
                          placeholder={`Vol. a Extraer (Máx ${saldoDisp} L)`}
                          value={src.volExtraer}
                          onChange={e => handleFusionSourceChange(idx, "volExtraer", e.target.value)}
                          className="granel-input-control has-suffix"
                          style={{ height: "40px" }}
                          required
                        />
                        <span className="granel-input-suffix-badge" style={{ bottom: "6px" }}>L</span>
                      </div>

                      {fusionSources.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveFusionSource(idx)}
                          title="Eliminar lote origen"
                          style={{ background: "#FEE2E2", color: "#DC2626", border: "none", borderRadius: "10px", width: "40px", height: "40px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                        >
                          <DeleteIcon style={{ fontSize: 18 }} />
                        </button>
                      )}
                    </div>
                  );
                })}

                <button
                  type="button"
                  onClick={handleAddFusionSource}
                  style={{ background: "#F3E8FF", color: "#7C3AED", border: "1px dashed #C084FC", borderRadius: "10px", padding: "8px 16px", cursor: "pointer", fontSize: "12.5px", fontWeight: 700, width: "fit-content", display: "inline-flex", alignItems: "center", gap: 6, marginTop: "4px" }}
                >
                  <AddIcon style={{ fontSize: 16 }} />
                  <span>+ Agregar Otro Lote (Mezcla)</span>
                </button>

                {/* Resumen en vivo de la mezcla */}
                <div style={{ marginTop: "14px", background: "#FFFFFF", border: "1px solid #DDD6FE", borderRadius: "10px", padding: "12px 16px", display: "flex", flexDirection: "column", gap: "8px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
                    <div>
                      <span style={{ fontSize: "11px", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>Volumen Consolidado:</span>
                      <strong style={{ fontSize: "15px", color: "#7C3AED", marginLeft: "8px" }}>
                        {fusionCalculations.totalVolMezcla.toLocaleString()} L
                      </strong>
                    </div>
                    <div>
                      <span style={{ fontSize: "11px", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>° Alc. Ponderado:</span>
                      <strong style={{ fontSize: "15px", color: "#1D4ED8", marginLeft: "8px" }}>
                        {fusionCalculations.alcPonderadoMezcla}% Alc.
                      </strong>
                    </div>
                  </div>

                  {fusionCalculations.magueyesOrdenados && (
                    <div style={{ fontSize: "12px", color: "#475569", borderTop: "1px dashed #E2E8F0", paddingTop: "6px" }}>
                      <strong>Magueyes por proporción:</strong> <span style={{ color: "#0F172A" }}>{fusionCalculations.magueyesOrdenados}</span>
                    </div>
                  )}
                </div>

                {/* Banners de Incompatibilidad NOM-070 */}
                {fusionCalculations.tieneConflictoCategoria && (
                  <div style={{ marginTop: "12px", background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: "10px", padding: "10px 14px", color: "#991B1B", fontSize: "12.5px", display: "flex", alignItems: "center", gap: "10px" }}>
                    <span style={{ fontSize: "18px" }}>⚠️</span>
                    <div>
                      <strong>Incompatibilidad NOM-070 (Categoría):</strong> No se pueden mezclar lotes de diferentes categorías (<i>{fusionCalculations.categorias.join(", ")}</i>). Todos los lotes deben pertenecer a la misma categoría.
                    </div>
                  </div>
                )}

                {fusionCalculations.tieneConflictoClase && (
                  <div style={{ marginTop: "12px", background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: "10px", padding: "10px 14px", color: "#991B1B", fontSize: "12.5px", display: "flex", alignItems: "center", gap: "10px" }}>
                    <span style={{ fontSize: "18px" }}>⚠️</span>
                    <div>
                      <strong>Incompatibilidad NOM-070 (Clase):</strong> No se pueden mezclar lotes de diferentes clases (<i>{fusionCalculations.clases.join(", ")}</i>). Todos los lotes deben pertenecer a la misma clase.
                    </div>
                  </div>
                )}
              </div>

              {/* DATOS GENERALES PARA EL NUEVO REGISTRO RESULTANTE */}
              <div className="granel-section-card" style={{ marginBottom: 0 }}>
                <div className="granel-section-header">
                  <div className="granel-icon-wrapper-green">
                    <LayersIcon style={{ fontSize: 24 }} />
                  </div>
                  <div className="granel-section-header-titles">
                    <h3>Identificación del Mezcal y Contenedor Resultante</h3>
                    <p>Lote generado por extracción o consolidación de graneles</p>
                  </div>
                </div>

                <div className="granel-grid-row-2">
                  <div className="granel-input-group">
                    <label className="granel-input-label">FECHA MOVIMIENTO</label>
                    <input
                      type="date"
                      value={fusionForm.fechaMovimiento}
                      onChange={e => setFusionForm(prev => ({ ...prev, fechaMovimiento: e.target.value }))}
                      className="granel-input-control"
                      required
                    />
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">Nº DE LOTE RESULTANTE *</label>
                    <input
                      type="text"
                      value={fusionForm.noLote}
                      onChange={e => setFusionForm(prev => ({ ...prev, noLote: e.target.value }))}
                      className="granel-input-control"
                      placeholder="Ej. LOTE-GRANEL-01"
                      required
                    />
                  </div>
                </div>

                <div className="granel-grid-row-2">
                  <div className="granel-input-group">
                    <label className="granel-input-label">ID CONTENEDOR DESTINO *</label>
                    <input
                      type="text"
                      value={fusionForm.idContenedor}
                      onChange={e => setFusionForm(prev => ({ ...prev, idContenedor: e.target.value }))}
                      className="granel-input-control"
                      placeholder="Ej. TANQUE-01 / GARRAFA-05"
                      required
                    />
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">CATEGORÍA (NOM-070)</label>
                    <input
                      type="text"
                      readOnly
                      value={fusionCalculations.categoriaBase || "Seleccione lote(s) origen"}
                      className="granel-input-control"
                      style={{ background: "#F8FAFC", cursor: "not-allowed", fontWeight: 600, color: "#1E293B" }}
                    />
                    <span style={{ fontSize: "11px", color: "#64748B", marginTop: "2px" }}>
                      * Heredada automáticamente de los lotes de origen.
                    </span>
                  </div>
                </div>

                <div className="granel-grid-row-2">
                  <div className="granel-input-group">
                    <label className="granel-input-label">CLASE (NOM-070)</label>
                    <input
                      type="text"
                      readOnly
                      value={fusionCalculations.claseBase || "Seleccione lote(s) origen"}
                      className="granel-input-control"
                      style={{ background: "#F8FAFC", cursor: "not-allowed", fontWeight: 600, color: "#1E293B" }}
                    />
                    <span style={{ fontSize: "11px", color: "#64748B", marginTop: "2px" }}>
                      * Heredada automáticamente de los lotes de origen.
                    </span>
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">INGREDIENTE(S)</label>
                    <input
                      type="text"
                      value={fusionForm.ingredientes || fusionCalculations.ingredientesUnicos}
                      onChange={e => setFusionForm(prev => ({ ...prev, ingredientes: e.target.value }))}
                      className="granel-input-control"
                      placeholder="Botánicos o ingredientes si aplica"
                    />
                  </div>
                </div>

                <div className="granel-grid-row-2">
                  <div className="granel-input-group">
                    <label className="granel-input-label">TIPO DE MAGUEY(ES) (ORDENADO DE MAYOR A MENOR)</label>
                    <input
                      type="text"
                      readOnly
                      value={fusionCalculations.magueyesOrdenados || fusionForm.tipoMaguey || "Seleccione lote(s) origen"}
                      className="granel-input-control"
                      style={{ background: "#F8FAFC", fontWeight: 600, color: "#0F172A" }}
                    />
                    <span style={{ fontSize: "11px", color: "#0284C7", marginTop: "2px" }}>
                      * Ordenado de mayor a menor proporción: Nombre Común (Nombre Científico).
                    </span>
                  </div>
                  <div className="granel-input-group">
                    <label className="granel-input-label">Nº ANÁLISIS FISICOQUÍMICO (FQ)</label>
                    <input
                      type="text"
                      readOnly
                      disabled
                      value=""
                      className="granel-input-control"
                      placeholder="(En blanco - FQ anterior invalidado por mezcla/extracción)"
                      style={{ background: "#FEF2F2", color: "#991B1B", border: "1px dashed #FCA5A5", cursor: "not-allowed" }}
                    />
                    <span style={{ fontSize: "11px", color: "#DC2626", fontWeight: 600, marginTop: "2px" }}>
                      * Al mezclar o extraer graneles, el análisis FQ queda invalidado y permanece en blanco hasta nuevo dictamen.
                    </span>
                  </div>
                </div>

                <div className="granel-input-group" style={{ marginTop: "12px" }}>
                  <label className="granel-input-label">OBSERVACIONES</label>
                  <textarea
                    rows={2}
                    value={fusionForm.observaciones}
                    onChange={e => setFusionForm(prev => ({ ...prev, observaciones: e.target.value }))}
                    className="granel-input-control"
                    placeholder="Observaciones adicionales sobre la extracción o mezcla de granel..."
                    style={{ resize: "vertical" }}
                  />
                </div>
              </div>

              {/* Botones de Acción */}
              <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "10px", paddingTop: "14px", borderTop: "1px solid #E2E8F0" }}>
                <button type="button" className="btn-cancel-modal" onClick={() => setIsFusionModalOpen(false)}>
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={(e) => handleExecuteFusion(e, "borrador")}
                  disabled={fusionCalculations.tieneConflictoCategoria || fusionCalculations.tieneConflictoClase}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "9px 18px",
                    background: "#F1F5F9",
                    color: (fusionCalculations.tieneConflictoCategoria || fusionCalculations.tieneConflictoClase) ? "#94A3B8" : "#334155",
                    border: "1px solid #CBD5E1",
                    borderRadius: "10px",
                    fontWeight: 700,
                    fontSize: "13px",
                    cursor: (fusionCalculations.tieneConflictoCategoria || fusionCalculations.tieneConflictoClase) ? "not-allowed" : "pointer"
                  }}
                >
                  <span>💾 Guardar en Borrador</span>
                </button>
                <button
                  type="button"
                  onClick={(e) => handleExecuteFusion(e, "revision")}
                  disabled={isSubmittingFusion || fusionCalculations.tieneConflictoCategoria || fusionCalculations.tieneConflictoClase}
                  className="destilacion-btn-new"
                  style={{
                    background: (isSubmittingFusion || fusionCalculations.tieneConflictoCategoria || fusionCalculations.tieneConflictoClase)
                      ? "#94A3B8"
                      : "linear-gradient(135deg, #7C3AED 0%, #9333EA 100%)",
                    boxShadow: (isSubmittingFusion || fusionCalculations.tieneConflictoCategoria || fusionCalculations.tieneConflictoClase)
                      ? "none"
                      : "0 4px 14px rgba(124, 58, 237, 0.3)",
                    cursor: (isSubmittingFusion || fusionCalculations.tieneConflictoCategoria || fusionCalculations.tieneConflictoClase) ? "not-allowed" : "pointer"
                  }}
                >
                  <MergeTypeIcon style={{ fontSize: 18 }} />
                  <span>
                    {isSubmittingFusion ? "Procesando..." : (fusionSources.filter(s => s.granelId).length <= 1 ? "Registrar Extracción y Enviar a Revisión UI" : "Registrar Mezcla y Enviar a Revisión UI")}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* =========================================================================
          5. MODAL DE TRASLADO ENTRE INSTALACIONES
      ========================================================================= */}
      {isTrasladoModalOpen && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setIsTrasladoModalOpen(false)}>
          <div className="detail-modal-card animate-fadeIn" onClick={e => e.stopPropagation()} style={{ maxWidth: "780px" }}>
            <div className="detail-modal-header" style={{ background: "linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)", color: "white", padding: "16px 22px" }}>
              <div className="detail-modal-title-box">
                <div style={{ display: "flex", gap: "6px", marginBottom: "4px" }}>
                  <span className="badge-modal-tag" style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>SECCIÓN II: GRANEL</span>
                  <span className="badge-modal-tag" style={{ background: "#DBEAFE", color: "#1E40AF", fontWeight: 700 }}>TRASLADO ENTRE INSTALACIONES</span>
                </div>
                <h1 className="detail-modal-title" style={{ color: "white", fontSize: "19px" }}>
                  Traslado de Mezcal a Granel entre Instalaciones
                </h1>
                <p style={{ margin: 0, fontSize: "12px", opacity: 0.9 }}>
                  Control de movilización interna de producto a granel con dictamen y validación UI
                </p>
              </div>
              <button type="button" onClick={() => setIsTrasladoModalOpen(false)} className="detail-modal-close-btn">
                <CloseIcon />
              </button>
            </div>

            <form onSubmit={(e) => {
              e.preventDefault();
              const vol = parseFloat(trasladoForm.litros_traslado || 0);
              const saldoDisp = parseFloat(trasladoForm.volumen_disponible || 0);
              if (vol <= 0 || vol > saldoDisp) {
                Swal.fire({
                  title: "Volumen inválido",
                  text: `El volumen a trasladar debe ser mayor a 0 y no puede exceder el saldo disponible (${saldoDisp} L).`,
                  icon: "warning",
                  confirmButtonColor: "#2563EB"
                });
                return;
              }
              if (!trasladoForm.instalacion_destino) {
                Swal.fire({
                  title: "Instalación requerida",
                  text: "Debe seleccionar una instalación de destino para el traslado.",
                  icon: "warning",
                  confirmButtonColor: "#2563EB"
                });
                return;
              }
              if (!trasladoForm.no_tanque_destino) {
                Swal.fire({
                  title: "Tanque destino requerido",
                  text: "Debe especificar el tanque o contenedor receptor en la instalación destino.",
                  icon: "warning",
                  confirmButtonColor: "#2563EB"
                });
                return;
              }

              const vRestante = Math.max(0, parseFloat((saldoDisp - vol).toFixed(2)));

              if (handleSaveTraslado) {
                handleSaveTraslado({
                  ...trasladoForm,
                  volumen: vol,
                  litros_traslado: vol,
                  saldo_restante: vRestante
                });
              }

              setIsTrasladoModalOpen(false);
              Swal.fire({
                title: "¡Solicitud de Traslado Enviada a la Unidad de Inspección!",
                html: `Se registró la solicitud de traslado de <b>${vol} L</b> del lote <b>${trasladoForm.no_lote_origen}</b> hacia <b>${trasladoForm.instalacion_destino}</b> (Tanque: <b>${trasladoForm.no_tanque_destino}</b>).<br/><br/>
                <div style="background: #EFF6FF; border: 1.5px solid #93C5FD; border-radius: 10px; padding: 12px; text-align: left; font-size: 13px; color: #1E40AF; line-height: 1.5;">
                  <div style="margin-bottom: 4px;">• <b>Estatus:</b> Enviado a revisión y validación por la Unidad de Inspección.</div>
                  <div style="margin-bottom: 4px;">• <b>Saldo en instalación origen:</b> ${saldoDisp} L (permanecerá oficial hasta la aprobación del inspector).</div>
                  <div style="margin-bottom: 4px;">• <b>Volumen a transferir:</b> ${vol} L.</div>
                  <div style="margin-bottom: 4px;">• <b>Saldo final restante tras aprobación:</b> ${vRestante} L.</div>
                  <div style="margin-bottom: 4px;">• <b>Instalación destino:</b> ${trasladoForm.instalacion_destino}.</div>
                  <div>• <b>Nuevo granel destino:</b> Se generará de forma automática en el inventario de la instalación receptora al emitirse el dictamen favorable del inspector.</div>
                </div>`,
                icon: "success",
                confirmButtonColor: "#2563EB",
                confirmButtonText: "Entendido"
              });
            }} style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: "16px", maxHeight: "80vh", overflowY: "auto" }}>

              {/* Lote Origen y Saldo */}
              <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: "14px", padding: "18px" }}>
                <h4 style={{ margin: "0 0 12px 0", fontSize: "12px", fontWeight: 800, color: "#1D4ED8", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  1. Lote de Origen e Instalación Actual
                </h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div className="integrated-input-box">
                    <span className="integrated-input-label">Lote a Trasladar <span style={{ color: "#DC2626" }}>*</span></span>
                    <select
                      value={trasladoForm.id_granel_origen}
                      onChange={e => {
                        const selId = e.target.value;
                        const g = (allAvailableGraneles || []).find(item =>
                          String(item.id_bitacora_granel) === String(selId) ||
                          String(item.id) === String(selId) ||
                          String(item.dbId) === String(selId) ||
                          String(item.noLote || item.no_lote) === String(selId) ||
                          String(item._optVal) === String(selId)
                        );
                        const v = g ? g._vDisp : 0;
                        const origInstId = g?.id_unidad_produccion || g?.id_anexo_cliente || "";
                        let origInstAlias = g?.instalacion || g?.instalacionOrigen || "";
                        if (!origInstAlias || origInstAlias === "Instalación Principal") {
                          const matchedInst = availableInstalaciones.find(i => String(i.idanexocliente || i.id) === String(origInstId));
                          if (matchedInst) {
                            origInstAlias = matchedInst.informaciongeneralinstalacioncliente?.alias || matchedInst.alias;
                          }
                        }
                        origInstAlias = origInstAlias || availableInstalaciones[0]?.alias || "Instalación Principal";

                        setTrasladoForm(prev => ({
                          ...prev,
                          id_granel_origen: selId,
                          no_lote_origen: g?.noLote || g?.no_lote || "",
                          id_unidad_produccion_origen: origInstId,
                          instalacion_origen: origInstAlias,
                          volumen_disponible: v,
                          litros_traslado: "",
                          ...(String(prev.id_unidad_produccion_destino) === String(origInstId) ? {
                            id_unidad_produccion_destino: "",
                            instalacion_destino: "",
                            tipo_instalacion_destino: ""
                          } : {})
                        }));
                      }}
                      className="integrated-select-control"
                      required
                    >
                      <option value="">-- Seleccionar Lote de Granel ({(allAvailableGraneles || []).filter(g => g._vDisp > 0 || String(g._optVal) === String(trasladoForm.id_granel_origen)).length} Disponibles) --</option>
                      {(allAvailableGraneles || [])
                        .filter(g => g._vDisp > 0 || String(g._optVal) === String(trasladoForm.id_granel_origen))
                        .map((g, idx) => (
                          <option key={g._optVal || idx} value={g._optVal}>
                            {g._label}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="integrated-input-box" style={{ background: "#F1F5F9" }}>
                    <span className="integrated-input-label">Instalación Origen Actual</span>
                    <div className="integrated-display-value" style={{ fontWeight: 700, color: "#1E293B" }}>
                      {trasladoForm.instalacion_origen || "Instalación Principal"}
                    </div>
                  </div>
                </div>

                {trasladoForm.volumen_disponible > 0 && (
                  <div style={{ marginTop: "12px", fontSize: "12.5px", color: "#1E40AF", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#DBEAFE", padding: "8px 12px", borderRadius: "8px" }}>
                    <span>Saldo disponible en tanque origen: <strong>{trasladoForm.volumen_disponible.toLocaleString()} Litros</strong></span>
                    <button
                      type="button"
                      onClick={() => setTrasladoForm(prev => ({ ...prev, litros_traslado: prev.volumen_disponible }))}
                      style={{ background: "#1D4ED8", border: "none", color: "white", borderRadius: "6px", padding: "4px 10px", fontSize: "11px", fontWeight: 700, cursor: "pointer" }}
                    >
                      Trasladar todo el saldo
                    </button>
                  </div>
                )}
              </div>

              {/* Destino del Traslado */}
              <div style={{ background: "#F8FAFC", border: "1px solid #CBD5E1", borderRadius: "14px", padding: "18px" }}>
                <h4 style={{ margin: "0 0 12px 0", fontSize: "12px", fontWeight: 800, color: "#334155", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  2. Instalación Destino y Tanque Receptor
                </h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
                  <div className="integrated-input-box">
                    <span className="integrated-input-label">Instalación Destino <span style={{ color: "#DC2626" }}>*</span></span>
                    <select
                      value={trasladoForm.id_unidad_produccion_destino}
                      onChange={e => {
                        const selId = e.target.value;
                        const inst = availableInstalaciones.find(i => String(i.idanexocliente || i.id) === String(selId));
                        const alias = inst?.informaciongeneralinstalacioncliente?.alias || inst?.alias || "";
                        const tipo = inst?.tipo || "";
                        setTrasladoForm(prev => ({
                          ...prev,
                          id_unidad_produccion_destino: selId,
                          instalacion_destino: alias,
                          tipo_instalacion_destino: tipo
                        }));
                      }}
                      className="integrated-select-control"
                      required
                    >
                      <option value="">-- Seleccionar Instalación Destino ({availableInstalaciones.length} registradas) --</option>
                      {availableInstalaciones.map((inst, idx) => {
                        const instId = inst.idanexocliente || inst.id;
                        const aliasLabel = inst.informaciongeneralinstalacioncliente?.alias || inst.alias || `Instalación #${instId}`;
                        const tipoLabel = inst.tipo || (inst.idtipoanexo === 1 ? "Unidad de producción" : inst.idtipoanexo === 2 ? "Unidad de envasado" : inst.idtipoanexo === 3 ? "Unidad de almacén" : "Instalación");
                        const dirLabel = inst.direccion || inst.informaciongeneralinstalacioncliente?.direccion || "";
                        const isCurrentOrigin = (trasladoForm.id_unidad_produccion_origen && String(trasladoForm.id_unidad_produccion_origen) === String(instId)) ||
                          (trasladoForm.instalacion_origen && String(trasladoForm.instalacion_origen).trim().toLowerCase() === String(aliasLabel).trim().toLowerCase());

                        return (
                          <option key={idx} value={instId} disabled={isCurrentOrigin}>
                            [{tipoLabel}] {aliasLabel} {isCurrentOrigin ? " (Instalación actual / Origen)" : ""} {dirLabel ? ` - ${dirLabel}` : ""}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div className="integrated-input-box">
                    <span className="integrated-input-label">Tanque / Contenedor Destino <span style={{ color: "#DC2626" }}>*</span></span>
                    <input
                      type="text"
                      placeholder="Ej: T-08, Contenedor 3"
                      value={trasladoForm.no_tanque_destino}
                      onChange={e => setTrasladoForm(prev => ({ ...prev, no_tanque_destino: e.target.value }))}
                      className="integrated-input-control"
                      required
                    />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
                  <div className="integrated-input-box" style={{ borderColor: "#2563EB", background: "#EFF6FF" }}>
                    <span className="integrated-input-label" style={{ color: "#1D4ED8" }}>Volumen a Trasladar (L) <span style={{ color: "#DC2626" }}>*</span></span>
                    <input
                      type="number"
                      step="any"
                      min="0.1"
                      max={trasladoForm.volumen_disponible || undefined}
                      placeholder={`Máx. ${trasladoForm.volumen_disponible} L`}
                      value={trasladoForm.litros_traslado}
                      onChange={e => setTrasladoForm(prev => ({ ...prev, litros_traslado: e.target.value }))}
                      className="integrated-input-control"
                      style={{ fontWeight: 900, color: "#1D4ED8", fontSize: "15px" }}
                      required
                    />
                    <span className="integrated-input-suffix" style={{ color: "#1D4ED8", background: "#DBEAFE", borderColor: "#BFDBFE" }}>L</span>
                  </div>

                  <div className="integrated-input-box">
                    <span className="integrated-input-label">Fecha del Traslado <span style={{ color: "#DC2626" }}>*</span></span>
                    <input
                      type="date"
                      value={trasladoForm.fecha}
                      onChange={e => setTrasladoForm(prev => ({ ...prev, fecha: e.target.value }))}
                      className="integrated-input-control"
                      required
                    />
                  </div>
                </div>

                {/* Tarjeta de Cálculo de Saldo en Origen */}
                {(() => {
                  const vol = parseFloat(trasladoForm.litros_traslado || 0);
                  const saldoActual = parseFloat(trasladoForm.volumen_disponible || 0);
                  const vRestante = Math.max(0, parseFloat((saldoActual - vol).toFixed(2)));
                  return (
                    <div style={{ background: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)", border: "1.5px solid #93C5FD", borderRadius: "12px", padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <span style={{ fontSize: "11px", fontWeight: 800, color: "#1D4ED8", textTransform: "uppercase", letterSpacing: "0.5px" }}>Volumen a Trasladar:</span>
                        <div style={{ fontSize: "20px", fontWeight: 900, color: "#1D4ED8" }}>
                          -{vol.toLocaleString()} Litros
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <span style={{ fontSize: "11px", fontWeight: 800, color: "#1D4ED8", textTransform: "uppercase", letterSpacing: "0.5px" }}>Saldo Restante en Origen tras Dictamen UI:</span>
                        <div style={{ fontSize: "20px", fontWeight: 900, color: "#166534" }}>
                          {vRestante.toLocaleString()} Litros
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Trazabilidad y Transporte */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div className="integrated-input-box">
                  <span className="integrated-input-label">No. Guía / Carta Porte / Folio de Traslado</span>
                  <input
                    type="text"
                    placeholder="Ej: GUIA-TR-902, CARTA-PORTE-101"
                    value={trasladoForm.folio_documento}
                    onChange={e => setTrasladoForm(prev => ({ ...prev, folio_documento: e.target.value }))}
                    className="integrated-input-control"
                  />
                </div>

                <div className="integrated-input-box">
                  <span className="integrated-input-label">Datos del Transporte / Chofer / Placas / Precintos</span>
                  <input
                    type="text"
                    placeholder="Ej: Juan Pérez, Camión Isuzu Placas 45-AF-9, Precinto 108"
                    value={trasladoForm.chofer_transporte || ""}
                    onChange={e => setTrasladoForm(prev => ({ ...prev, chofer_transporte: e.target.value }))}
                    className="integrated-input-control"
                  />
                </div>
              </div>

              <div className="integrated-input-box">
                <span className="integrated-input-label">Observaciones del Traslado</span>
                <input
                  type="text"
                  placeholder="Observaciones adicionales sobre condiciones de transporte..."
                  value={trasladoForm.observaciones}
                  onChange={e => setTrasladoForm(prev => ({ ...prev, observaciones: e.target.value }))}
                  className="integrated-input-control"
                />
              </div>

              {/* Aviso de Validación UI */}
              <div style={{ background: "#EFF6FF", border: "1.5px solid #93C5FD", borderRadius: "12px", padding: "12px 16px", display: "flex", flexDirection: "column", gap: "6px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#1E40AF", fontWeight: 800, fontSize: "12.5px" }}>
                  <span>📋</span>
                  <span>FLUJO DE VALIDACIÓN OBLIGATORIA POR LA UNIDAD DE INSPECCIÓN (UI)</span>
                </div>
                <p style={{ margin: 0, fontSize: "12px", color: "#1E3A8A", lineHeight: 1.4 }}>
                  Al registrar este traslado, la solicitud pasará a <strong>revisión y dictamen del Inspector de la UI</strong>. El volumen permanecerá oficial en la instalación origen hasta que el Inspector emita su aprobación. Una vez aprobado, el sistema descontará automáticamente los <strong>{parseFloat(trasladoForm.litros_traslado || 0)} L</strong> del lote origen y <strong>creará el nuevo registro de granel en la instalación destino</strong>.
                </p>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "10px" }}>
                <button
                  type="button"
                  onClick={() => setIsTrasladoModalOpen(false)}
                  className="btn-cancel-custom"
                  style={{ padding: "10px 18px", borderRadius: "10px", border: "1px solid #CBD5E1", background: "white", fontWeight: 600 }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  style={{
                    background: "linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    padding: "10px 22px",
                    fontWeight: 700,
                    cursor: "pointer",
                    boxShadow: "0 4px 14px rgba(37, 99, 235, 0.3)",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px"
                  }}
                >
                  <SendIcon style={{ fontSize: 18 }} />
                  <span>Enviar Solicitud de Traslado a UI</span>
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* =========================================================================
          6. MODAL DE DILUCIÓN / AJUSTE DE GRADO ALCOHÓLICO
      ========================================================================= */}
      {isDilucionModalOpen && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setIsDilucionModalOpen(false)}>
          <div className="detail-modal-card animate-fadeIn" onClick={e => e.stopPropagation()} style={{ maxWidth: "780px" }}>
            <div className="detail-modal-header" style={{ background: "linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)", color: "white", padding: "16px 22px" }}>
              <div className="detail-modal-title-box">
                <div style={{ display: "flex", gap: "6px", marginBottom: "4px" }}>
                  <span className="badge-modal-tag" style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>SECCIÓN II: GRANEL</span>
                  <span className="badge-modal-tag" style={{ background: "#CCFBF1", color: "#0F766E", fontWeight: 700 }}>AJUSTE CON AGUA</span>
                </div>
                <h1 className="detail-modal-title" style={{ color: "white", fontSize: "19px" }}>
                  Dilución y Ajuste de Grado Alcohólico (% Alc. Vol.)
                </h1>
              </div>
              <button type="button" onClick={() => setIsDilucionModalOpen(false)} className="detail-modal-close-btn">
                <CloseIcon />
              </button>
            </div>

            <form onSubmit={(e) => {
              e.preventDefault();
              const v1 = parseFloat(dilucionForm.volumen_actual || 0);
              const c1 = parseFloat(dilucionForm.grado_actual || 0);
              const agua = parseFloat(dilucionForm.agua_agregada || 0);

              if (agua <= 0) {
                Swal.fire({
                  title: "Volumen de agua requerido",
                  text: "El volumen de agua a agregar debe ser mayor a 0 litros.",
                  icon: "warning",
                  confirmButtonColor: "#0D9488"
                });
                return;
              }

              const c2 = parseFloat(((v1 * c1) / (v1 + agua)).toFixed(2));
              const vFin = parseFloat((v1 + agua).toFixed(2));

              if (c2 < 35.0) {
                Swal.fire({
                  title: "Grado Alcohólico fuera de norma",
                  text: `El grado resultante (${c2}% Alc. Vol.) es menor al límite mínimo de 35% establecido en la NOM-070-SCFI-2016 para Mezcal.`,
                  icon: "warning",
                  confirmButtonColor: "#0D9488"
                });
                return;
              }

              if (handleSaveDilucion) {
                handleSaveDilucion({
                  ...dilucionForm,
                  grado_objetivo: c2,
                  agua_agregada: agua,
                  volumen_final: vFin
                });
              }

              setIsDilucionModalOpen(false);
              Swal.fire({
                title: "¡Solicitud Enviada a la Unidad de Inspección!",
                html: `Se registró la incorporación de <b>${agua} L</b> de agua para el lote <b>${dilucionForm.no_lote_origen}</b>.<br/><br/>
                <div style="background: #F0FDF4; border: 1.5px solid #86EFAC; border-radius: 10px; padding: 12px; text-align: left; font-size: 13px; color: #166534; line-height: 1.5;">
                  <div style="margin-bottom: 4px;">• <b>Estatus:</b> Enviado a revisión y validación por la Unidad de Inspección.</div>
                  <div style="margin-bottom: 4px;">• <b>Volumen actual en bitácora:</b> ${v1} L (permanecerá oficial hasta la aprobación del inspector).</div>
                  <div style="margin-bottom: 4px;">• <b>Volumen proyectado con dilución:</b> ${vFin} L.</div>
                  <div style="margin-bottom: 4px;">• <b>Grado proyectado:</b> ${c2}% Alc. Vol.</div>
                  <div style="color: #DC2626; font-size: 11.5px; margin-top: 6px;">⚠️ <b>NOM-070:</b> Al aprobarse por la UI, la clave FQ anterior quedará invalidada hasta nuevo análisis.</div>
                </div>`,
                icon: "success",
                confirmButtonColor: "#0F766E",
                confirmButtonText: "Entendido"
              });
            }} style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: "16px", maxHeight: "80vh", overflowY: "auto" }}>

              {/* Selección de Lote */}
              <div style={{ background: "#F0FDFA", border: "1px solid #99F6E4", borderRadius: "14px", padding: "18px" }}>
                <h4 style={{ margin: "0 0 12px 0", fontSize: "12px", fontWeight: 800, color: "#0F766E", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  1. Lote a Ajustar y Parámetros Iniciales
                </h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div className="integrated-input-box">
                    <span className="integrated-input-label">Lote a Diluir <span style={{ color: "#DC2626" }}>*</span></span>
                    <select
                      value={dilucionForm.id_granel_origen}
                      onChange={e => {
                        const selId = e.target.value;
                        const g = (allAvailableGraneles || []).find(item =>
                          String(item.id_bitacora_granel) === String(selId) ||
                          String(item.id) === String(selId) ||
                          String(item.dbId) === String(selId) ||
                          String(item.noLote || item.no_lote) === String(selId) ||
                          String(item._optVal) === String(selId)
                        );
                        const v = g ? g._vDisp : 0;
                        const a = g ? g._aDisp : 48.0;
                        const aguaNum = parseFloat(dilucionForm.agua_agregada || 0);
                        const c2Calc = (v > 0 && a > 0 && aguaNum > 0)
                          ? parseFloat(((v * a) / (v + aguaNum)).toFixed(2))
                          : a;
                        const vFinCalc = parseFloat((v + aguaNum).toFixed(2));

                        setDilucionForm(prev => ({
                          ...prev,
                          id_granel_origen: selId,
                          no_lote_origen: g?.noLote || g?.no_lote || "",
                          volumen_actual: v,
                          grado_actual: a,
                          grado_objetivo: c2Calc,
                          volumen_final: vFinCalc,
                          no_tanque: g?.idContenedor || g?.no_tanque || "TANQUE-01"
                        }));
                      }}
                      className="integrated-select-control"
                      required
                    >
                      <option value="">-- Seleccionar Lote a Diluir ({(allAvailableGraneles || []).filter(g => g._vDisp > 0 || String(g._optVal) === String(dilucionForm.id_granel_origen)).length} Disponibles) --</option>
                      {(allAvailableGraneles || [])
                        .filter(g => g._vDisp > 0 || String(g._optVal) === String(dilucionForm.id_granel_origen))
                        .map((g, idx) => (
                          <option key={g._optVal || idx} value={g._optVal}>
                            {g._label}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="integrated-input-box">
                    <span className="integrated-input-label">Tanque / Contenedor Receptor <span style={{ color: "#DC2626" }}>*</span></span>
                    <input
                      type="text"
                      value={dilucionForm.no_tanque}
                      onChange={e => setDilucionForm(prev => ({ ...prev, no_tanque: e.target.value }))}
                      className="integrated-input-control"
                      placeholder="Ej: TANQUE-01"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Calculadora Dinámica de Dilución */}
              <div style={{ background: "#F8FAFC", border: "1px solid #CBD5E1", borderRadius: "14px", padding: "18px" }}>
                <h4 style={{ margin: "0 0 12px 0", fontSize: "12px", fontWeight: 800, color: "#334155", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  2. Calculadora de Dilución (Conservación de Alcohol Puro)
                </h4>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "14px" }}>
                  <div className="integrated-input-box" style={{ background: "#F1F5F9" }}>
                    <span className="integrated-input-label">Volumen Inicial (L)</span>
                    <div className="integrated-display-value" style={{ fontWeight: 800, color: "#1E293B" }}>
                      {dilucionForm.volumen_actual ? `${dilucionForm.volumen_actual.toLocaleString()} L` : "0 L"}
                    </div>
                    <span className="integrated-input-suffix">L</span>
                  </div>

                  <div className="integrated-input-box" style={{ background: "#F1F5F9" }}>
                    <span className="integrated-input-label">Grado Actual (% Alc. Vol.)</span>
                    <div className="integrated-display-value" style={{ fontWeight: 800, color: "#1E293B" }}>
                      {dilucionForm.grado_actual ? `${dilucionForm.grado_actual}%` : "0%"}
                    </div>
                    <span className="integrated-input-suffix">%</span>
                  </div>

                  <div className="integrated-input-box" style={{ borderColor: "#0D9488", background: "#F0FDFA" }}>
                    <span className="integrated-input-label" style={{ color: "#0F766E" }}>Agua a Incorporar (L) <span style={{ color: "#DC2626" }}>*</span></span>
                    <input
                      type="number"
                      step="any"
                      min="0.1"
                      placeholder="Ej: 25.5"
                      value={dilucionForm.agua_agregada}
                      onChange={e => {
                        const newAguaStr = e.target.value;
                        const aguaNum = parseFloat(newAguaStr || 0);
                        const v1 = parseFloat(dilucionForm.volumen_actual || 0);
                        const c1 = parseFloat(dilucionForm.grado_actual || 0);
                        const c2Calc = (v1 > 0 && c1 > 0 && aguaNum > 0)
                          ? parseFloat(((v1 * c1) / (v1 + aguaNum)).toFixed(2))
                          : c1;
                        const vFinCalc = parseFloat((v1 + aguaNum).toFixed(2));
                        setDilucionForm(prev => ({
                          ...prev,
                          agua_agregada: newAguaStr,
                          grado_objetivo: c2Calc,
                          volumen_final: vFinCalc
                        }));
                      }}
                      className="integrated-input-control"
                      style={{ fontWeight: 900, color: "#0F766E", fontSize: "15px" }}
                      required
                    />
                    <span className="integrated-input-suffix" style={{ color: "#0F766E", background: "#CCFBF1", borderColor: "#99F6E4" }}>L</span>
                  </div>
                </div>

                {/* Tarjeta de Resultado del Cálculo */}
                <div style={{ background: "linear-gradient(135deg, #CCFBF1 0%, #99F6E4 100%)", border: "1.5px solid #5EEAD4", borderRadius: "12px", padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <span style={{ fontSize: "11px", fontWeight: 800, color: "#0F766E", textTransform: "uppercase", letterSpacing: "0.5px" }}>Grado Alcohólico Calculado:</span>
                    <div style={{ fontSize: "22px", fontWeight: 900, color: (dilucionForm.grado_objetivo < 35 && parseFloat(dilucionForm.agua_agregada || 0) > 0) ? "#DC2626" : "#0F766E" }}>
                      {dilucionForm.grado_objetivo}% Alc. Vol.
                      {(dilucionForm.grado_objetivo < 35 && parseFloat(dilucionForm.agua_agregada || 0) > 0) && (
                        <span style={{ fontSize: "11px", color: "#DC2626", fontWeight: 700, marginLeft: "8px" }}>
                          (⚠️ Menor al mín. 35% NOM-070)
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <span style={{ fontSize: "11px", fontWeight: 800, color: "#0F766E", textTransform: "uppercase", letterSpacing: "0.5px" }}>Nuevo Volumen Total:</span>
                    <div style={{ fontSize: "20px", fontWeight: 900, color: "#134E4A" }}>
                      {dilucionForm.volumen_final ? dilucionForm.volumen_final.toLocaleString() : 0} Litros
                    </div>
                  </div>
                </div>
              </div>

              {/* Trazabilidad del Agua y Aviso NOM-070 */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div className="integrated-input-box">
                  <span className="integrated-input-label">Lote / Bitácora de Agua</span>
                  <input
                    type="text"
                    placeholder="Ej: LOTE-AGUA-2026-03, OSMOSIS-1"
                    value={dilucionForm.lote_agua}
                    onChange={e => setDilucionForm(prev => ({ ...prev, lote_agua: e.target.value }))}
                    className="integrated-input-control"
                  />
                </div>

                <div className="integrated-input-box">
                  <span className="integrated-input-label">Fecha del Ajuste <span style={{ color: "#DC2626" }}>*</span></span>
                  <input
                    type="date"
                    value={dilucionForm.fecha}
                    onChange={e => setDilucionForm(prev => ({ ...prev, fecha: e.target.value }))}
                    className="integrated-input-control"
                    required
                  />
                </div>
              </div>

              {/* Alerta de Envío a UI y Aviso NOM-070 */}
              <div style={{ background: "#F0FDFA", border: "1.5px solid #99F6E4", borderRadius: "12px", padding: "12px 16px", display: "flex", flexDirection: "column", gap: "6px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#0F766E", fontWeight: 800, fontSize: "12.5px" }}>
                  <span>📋</span>
                  <span>FLUJO DE VALIDACIÓN OBLIGATORIA POR LA UNIDAD DE INSPECCIÓN (UI)</span>
                </div>
                <p style={{ margin: 0, fontSize: "12px", color: "#134E4A", lineHeight: 1.4 }}>
                  Al registrar este proceso, se generará la solicitud de dilución y se enviará para <strong>dictamen y validación del Inspector de la UI</strong>. El nuevo volumen oficial de <strong>{dilucionForm.volumen_final || 0} L</strong> se aplicará en el inventario automáticamente tras ser aprobado por la UI.
                </p>
                <div style={{ color: "#991B1B", fontSize: "11.5px", marginTop: "2px", fontWeight: 600 }}>
                  ⚠️ <strong>NOM-070-SCFI-2016:</strong> Al diluir con agua, el análisis fisicoquímico (FQ) anterior quedará invalidado y el nuevo lote permanecerá sin clave FQ hasta un nuevo informe de laboratorio acreditado.
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "10px" }}>
                <button
                  type="button"
                  onClick={() => setIsDilucionModalOpen(false)}
                  className="btn-cancel-custom"
                  style={{ padding: "10px 18px", borderRadius: "10px", border: "1px solid #CBD5E1", background: "white", fontWeight: 600 }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  style={{
                    background: "linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    padding: "10px 22px",
                    fontWeight: 700,
                    cursor: "pointer",
                    boxShadow: "0 4px 14px rgba(20, 184, 166, 0.3)",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px"
                  }}
                >
                  <SendIcon style={{ fontSize: 18 }} />
                  <span>Enviar Solicitud de Dilución a UI</span>
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* =========================================================================
          7. MODAL DE SALIDAS O BAJAS DE PRODUCTO
      ========================================================================= */}
      {isBajaModalOpen && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setIsBajaModalOpen(false)}>
          <div className="detail-modal-card animate-fadeIn" onClick={e => e.stopPropagation()} style={{ maxWidth: "780px" }}>
            <div className="detail-modal-header" style={{ background: "linear-gradient(135deg, #C2410C 0%, #EA580C 100%)", color: "white", padding: "16px 22px" }}>
              <div className="detail-modal-title-box">
                <div style={{ display: "flex", gap: "6px", marginBottom: "4px" }}>
                  <span className="badge-modal-tag" style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>SECCIÓN II: GRANEL</span>
                  <span className="badge-modal-tag" style={{ background: "#FFEDD5", color: "#C2410C", fontWeight: 700 }}>EGRESO / BAJA DE INVENTARIO</span>
                </div>
                <h1 className="detail-modal-title" style={{ color: "white", fontSize: "19px" }}>
                  Salida o Baja de Producto a Granel
                </h1>
              </div>
              <button type="button" onClick={() => setIsBajaModalOpen(false)} className="detail-modal-close-btn">
                <CloseIcon />
              </button>
            </div>

            <form onSubmit={(e) => {
              e.preventDefault();
              const vol = parseFloat(bajaForm.litros_baja || 0);
              const saldoActual = parseFloat(bajaForm.volumen_actual || 0);
              if (vol <= 0 || vol > saldoActual) {
                Swal.fire({
                  title: "Volumen inválido",
                  text: `El volumen a dar de baja debe ser mayor a 0 y no puede exceder el saldo disponible (${saldoActual} L).`,
                  icon: "warning",
                  confirmButtonColor: "#EA580C"
                });
                return;
              }

              const vRestante = Math.max(0, parseFloat((saldoActual - vol).toFixed(2)));

              if (handleSaveBaja) {
                handleSaveBaja({
                  ...bajaForm,
                  volumen: vol,
                  litros_baja: vol,
                  saldo_restante: vRestante
                });
              }

              setIsBajaModalOpen(false);
              Swal.fire({
                title: "¡Solicitud de Baja Enviada a la Unidad de Inspección!",
                html: `Se registró la solicitud de baja de <b>${vol} L</b> del lote <b>${bajaForm.no_lote_origen}</b>.<br/><br/>
                <div style="background: #FFF7ED; border: 1.5px solid #FDBA74; border-radius: 10px; padding: 12px; text-align: left; font-size: 13px; color: #9A3412; line-height: 1.5;">
                  <div style="margin-bottom: 4px;">• <b>Estatus:</b> Enviado a revisión y validación por la Unidad de Inspección.</div>
                  <div style="margin-bottom: 4px;">• <b>Saldo actual en bitácora:</b> ${saldoActual} L (permanecerá oficial hasta la aprobación del inspector).</div>
                  <div style="margin-bottom: 4px;">• <b>Volumen a descontar:</b> ${vol} L.</div>
                  <div style="margin-bottom: 4px;">• <b>Saldo final proyectado tras aprobación:</b> ${vRestante} L.</div>
                  <div style="margin-bottom: 4px;">• <b>Motivo:</b> ${bajaForm.motivo}.</div>
                </div>`,
                icon: "success",
                confirmButtonColor: "#EA580C",
                confirmButtonText: "Entendido"
              });
            }} style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: "16px", maxHeight: "80vh", overflowY: "auto" }}>

              {/* Selección de Lote y Saldo */}
              <div style={{ background: "#FFF7ED", border: "1px solid #FFEDD5", borderRadius: "14px", padding: "18px" }}>
                <h4 style={{ margin: "0 0 12px 0", fontSize: "12px", fontWeight: 800, color: "#C2410C", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  1. Lote Afectado y Saldo Disponible
                </h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div className="integrated-input-box">
                    <span className="integrated-input-label">Lote Afectado <span style={{ color: "#DC2626" }}>*</span></span>
                    <select
                      value={bajaForm.id_granel_origen}
                      onChange={e => {
                        const selId = e.target.value;
                        const g = (allAvailableGraneles || []).find(item =>
                          String(item.id_bitacora_granel) === String(selId) ||
                          String(item.id) === String(selId) ||
                          String(item.dbId) === String(selId) ||
                          String(item.noLote || item.no_lote) === String(selId) ||
                          String(item._optVal) === String(selId)
                        );
                        const v = g ? g._vDisp : 0;
                        setBajaForm(prev => ({
                          ...prev,
                          id_granel_origen: selId,
                          no_lote_origen: g?.noLote || g?.no_lote || "",
                          volumen_actual: v
                        }));
                      }}
                      className="integrated-select-control"
                      required
                    >
                      <option value="">-- Seleccionar Lote de Granel ({(allAvailableGraneles || []).filter(g => g._vDisp > 0 || String(g._optVal) === String(bajaForm.id_granel_origen)).length} Disponibles) --</option>
                      {(allAvailableGraneles || [])
                        .filter(g => g._vDisp > 0 || String(g._optVal) === String(bajaForm.id_granel_origen))
                        .map((g, idx) => (
                          <option key={g._optVal || idx} value={g._optVal}>
                            {g._label}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="integrated-input-box" style={{ background: "#F1F5F9" }}>
                    <span className="integrated-input-label">Saldo Disponible en Tanque</span>
                    <div className="integrated-display-value" style={{ fontWeight: 800, color: "#1E293B" }}>
                      {bajaForm.volumen_actual ? `${bajaForm.volumen_actual.toLocaleString()} Litros` : "0 Litros"}
                    </div>
                    <span className="integrated-input-suffix">L</span>
                  </div>
                </div>
              </div>

              {/* Volumen y Motivo de la Baja */}
              <div style={{ background: "#F8FAFC", border: "1px solid #CBD5E1", borderRadius: "14px", padding: "18px" }}>
                <h4 style={{ margin: "0 0 12px 0", fontSize: "12px", fontWeight: 800, color: "#334155", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  2. Parámetros de la Salida / Baja
                </h4>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "14px" }}>
                  <div className="integrated-input-box" style={{ borderColor: "#EA580C", background: "#FFF7ED" }}>
                    <span className="integrated-input-label" style={{ color: "#C2410C" }}>Volumen a Dar de Baja (L) <span style={{ color: "#DC2626" }}>*</span></span>
                    <input
                      type="number"
                      step="any"
                      min="0.1"
                      max={bajaForm.volumen_actual || undefined}
                      placeholder={`Máx. ${bajaForm.volumen_actual || 0}`}
                      value={bajaForm.litros_baja}
                      onChange={e => setBajaForm(prev => ({ ...prev, litros_baja: e.target.value }))}
                      className="integrated-input-control"
                      style={{ fontWeight: 900, color: "#C2410C", fontSize: "15px" }}
                      required
                    />
                    <span className="integrated-input-suffix" style={{ color: "#C2410C", background: "#FFEDD5", borderColor: "#FDBA74" }}>L</span>
                  </div>

                  <div className="integrated-input-box">
                    <span className="integrated-input-label">Motivo de la Salida / Baja <span style={{ color: "#DC2626" }}>*</span></span>
                    <select
                      value={bajaForm.motivo}
                      onChange={e => setBajaForm(prev => ({ ...prev, motivo: e.target.value }))}
                      className="integrated-select-control"
                      required
                    >
                      <option value="Merma natural por evaporación en tanque">Merma natural por evaporación en tanque</option>
                      <option value="Derrame accidental / Rotura de válvula">Derrame accidental / Rotura de válvula</option>
                      <option value="Ajuste por inventario físico / Auditoría">Ajuste por inventario físico / Auditoría</option>
                      <option value="Destrucción técnica / Desecho de producto">Destrucción técnica / Desecho de producto</option>
                      <option value="Otro motivo especificado en observaciones">Otro motivo especificado en observaciones</option>
                    </select>
                  </div>
                </div>

                {/* Tarjeta de Cálculo de Saldo Resultante */}
                {(() => {
                  const vol = parseFloat(bajaForm.litros_baja || 0);
                  const saldoActual = parseFloat(bajaForm.volumen_actual || 0);
                  const vRestante = Math.max(0, parseFloat((saldoActual - vol).toFixed(2)));
                  return (
                    <div style={{ background: "linear-gradient(135deg, #FFEDD5 0%, #FED7AA 100%)", border: "1.5px solid #FDBA74", borderRadius: "12px", padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <span style={{ fontSize: "11px", fontWeight: 800, color: "#C2410C", textTransform: "uppercase", letterSpacing: "0.5px" }}>Volumen a Descontar:</span>
                        <div style={{ fontSize: "20px", fontWeight: 900, color: "#DC2626" }}>
                          -{vol.toLocaleString()} Litros
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <span style={{ fontSize: "11px", fontWeight: 800, color: "#C2410C", textTransform: "uppercase", letterSpacing: "0.5px" }}>Saldo Final Proyectado tras Dictamen UI:</span>
                        <div style={{ fontSize: "20px", fontWeight: 900, color: "#166534" }}>
                          {vRestante.toLocaleString()} Litros
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Folio y Fecha */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div className="integrated-input-box">
                  <span className="integrated-input-label">No. Folio / Acta Circunstanciada / Factura</span>
                  <input
                    type="text"
                    placeholder="Ej: ACTA-BAJA-04, FACT-891"
                    value={bajaForm.folio_documento}
                    onChange={e => setBajaForm(prev => ({ ...prev, folio_documento: e.target.value }))}
                    className="integrated-input-control"
                  />
                </div>

                <div className="integrated-input-box">
                  <span className="integrated-input-label">Fecha de la Salida <span style={{ color: "#DC2626" }}>*</span></span>
                  <input
                    type="date"
                    value={bajaForm.fecha}
                    onChange={e => setBajaForm(prev => ({ ...prev, fecha: e.target.value }))}
                    className="integrated-input-control"
                    required
                  />
                </div>
              </div>

              {/* Observaciones y Justificación */}
              <div className="integrated-input-box" style={{ minHeight: "80px", justifyContent: "flex-start", padding: "10px 14px" }}>
                <span className="integrated-input-label">Observaciones y Justificación Técnica</span>
                <textarea
                  rows="2"
                  placeholder="Detalles técnicos, circunstancias o causas de la merma o egreso..."
                  value={bajaForm.observaciones}
                  onChange={e => setBajaForm(prev => ({ ...prev, observaciones: e.target.value }))}
                  className="integrated-input-control"
                  style={{ resize: "none" }}
                />
              </div>

              {/* Aviso de Validación UI */}
              <div style={{ background: "#FFF7ED", border: "1.5px solid #FDBA74", borderRadius: "12px", padding: "12px 16px", display: "flex", flexDirection: "column", gap: "6px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#C2410C", fontWeight: 800, fontSize: "12.5px" }}>
                  <span>📋</span>
                  <span>FLUJO DE VALIDACIÓN OBLIGATORIA POR LA UNIDAD DE INSPECCIÓN (UI)</span>
                </div>
                <p style={{ margin: 0, fontSize: "12px", color: "#9A3412", lineHeight: 1.4 }}>
                  Al registrar este egreso, se generará la solicitud de baja y pasará a <strong>revisión y dictamen del Inspector de la UI</strong>. El volumen de <strong>{parseFloat(bajaForm.litros_baja || 0)} L</strong> se descontará oficialmente del inventario de forma automática una vez que el Inspector emita su aprobación.
                </p>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "10px" }}>
                <button
                  type="button"
                  onClick={() => setIsBajaModalOpen(false)}
                  className="btn-cancel-custom"
                  style={{ padding: "10px 18px", borderRadius: "10px", border: "1px solid #CBD5E1", background: "white", fontWeight: 600 }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  style={{
                    background: "linear-gradient(135deg, #C2410C 0%, #EA580C 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    padding: "10px 22px",
                    fontWeight: 700,
                    cursor: "pointer",
                    boxShadow: "0 4px 14px rgba(234, 88, 12, 0.3)",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px"
                  }}
                >
                  <SendIcon style={{ fontSize: 18 }} />
                  <span>Enviar Solicitud de Baja a UI</span>
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* =========================================================================
          8. MODAL DE SALIDAS A MUESTREO DE PRODUCTO
      ========================================================================= */}
      {isMuestreoModalOpen && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setIsMuestreoModalOpen(false)}>
          <div className="detail-modal-card animate-fadeIn" onClick={e => e.stopPropagation()} style={{ maxWidth: "780px" }}>
            <div className="detail-modal-header" style={{ background: "linear-gradient(135deg, #4338CA 0%, #6366F1 100%)", color: "white", padding: "16px 22px" }}>
              <div className="detail-modal-title-box">
                <div style={{ display: "flex", gap: "6px", marginBottom: "4px" }}>
                  <span className="badge-modal-tag" style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>SECCIÓN II: GRANEL</span>
                  <span className="badge-modal-tag" style={{ background: "#E0E7FF", color: "#4338CA", fontWeight: 700 }}>EXTRACCIÓN DE MUESTRA</span>
                </div>
                <h1 className="detail-modal-title" style={{ color: "white", fontSize: "19px" }}>
                  Salida a Muestreo de Mezcal a Granel
                </h1>
              </div>
              <button type="button" onClick={() => setIsMuestreoModalOpen(false)} className="detail-modal-close-btn">
                <CloseIcon />
              </button>
            </div>

            <form onSubmit={(e) => {
              e.preventDefault();
              const vol = parseFloat(muestreoForm.litros_muestra || 0);
              const saldoActual = parseFloat(muestreoForm.volumen_actual || 0);
              if (vol <= 0 || vol > saldoActual) {
                Swal.fire({
                  title: "Volumen inválido",
                  text: `El volumen de muestra debe ser mayor a 0 y no puede exceder el saldo disponible (${saldoActual} L).`,
                  icon: "warning",
                  confirmButtonColor: "#4F46E5"
                });
                return;
              }

              const vRestante = Math.max(0, parseFloat((saldoActual - vol).toFixed(2)));

              if (handleSaveMuestreo) {
                handleSaveMuestreo({
                  ...muestreoForm,
                  volumen: vol,
                  litros_muestra: vol,
                  saldo_restante: vRestante
                });
              }

              setIsMuestreoModalOpen(false);
              Swal.fire({
                title: "¡Solicitud de Muestreo Enviada a la Unidad de Inspección!",
                html: `Se registró la extracción de muestra de <b>${vol} L</b> del lote <b>${muestreoForm.no_lote_origen}</b> para <b>${muestreoForm.tipo_muestreo}</b>.<br/><br/>
                <div style="background: #EEF2FF; border: 1.5px solid #A5B4FC; border-radius: 10px; padding: 12px; text-align: left; font-size: 13px; color: #3730A3; line-height: 1.5;">
                  <div style="margin-bottom: 4px;">• <b>Estatus:</b> Enviado a revisión y validación por la Unidad de Inspección.</div>
                  <div style="margin-bottom: 4px;">• <b>Saldo actual en bitácora:</b> ${saldoActual} L (permanecerá oficial hasta la aprobación del inspector).</div>
                  <div style="margin-bottom: 4px;">• <b>Volumen de muestra:</b> ${vol} L.</div>
                  <div style="margin-bottom: 4px;">• <b>Saldo final proyectado tras aprobación:</b> ${vRestante} L.</div>
                  <div style="margin-bottom: 4px;">• <b>Destino / Tipo:</b> ${muestreoForm.tipo_muestreo}.</div>
                </div>`,
                icon: "success",
                confirmButtonColor: "#4F46E5",
                confirmButtonText: "Entendido"
              });
            }} style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: "16px", maxHeight: "80vh", overflowY: "auto" }}>

              {/* Selección de Lote y Saldo */}
              <div style={{ background: "#EEF2FF", border: "1px solid #C7D2FE", borderRadius: "14px", padding: "18px" }}>
                <h4 style={{ margin: "0 0 12px 0", fontSize: "12px", fontWeight: 800, color: "#4338CA", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  1. Lote a Muestrear y Saldo Disponible
                </h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div className="integrated-input-box">
                    <span className="integrated-input-label">Lote a Muestrear <span style={{ color: "#DC2626" }}>*</span></span>
                    <select
                      value={muestreoForm.id_granel_origen}
                      onChange={e => {
                        const selId = e.target.value;
                        const g = (allAvailableGraneles || []).find(item =>
                          String(item.id_bitacora_granel) === String(selId) ||
                          String(item.id) === String(selId) ||
                          String(item.dbId) === String(selId) ||
                          String(item.noLote || item.no_lote) === String(selId) ||
                          String(item._optVal) === String(selId)
                        );
                        const v = g ? g._vDisp : 0;
                        setMuestreoForm(prev => ({
                          ...prev,
                          id_granel_origen: selId,
                          no_lote_origen: g?.noLote || g?.no_lote || "",
                          volumen_actual: v
                        }));
                      }}
                      className="integrated-select-control"
                      required
                    >
                      <option value="">-- Seleccionar Lote de Granel ({(allAvailableGraneles || []).filter(g => g._vDisp > 0 || String(g._optVal) === String(muestreoForm.id_granel_origen)).length} Disponibles) --</option>
                      {(allAvailableGraneles || [])
                        .filter(g => g._vDisp > 0 || String(g._optVal) === String(muestreoForm.id_granel_origen))
                        .map((g, idx) => (
                          <option key={g._optVal || idx} value={g._optVal}>
                            {g._label}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="integrated-input-box" style={{ background: "#F1F5F9" }}>
                    <span className="integrated-input-label">Saldo Disponible en Tanque</span>
                    <div className="integrated-display-value" style={{ fontWeight: 800, color: "#1E293B" }}>
                      {muestreoForm.volumen_actual ? `${muestreoForm.volumen_actual.toLocaleString()} Litros` : "0 Litros"}
                    </div>
                    <span className="integrated-input-suffix">L</span>
                  </div>
                </div>
              </div>

              {/* Volumen y Destino del Muestreo */}
              <div style={{ background: "#F8FAFC", border: "1px solid #CBD5E1", borderRadius: "14px", padding: "18px" }}>
                <h4 style={{ margin: "0 0 12px 0", fontSize: "12px", fontWeight: 800, color: "#334155", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  2. Parámetros de la Muestra (NOM-070-SCFI-2016)
                </h4>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "14px" }}>
                  <div className="integrated-input-box" style={{ borderColor: "#6366F1", background: "#EEF2FF" }}>
                    <span className="integrated-input-label" style={{ color: "#4338CA" }}>Volumen de Muestra (L) <span style={{ color: "#DC2626" }}>*</span></span>
                    <input
                      type="number"
                      step="0.01"
                      min="0.1"
                      max={muestreoForm.volumen_actual || undefined}
                      placeholder="Ej: 1.5, 3.0"
                      value={muestreoForm.litros_muestra}
                      onChange={e => setMuestreoForm(prev => ({ ...prev, litros_muestra: e.target.value }))}
                      className="integrated-input-control"
                      style={{ fontWeight: 900, color: "#4338CA", fontSize: "15px" }}
                      required
                    />
                    <span className="integrated-input-suffix" style={{ color: "#4338CA", background: "#E0E7FF", borderColor: "#C7D2FE" }}>L</span>
                  </div>

                  <div className="integrated-input-box">
                    <span className="integrated-input-label">Tipo / Destino de Muestreo <span style={{ color: "#DC2626" }}>*</span></span>
                    <select
                      value={muestreoForm.tipo_muestreo}
                      onChange={e => setMuestreoForm(prev => ({ ...prev, tipo_muestreo: e.target.value }))}
                      className="integrated-select-control"
                      required
                    >
                      <option value="Muestreo Oficial NOM-070 (Tercio de muestra: ensayo, testigo socio, testigo UI)">Muestreo Oficial NOM-070 (Tercio de muestra: ensayo, testigo socio, testigo UI)</option>
                      <option value="Muestreo para Análisis Fisicoquímico Previo (Laboratorio Acreditado)">Muestreo para Análisis Fisicoquímico Previo (Laboratorio Acreditado)</option>
                      <option value="Muestreo de Calidad Interno / Perfil Organoléptico">Muestreo de Calidad Interno / Perfil Organoléptico</option>
                      <option value="Muestra Comercial / Exportación / Envío a Cliente">Muestra Comercial / Exportación / Envío a Cliente</option>
                    </select>
                  </div>
                </div>

                {/* Tarjeta de Cálculo de Saldo Resultante */}
                {(() => {
                  const vol = parseFloat(muestreoForm.litros_muestra || 0);
                  const saldoActual = parseFloat(muestreoForm.volumen_actual || 0);
                  const vRestante = Math.max(0, parseFloat((saldoActual - vol).toFixed(2)));
                  return (
                    <div style={{ background: "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)", border: "1.5px solid #A5B4FC", borderRadius: "12px", padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <span style={{ fontSize: "11px", fontWeight: 800, color: "#4338CA", textTransform: "uppercase", letterSpacing: "0.5px" }}>Muestra a Extraer:</span>
                        <div style={{ fontSize: "20px", fontWeight: 900, color: "#4F46E5" }}>
                          -{vol.toLocaleString()} Litros
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <span style={{ fontSize: "11px", fontWeight: 800, color: "#4338CA", textTransform: "uppercase", letterSpacing: "0.5px" }}>Saldo Final Proyectado tras Dictamen UI:</span>
                        <div style={{ fontSize: "20px", fontWeight: 900, color: "#166534" }}>
                          {vRestante.toLocaleString()} Litros
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Laboratorio y Folio */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div className="integrated-input-box">
                  <span className="integrated-input-label">Laboratorio Acreditado o Inspector Asignado</span>
                  <input
                    type="text"
                    placeholder="Ej: Laboratorio CIATEJ, Ing. Carlos Mendoza"
                    value={muestreoForm.laboratorio_inspector}
                    onChange={e => setMuestreoForm(prev => ({ ...prev, laboratorio_inspector: e.target.value }))}
                    className="integrated-input-control"
                  />
                </div>

                <div className="integrated-input-box">
                  <span className="integrated-input-label">No. Solicitud / Precinto / Acta de Muestreo</span>
                  <input
                    type="text"
                    placeholder="Ej: SOL-MUES-2026-44, PRECINTOS 89-91"
                    value={muestreoForm.folio_documento}
                    onChange={e => setMuestreoForm(prev => ({ ...prev, folio_documento: e.target.value }))}
                    className="integrated-input-control"
                  />
                </div>
              </div>

              {/* Fecha y Observaciones */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div className="integrated-input-box">
                  <span className="integrated-input-label">Fecha de Muestreo <span style={{ color: "#DC2626" }}>*</span></span>
                  <input
                    type="date"
                    value={muestreoForm.fecha}
                    onChange={e => setMuestreoForm(prev => ({ ...prev, fecha: e.target.value }))}
                    className="integrated-input-control"
                    required
                  />
                </div>

                <div className="integrated-input-box">
                  <span className="integrated-input-label">Observaciones de Precintos y Condiciones</span>
                  <input
                    type="text"
                    placeholder="Notas sobre precintos, temperatura o condiciones..."
                    value={muestreoForm.observaciones}
                    onChange={e => setMuestreoForm(prev => ({ ...prev, observaciones: e.target.value }))}
                    className="integrated-input-control"
                  />
                </div>
              </div>

              {/* Aviso de Validación UI */}
              <div style={{ background: "#EEF2FF", border: "1.5px solid #A5B4FC", borderRadius: "12px", padding: "12px 16px", display: "flex", flexDirection: "column", gap: "6px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#4338CA", fontWeight: 800, fontSize: "12.5px" }}>
                  <span>📋</span>
                  <span>FLUJO DE VALIDACIÓN OBLIGATORIA POR LA UNIDAD DE INSPECCIÓN (UI)</span>
                </div>
                <p style={{ margin: 0, fontSize: "12px", color: "#312E81", lineHeight: 1.4 }}>
                  Al registrar este muestreo, se generará la solicitud y pasará a <strong>revisión y dictamen del Inspector de la UI</strong>. El volumen de muestra de <strong>{parseFloat(muestreoForm.litros_muestra || 0)} L</strong> se descontará oficialmente del inventario de forma automática una vez que el Inspector emita su aprobación.
                </p>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "10px" }}>
                <button
                  type="button"
                  onClick={() => setIsMuestreoModalOpen(false)}
                  className="btn-cancel-custom"
                  style={{ padding: "10px 18px", borderRadius: "10px", border: "1px solid #CBD5E1", background: "white", fontWeight: 600 }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  style={{
                    background: "linear-gradient(135deg, #4338CA 0%, #6366F1 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    padding: "10px 22px",
                    fontWeight: 700,
                    cursor: "pointer",
                    boxShadow: "0 4px 14px rgba(99, 102, 241, 0.3)",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px"
                  }}
                >
                  <SendIcon style={{ fontSize: 18 }} />
                  <span>Enviar Solicitud de Muestreo a UI</span>
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* MODAL DE MOVIMIENTOS Y TRAZABILIDAD (UI Y ASOCIADO) */}
      {isMovimientosModalOpen && createPortal(
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(15, 23, 42, 0.75)",
          backdropFilter: "blur(4px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 99999,
          padding: "16px"
        }}>
          {(() => {
            const targetRecords = selectedLoteForMovimientos
              ? [selectedLoteForMovimientos]
              : (Array.isArray(granelRecords) ? granelRecords : []);

            let allMovs = [];
            targetRecords.forEach(rec => {
              const rawMovs = Array.isArray(rec.movimientos) ? rec.movimientos : [];
              const loteIdent = rec.noLote || rec.no_lote || rec.idContenedor || "S/N";
              const catIdent = rec.categoria || "Mezcal";
              const claseIdent = rec.clase || "";

              rawMovs.forEach((m, idx) => {
                const id = m.id_movimiento || m.id || `mov-${rec.id || 0}-${idx}`;
                const tipo = (m.tipo_movimiento || m.tipo || 'ajuste').toLowerCase();

                const motLower = String(m.motivo || '').toLowerCase();
                const obsLower = String(m.observaciones || '').toLowerCase();
                const estadoLower = String(m.estado_ui || '').toLowerCase();
                let estatusNorm = 'pendiente';
                if (m.es_rechazado || estadoLower.includes('rechazad') || m.estatus === 'rechazado' || m.aprobado === false || motLower.includes('rechazad') || obsLower.includes('rechazado por')) {
                  estatusNorm = 'rechazado';
                } else if (m.es_aprobado || estadoLower.includes('aprobad') || m.estatus === 'aprobado' || m.aprobado === true || motLower.includes('aprobad') || motLower.includes('autorizad') || obsLower.includes('aprobado y aplicado')) {
                  estatusNorm = 'aprobado';
                }

                allMovs.push({
                  ...m,
                  id_movimiento: id,
                  tipo_norm: tipo,
                  estatus_norm: estatusNorm,
                  lote_origen_display: loteIdent,
                  categoria_display: catIdent,
                  clase_display: claseIdent,
                  parentRecord: rec
                });
              });
            });

            const countByType = {
              todos: allMovs.length,
              dilucion: allMovs.filter(m => m.tipo_norm.includes('diluc') || m.tipo_norm.includes('ajuste')).length,
              traslado: allMovs.filter(m => m.tipo_norm.includes('traslado')).length,
              baja: allMovs.filter(m => m.tipo_norm.includes('baja') || m.tipo_norm.includes('merma')).length,
              muestreo: allMovs.filter(m => m.tipo_norm.includes('muestr')).length,
              fusion: allMovs.filter(m => m.tipo_norm.includes('fusion') || m.tipo_norm.includes('mezcla') || m.tipo_norm.includes('extracc')).length,
            };

            const countPending = allMovs.filter(m => m.estatus_norm === 'pendiente').length;
            const countApproved = allMovs.filter(m => m.estatus_norm === 'aprobado').length;
            const countRejected = allMovs.filter(m => m.estatus_norm === 'rechazado').length;

            const filteredMovs = allMovs.filter(m => {
              if (movimientosFilterTab === 'dilucion' && !(m.tipo_norm.includes('diluc') || m.tipo_norm.includes('ajuste'))) return false;
              if (movimientosFilterTab === 'traslado' && !m.tipo_norm.includes('traslado')) return false;
              if (movimientosFilterTab === 'baja' && !(m.tipo_norm.includes('baja') || m.tipo_norm.includes('merma'))) return false;
              if (movimientosFilterTab === 'muestreo' && !m.tipo_norm.includes('muestr')) return false;
              if (movimientosFilterTab === 'fusion' && !(m.tipo_norm.includes('fusion') || m.tipo_norm.includes('mezcla') || m.tipo_norm.includes('extracc'))) return false;

              if (movimientosStatusFilter !== 'todos' && m.estatus_norm !== movimientosStatusFilter) return false;

              return true;
            });

            return (
              <div style={{
                background: "white",
                borderRadius: "16px",
                width: "100%",
                maxWidth: "1050px",
                maxHeight: "90vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.35)",
                fontFamily: "'Inter', sans-serif"
              }}>
                {/* Header */}
                <div style={{
                  background: "linear-gradient(135deg, #0F766E 0%, #115E59 100%)",
                  color: "white",
                  padding: "18px 24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <div style={{
                      background: "rgba(255, 255, 255, 0.15)",
                      borderRadius: "12px",
                      padding: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                      <HistoryIcon style={{ fontSize: 24 }} />
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "white" }}>
                        Movimientos y Trazabilidad de Lotes a Granel
                      </h3>
                      <div style={{ fontSize: "12px", opacity: 0.85, marginTop: "2px" }}>
                        {selectedLoteForMovimientos ? (
                          <span>Lote: <strong>{selectedLoteForMovimientos.noLote || selectedLoteForMovimientos.no_lote || "S/N"}</strong> • {selectedLoteForMovimientos.categoria || "Mezcal"} • Vol: {selectedLoteForMovimientos.invFinalVol || selectedLoteForMovimientos.litros || 0} L</span>
                        ) : (
                          <span>Historial general de todos los movimientos registrados para este productor</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    {granelRecords && granelRecords.length > 0 && (
                      <select
                        value={selectedLoteForMovimientos ? (selectedLoteForMovimientos.id || selectedLoteForMovimientos.id_bitacora_granel || "") : "todos"}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "todos") {
                            setSelectedLoteForMovimientos(null);
                          } else {
                            const found = granelRecords.find(r => String(r.id || r.id_bitacora_granel) === String(val));
                            if (found) setSelectedLoteForMovimientos(found);
                          }
                        }}
                        style={{
                          background: "rgba(255, 255, 255, 0.2)",
                          color: "white",
                          border: "1px solid rgba(255, 255, 255, 0.3)",
                          borderRadius: "8px",
                          padding: "6px 10px",
                          fontSize: "12px",
                          fontWeight: 600,
                          cursor: "pointer",
                          outline: "none"
                        }}
                      >
                        <option value="todos" style={{ color: "#1E293B" }}>🌐 Ver Todos los Lotes</option>
                        {granelRecords.map((r, i) => (
                          <option key={r.id || i} value={r.id || r.id_bitacora_granel} style={{ color: "#1E293B" }}>
                            Lote {r.noLote || r.no_lote || `ID ${r.id}`} ({r.invFinalVol || r.litros || 0} L)
                          </option>
                        ))}
                      </select>
                    )}

                    <button
                      type="button"
                      onClick={() => setIsMovimientosModalOpen(false)}
                      style={{
                        background: "rgba(255, 255, 255, 0.15)",
                        border: "none",
                        color: "white",
                        borderRadius: "8px",
                        padding: "6px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      <CloseIcon style={{ fontSize: 20 }} />
                    </button>
                  </div>
                </div>

                {/* Sub-barra de Filtros: Apartados y Estatus */}
                <div style={{
                  padding: "14px 24px",
                  background: "#F8FAFC",
                  borderBottom: "1px solid #E2E8F0",
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px"
                }}>
                  {/* Apartados / Tabs por Tipo de Movimiento */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {[
                      { key: 'todos', label: 'Todos', count: countByType.todos, color: '#0F766E' },
                      { key: 'dilucion', label: '💧 Dilución', count: countByType.dilucion, color: '#0284C7' },
                      { key: 'traslado', label: '🚚 Traslado', count: countByType.traslado, color: '#7C3AED' },
                      { key: 'baja', label: '📉 Baja / Merma', count: countByType.baja, color: '#DC2626' },
                      { key: 'muestreo', label: '🧪 Muestreo', count: countByType.muestreo, color: '#D97706' },
                      { key: 'fusion', label: '🔄 Fusión / Extracción', count: countByType.fusion, color: '#059669' }
                    ].map(tab => {
                      const isActive = movimientosFilterTab === tab.key;
                      return (
                        <button
                          key={tab.key}
                          type="button"
                          onClick={() => setMovimientosFilterTab(tab.key)}
                          style={{
                            border: isActive ? `1.5px solid ${tab.color}` : "1px solid #CBD5E1",
                            background: isActive ? tab.color : "white",
                            color: isActive ? "white" : "#475569",
                            padding: "6px 12px",
                            borderRadius: "8px",
                            fontSize: "12px",
                            fontWeight: 700,
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px"
                          }}
                        >
                          <span>{tab.label}</span>
                          <span style={{
                            background: isActive ? "rgba(255, 255, 255, 0.25)" : "#F1F5F9",
                            color: isActive ? "white" : "#64748B",
                            borderRadius: "10px",
                            padding: "1px 6px",
                            fontSize: "10px",
                            fontWeight: 800
                          }}>
                            {tab.count}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Filtro por Estatus */}
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontSize: "12px", color: "#64748B", fontWeight: 700 }}>Estatus:</span>
                    <div style={{ display: "inline-flex", border: "1px solid #CBD5E1", borderRadius: "8px", overflow: "hidden", background: "white" }}>
                      {[
                        { key: 'todos', label: 'Todos' },
                        { key: 'pendiente', label: `⏳ Pendientes (${countPending})`, activeColor: '#FEF3C7', textColor: '#B45309' },
                        { key: 'aprobado', label: `✅ Aprobados (${countApproved})`, activeColor: '#DCFCE7', textColor: '#15803D' },
                        { key: 'rechazado', label: `❌ Rechazados (${countRejected})`, activeColor: '#FEE2E2', textColor: '#B91C1C' }
                      ].map(st => {
                        const isStActive = movimientosStatusFilter === st.key;
                        return (
                          <button
                            key={st.key}
                            type="button"
                            onClick={() => setMovimientosStatusFilter(st.key)}
                            style={{
                              border: "none",
                              borderRight: "1px solid #E2E8F0",
                              background: isStActive ? (st.activeColor || "#0F766E") : "transparent",
                              color: isStActive ? (st.textColor || "white") : "#475569",
                              padding: "6px 10px",
                              fontSize: "11px",
                              fontWeight: isStActive ? 800 : 500,
                              cursor: "pointer"
                            }}
                          >
                            {st.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Lista de Movimientos */}
                <div style={{
                  padding: "20px 24px",
                  overflowY: "auto",
                  maxHeight: "calc(90vh - 190px)",
                  background: "#F1F5F9",
                  display: "flex",
                  flexDirection: "column",
                  gap: "14px"
                }}>
                  {filteredMovs.length === 0 ? (
                    <div style={{
                      background: "white",
                      borderRadius: "12px",
                      padding: "48px 24px",
                      textAlign: "center",
                      border: "1px dashed #CBD5E1"
                    }}>
                      <div style={{ fontSize: "36px", marginBottom: "8px" }}>📦</div>
                      <h4 style={{ margin: "0 0 6px 0", color: "#334155", fontWeight: 700, fontSize: "16px" }}>
                        No se encontraron movimientos registrados
                      </h4>
                      <p style={{ margin: 0, color: "#64748B", fontSize: "13px" }}>
                        {movimientosFilterTab !== 'todos' || movimientosStatusFilter !== 'todos'
                          ? 'No hay movimientos que coincidan con los filtros seleccionados.'
                          : 'Este lote aún no cuenta con solicitudes de dilución, traslados, bajas o muestreos.'}
                      </p>
                    </div>
                  ) : (
                    filteredMovs.map((mov, idx) => {
                      const isPending = mov.estatus_norm === 'pendiente';
                      const isApproved = mov.estatus_norm === 'aprobado';
                      const isRejected = mov.estatus_norm === 'rechazado';

                      return (
                        <div
                          key={mov.id_movimiento || idx}
                          style={{
                            background: "white",
                            borderRadius: "12px",
                            border: isPending ? "1.5px solid #FCD34D" : (isApproved ? "1.5px solid #86EFAC" : "1.5px solid #E2E8F0"),
                            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.04)",
                            overflow: "hidden"
                          }}
                        >
                          {/* Card Header */}
                          <div style={{
                            padding: "12px 18px",
                            background: isPending ? "#FFFBEB" : (isApproved ? "#F0FDF4" : "#F8FAFC"),
                            borderBottom: "1px solid #E2E8F0",
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "10px"
                          }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              {mov.tipo_norm.includes('diluc') && (
                                <span style={{ background: "#0284C7", color: "white", padding: "4px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: 800 }}>
                                  💧 Dilución / Ajuste de Grado
                                </span>
                              )}
                              {mov.tipo_norm.includes('traslado') && (
                                <span style={{ background: "#7C3AED", color: "white", padding: "4px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: 800 }}>
                                  🚚 Traslado Instalaciones
                                </span>
                              )}
                              {mov.tipo_norm.includes('baja') && (
                                <span style={{ background: "#DC2626", color: "white", padding: "4px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: 800 }}>
                                  📉 Baja / Merma
                                </span>
                              )}
                              {mov.tipo_norm.includes('muestr') && (
                                <span style={{ background: "#D97706", color: "white", padding: "4px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: 800 }}>
                                  🧪 Muestreo Oficial
                                </span>
                              )}
                              {mov.tipo_norm.includes('extracc') && (
                                <span style={{ background: "#059669", color: "white", padding: "4px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: 800 }}>
                                  📤 Extracción de Lote
                                </span>
                              )}
                              {(mov.tipo_norm.includes('fusion') || mov.tipo_norm.includes('mezcla')) && (
                                <span style={{ background: "#059669", color: "white", padding: "4px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: 800 }}>
                                  🔄 Fusión / Mezcla
                                </span>
                              )}

                              <span style={{ fontSize: "12px", color: "#64748B", fontWeight: 600 }}>
                                📅 {mov.fecha ? new Date(mov.fecha).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Fecha S/R'}
                              </span>

                              <span style={{ background: "#F1F5F9", color: "#334155", padding: "2px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: 700, border: "1px solid #CBD5E1" }}>
                                Lote: {mov.lote_origen_display}
                              </span>

                              {mov.folio_documento && (
                                <span style={{ background: "#EFF6FF", color: "#1D4ED8", padding: "2px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: 700, border: "1px solid #BFDBFE" }}>
                                  Folio: {mov.folio_documento}
                                </span>
                              )}
                            </div>

                            {/* Badge Estatus */}
                            <div>
                              {isPending && (
                                <span style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D", padding: "4px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 800, display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                  <span style={{ display: "inline-block", width: "7px", height: "7px", borderRadius: "50%", background: "#F59E0B" }}></span>
                                  ⏳ Pendiente de Dictamen UI
                                </span>
                              )}
                              {isApproved && (
                                <span style={{ background: "#DCFCE7", color: "#166534", border: "1px solid #86EFAC", padding: "4px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 800, display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                  <span style={{ display: "inline-block", width: "7px", height: "7px", borderRadius: "50%", background: "#22C55E" }}></span>
                                  ✅ Aprobado y Aplicado por UI
                                </span>
                              )}
                              {isRejected && (
                                <span style={{ background: "#FEE2E2", color: "#991B1B", border: "1px solid #FCA5A5", padding: "4px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 800, display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                  <span style={{ display: "inline-block", width: "7px", height: "7px", borderRadius: "50%", background: "#EF4444" }}></span>
                                  ❌ Rechazado por UI
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Card Body */}
                          <div style={{ padding: "16px 18px" }}>
                            <div style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                              gap: "12px",
                              marginBottom: "12px"
                            }}>
                              <div style={{ background: "#F8FAFC", padding: "10px 14px", borderRadius: "8px", border: "1px solid #E2E8F0" }}>
                                <div style={{ fontSize: "11px", color: "#64748B", fontWeight: 700 }}>
                                  {mov.tipo_norm.includes('diluc') ? '💧 Agua Agregada' : '📦 Volumen Afectado'}
                                </div>
                                <div style={{ fontSize: "15px", fontWeight: 800, color: "#0F172A", marginTop: "2px" }}>
                                  {mov.tipo_norm.includes('diluc')
                                    ? `+${mov.agua_agregada || mov.litros_transferidos || 0} L`
                                    : (mov.tipo_norm.includes('baja') || mov.tipo_norm.includes('traslado') || mov.tipo_norm.includes('muestr')
                                      ? `-${mov.litros_transferidos || 0} L`
                                      : `${mov.litros_transferidos || 0} L`)}
                                </div>
                              </div>

                              <div style={{ background: "#F8FAFC", padding: "10px 14px", borderRadius: "8px", border: "1px solid #E2E8F0" }}>
                                <div style={{ fontSize: "11px", color: "#64748B", fontWeight: 700 }}>🌡️ Grado Alcohólico</div>
                                <div style={{ fontSize: "15px", fontWeight: 800, color: "#0F172A", marginTop: "2px" }}>
                                  {mov.grado_obtenido
                                    ? `${mov.grado_obtenido}% Alc. Vol.`
                                    : (mov.porcentaje_alc_vol ? `${mov.porcentaje_alc_vol}% Alc. Vol.` : 'No aplica')}
                                </div>
                              </div>

                              <div style={{ background: "#F8FAFC", padding: "10px 14px", borderRadius: "8px", border: "1px solid #E2E8F0" }}>
                                <div style={{ fontSize: "11px", color: "#64748B", fontWeight: 700 }}>
                                  {mov.tipo_norm.includes('traslado') ? '📍 Destino' : '🛢️ Tanque / Contenedor'}
                                </div>
                                <div style={{ fontSize: "13px", fontWeight: 700, color: "#0F172A", marginTop: "2px" }}>
                                  {mov.tanque_destino || mov.instalacion_destino || mov.tanque_origen || 'No especificado'}
                                </div>
                              </div>

                              <div style={{ background: "#F8FAFC", padding: "10px 14px", borderRadius: "8px", border: "1px solid #E2E8F0" }}>
                                <div style={{ fontSize: "11px", color: "#64748B", fontWeight: 700 }}>🏷️ Lote Destino / Ref.</div>
                                <div style={{ fontSize: "13px", fontWeight: 700, color: "#0F172A", marginTop: "2px" }}>
                                  {mov.lote_nuevo || mov.id_lote_destino || 'Mismo Lote'}
                                </div>
                              </div>
                            </div>

                            {(mov.motivo || mov.observaciones) && (
                              <div style={{
                                background: "#F8FAFC",
                                borderRadius: "8px",
                                padding: "10px 14px",
                                border: "1px solid #E2E8F0",
                                fontSize: "12px",
                                marginBottom: "10px"
                              }}>
                                {mov.motivo && (
                                  <div style={{ marginBottom: "4px" }}>
                                    <strong style={{ color: "#475569" }}>Motivo: </strong>
                                    <span style={{ color: "#1E293B" }}>{mov.motivo}</span>
                                  </div>
                                )}
                                {mov.observaciones && (
                                  <div>
                                    <strong style={{ color: "#475569" }}>Observaciones: </strong>
                                    <span style={{ color: "#1E293B" }}>{mov.observaciones}</span>
                                  </div>
                                )}
                              </div>
                            )}

                            {isPending && (
                              <div style={{
                                background: "#FFFBEB",
                                border: "1px solid #FCD34D",
                                borderRadius: "8px",
                                padding: "10px 14px",
                                fontSize: "12px",
                                color: "#92400E",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px"
                              }}>
                                <span>⏳</span>
                                <span>
                                  <strong>En proceso de validación:</strong> Este movimiento se encuentra en revisión por el Inspector de la Unidad de Inspección. El volumen oficial del lote se actualizará automáticamente una vez emitido el dictamen aprobatorio.
                                </span>
                              </div>
                            )}

                            {isApproved && (
                              <div style={{
                                background: "#F0FDF4",
                                border: "1px solid #86EFAC",
                                borderRadius: "8px",
                                padding: "10px 14px",
                                fontSize: "12px",
                                color: "#166534",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px"
                              }}>
                                <span>✅</span>
                                <span>
                                  <strong>Dictaminado y Aplicado por UI:</strong> El balance de litros y graduación oficial ha sido integrado satisfactoriamente al inventario conforme a la NOM-070.
                                </span>
                              </div>
                            )}

                            {isRejected && (
                              <div style={{
                                background: "#FEF2F2",
                                border: "1px solid #FCA5A5",
                                borderRadius: "8px",
                                padding: "10px 14px",
                                fontSize: "12px",
                                color: "#991B1B",
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px"
                              }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                  <span>❌</span>
                                  <strong>Movimiento Rechazado por la Unidad de Inspección:</strong>
                                </div>
                                <div style={{ marginLeft: "24px" }}>
                                  {mov.observaciones_ui || 'El inspector determinó que la solicitud no procede o requiere aclaraciones.'}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Footer */}
                <div style={{
                  padding: "14px 24px",
                  background: "white",
                  borderTop: "1px solid #E2E8F0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between"
                }}>
                  <div style={{ fontSize: "12.5px", color: "#64748B" }}>
                    Total movimientos registrados: <strong>{allMovs.length}</strong>
                    {countPending > 0 && (
                      <span style={{ marginLeft: "8px", background: "#FEF3C7", color: "#92400E", padding: "2px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: 700 }}>
                        {countPending} en revisión UI
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsMovimientosModalOpen(false)}
                    style={{
                      background: "#0F766E",
                      color: "white",
                      border: "none",
                      borderRadius: "10px",
                      padding: "8px 24px",
                      fontWeight: 700,
                      fontSize: "13px",
                      cursor: "pointer",
                      boxShadow: "0 2px 8px rgba(15, 118, 110, 0.2)"
                    }}
                  >
                    Entendido / Cerrar
                  </button>
                </div>
              </div>
            );
          })()}
        </div>,
        document.body
      )}
    </div>
  );
};

export default InventarioGranelSection;
